import React, { useState, useRef } from "react";
import { UserButton, useUser } from "@clerk/react";
import { useLocation, useNavigate } from "react-router-dom";
import { generateQuoteHTML } from "./pdfTemplate";
import { buildAutomotiveQuoteHTML, quoteDateStr } from "./generateQuoteDocument";
import { loadCompanySettings } from "./companyFooter";
import { buildJobSheetHTML } from "./jobSheet";
import { SERVICE_CATEGORIES } from "./serviceCategories";
import { SCOPE_TEMPLATES, buildAutoScopeBody, composeAutoScope, composeScope, upgradeScopeNotes, scopeTextToHTML } from "./scopeTemplates";
import QuoteLog from './QuoteLog';
import NoAutofillInput from "./NoAutofillInput";
import { AUTO_MACHINE_PRESETS, AUTO_SERVICE_HOURS, AUTO_SERVICE_INCLUDES, includedBy, AUTO_CONTAM_MULT, AUTO_VEHICLE_MULT, AUTO_PROTECTION, AUTO_PROTECTION_SCALE, AUTO_ADDONS, AUTO_ADDON_INCLUDES, AUTO_PROTECTION_ADDON_INCLUDES, addonIncludedIn, autoAddonsSummary, AUTO_DETAILING } from "./autoCatalogue";
import { SectorPill } from "./sectors";
import SaveQuoteModal from './SaveQuoteModal';
import QuotePreview from "./QuotePreview";
import { buildCopyState } from "./copyQuote";
import { DISCOUNT_PRESETS, activeDiscountPreset, applyDiscountPreset } from "./discountPresets";
import { loadDraft, saveDraft, clearDraft, draftToInitialState, onPageHide } from "./draftStore";
import CompanySettingsPage from './CompanySettingsPage';
import { JobSheetView } from './JobSheetView';
import { tokens, fonts, type, FONTS_HREF } from "./theme";
import { NewVersionBanner, BuildStamp } from './buildVersion';
import { overlappingJobs } from './workflow';
import { fetchAllQuotes, getJobDates } from './quoteLogger';
import { IS_PREVIEW_DEPLOYMENT, isInternalOnlySend, appendQuote, appendAmendment, newSaveId, ensureHeaders, fetchSuppliers, findDryIceSuppliers, primaryDryIceSupplier, hasDeliveredTiers, deliveredTiersOf, deliveredTiersExpired, tierPlanLabel, dryIceCostFor, daysSince, SUPPLIER_PRICE_STALE_DAYS } from './quoteLogger';

// Small line icon for the UserButton's custom "Admin" menu item,
// matching the thin-stroke icon style already used on the Quote Log page.
const IconSettingsGear = (p) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}>
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z" />
  </svg>
);

// App shell (header, nav buttons): the original palette, unchanged.
const shellT = {
  bg:"#0e1520",surface:"#141e2e",border:"#1e2d42",borderLight:"#243448",
  accent:"#3a52cc",accentDim:"#3a52cc22",accentGlow:"#3a52cc44",
  text:"#f0ede8",textMuted:"#7a8fa8",textDim:"#3a4d62",
  success:"#4cd97b",warning:"#ffb74d",danger:"#ff6b6b",
  navyDark:"#080e18",navyLight:"#141e2e",brandBlue:"#3a52cc",
};
// Calculators: the dark design tokens (theme.js) under the names the
// calculator code already uses. textDim and danger aren't tokens: a dimmer
// muted for placeholders and hints, and the existing red.
const D=tokens.dark;
const t = {
  bg:D.bg,surface:D.surface,border:D.line,borderLight:D.line,
  accent:D.accent,accentDim:D.accentSoft,accentGlow:D.accent+"55",
  text:D.ink,textMuted:D.muted,textDim:"#62798B",
  success:D.good,successSoft:D.goodSoft,warning:D.warn,warningSoft:D.warnSoft,danger:"#ff6b6b",
  navyDark:D.bg,navyLight:D.surface,brandBlue:D.accent,onAccent:D.bg,
};
// Eyebrow label (section titles, small headings) and small stat (label over value).
function Eyebrow({children,style}){
  return <div style={{...type.eyebrow,color:t.accent,...style}}>{children}</div>;
}
function Stat({label,value,note,style}){
  return(
    <div style={{minWidth:0,...style}}>
      <div style={{...type.statLabel,color:t.textMuted,marginBottom:3}}>{label}</div>
      <div style={{...type.statValue,fontSize:15,color:t.text}}>{value}</div>
      {note&&<div style={{fontSize:11.5,color:t.textMuted,marginTop:2}}>{note}</div>}
    </div>
  );
}

// ─── SEQUENTIAL QUOTE NUMBER ─────────────────────────────────────────────────
function getNextQuoteRef(prefix) {
  const year = new Date().getFullYear();
  const storageKey = `sublime_quote_counter_${prefix}_${year}`;
  const current = parseInt(localStorage.getItem(storageKey) || '0', 10);
  const next = current + 1;
  localStorage.setItem(storageKey, next.toString());
  return `${prefix}-${year}-${String(next).padStart(3, '0')}`;
}

function peekNextQuoteRef(prefix) {
  const year = new Date().getFullYear();
  const storageKey = `sublime_quote_counter_${prefix}_${year}`;
  const current = parseInt(localStorage.getItem(storageKey) || '0', 10);
  return `${prefix}-${year}-${String(current + 1).padStart(3, '0')}`;
}

// ─── PERSISTED RATE DEFAULTS ─────────────────────────────────────────────────
// Material/labour rates (diesel price, dry ice price, labour rate, etc.) drift
// with the market and were meant to carry forward as the new starting point for
// the next quote once adjusted — but every useState here just had a hardcoded
// literal, so any edit was silently lost the moment a new quote was started.
// keys, when given, limits what gets saved/loaded to just those fields — needed
// for Automotive's `rates` blob, which also holds per-quote-only state
// (travelMiles, the two overrides) that must never carry over between quotes.
function loadPersistedDefaults(storageKey, hardcoded, keys) {
  try {
    const saved = JSON.parse(localStorage.getItem(storageKey));
    if (!saved || typeof saved !== 'object') return hardcoded;
    if (!keys) return { ...hardcoded, ...saved };
    const merged = { ...hardcoded };
    keys.forEach(k => { if (saved[k] !== undefined) merged[k] = saved[k]; });
    return merged;
  } catch (e) { return hardcoded; }
}
// Automotive labour rate for new quotes: £35/h from 18 Oct 2026 (was £45/h).
const AUTO_DEFAULT_LABOUR_RATE=35;
// One-off, per browser: a remembered Automotive labour rate of exactly the
// old £45 default becomes £35. Only the remembered default for the next new
// quote is touched - never a saved quote, a draft or a copy (each keeps the
// rate in its own state). Runs once, then the flag stops it running again,
// so a rate set to £45 by hand afterwards stays.
const AUTO_LABOUR_MIGRATION_KEY='sublime_auto_labour_35_migrated';
function migrateAutoLabourDefault(){
  try{
    if(localStorage.getItem(AUTO_LABOUR_MIGRATION_KEY)) return;
    const saved=JSON.parse(localStorage.getItem('sublime_auto_rates_defaults'));
    if(saved&&typeof saved==='object'&&Number(saved.labourRate)===45){
      localStorage.setItem('sublime_auto_rates_defaults',JSON.stringify({...saved,labourRate:AUTO_DEFAULT_LABOUR_RATE}));
    }
    localStorage.setItem(AUTO_LABOUR_MIGRATION_KEY,new Date().toISOString());
  }catch(e){ /* storage blocked: nothing remembered to migrate */ }
}
function persistDefaults(storageKey, value, keys) {
  try {
    const toSave = keys ? Object.fromEntries(keys.map(k => [k, value[k]])) : value;
    localStorage.setItem(storageKey, JSON.stringify(toSave));
  } catch (e) {}
}

// "+ New Quote" above the calculators - starts a blank quote (App: startNewQuote).

// Quote Reference: new quotes have no number until the Apps Script assigns
// one on save, so show a greyed "New quote" rather than a provisional number
// that may not be the one it gets. Shows the real number once saved, and the
// amendment number when amending.
function QuoteRefField({number,amending}){
  return <input style={{...inp,color:number?t.text:t.textMuted,WebkitTextFillColor:number?t.text:t.textMuted,fontStyle:number?"normal":"italic"}} readOnly value={number||(amending?`Amending ${amending}`:"New quote")} title={number?"":"The quote number is assigned when the quote is saved."}/>;
}

// The reference printed on an internal Job Sheet. Opening a Job Sheet never
// saves, so an unsaved amendment has no number yet - show what it amends
// rather than the provisional letter (which the save may not end up using).
// savedRef: only when the calculator still matches what was saved.
function jobSheetRef(savedRef,amendOf){
  if(savedRef?.number) return savedRef.number;
  if(amendOf) return `Amendment of ${amendOf} (not saved)`;
  return "New quote (not saved)";
}

// What a quote looks like for "has it changed since it was saved?": what
// was entered, minus the quote number (the save itself turns it from
// provisional to real) and the figures derived from it at save time. The
// total is compared separately (see matchesSaved).
function quoteSnapshot(stateForSave){
  // amendedFrom only rides along on the first save of an "amend from this
  // version", so it isn't a change.
  const {costBreakdown,totalChargeableHrs,amendedFrom,...rest}=stateForSave;
  const {_discountSnapshot,marginSet,...commercial}=rest.commercial||{};
  return JSON.stringify({...rest,commercial,job:{...rest.job,quoteRef:undefined}});
}
// savedRef: { number, snap, total } for the quote as last saved (or as
// opened via Amend). Unchanged = same inputs and the same total.
function matchesSaved(savedRef,snap,price){
  return !!savedRef&&savedRef.snap===snap&&Math.abs((Number(price)||0)-Number(savedRef.total))<0.01;
}

// "Editing X, no changes yet" - a saved quote open with nothing changed, so
// saving creates nothing.
function NoChangesBanner({number}){
  return(
    <div style={{background:t.surface,border:`1px solid ${t.border}`,borderRadius:8,padding:"10px 14px",marginBottom:16,fontSize:13,color:t.textMuted}}>
      Editing <strong style={{color:t.text}}>{number}</strong>, no changes yet.
    </div>
  );
}

// The letter the next amendment of a quote should get: AUT-2026-028 ->
// AUT-2026-028a, 028a -> 028b. Display only; the Apps Script assigns the
// real one (next unused letter) when it saves.
function nextAmendmentNumber(n){
  const base=String(n||"").replace(/[a-z]+$/,"");
  const suffix=String(n||"").slice(base.length);
  return base+(suffix?String.fromCharCode(suffix.charCodeAt(suffix.length-1)+1):"a");
}

// "Editing X. Saving creates Xa" - shown whenever a save will write an
// amendment rather than a new quote.
function AmendBanner({amendOf,next}){
  return(
    <div style={{background:t.warningSoft,border:`1px solid ${t.warning}`,borderRadius:10,padding:"10px 14px",marginBottom:16,fontSize:13,color:t.text}}>
      Editing <strong>{amendOf}</strong>. Saving creates <strong>{next}</strong>.
    </div>
  );
}

// A copy of another quote, not yet saved: saving gives it a new number.
// note: an extra line, e.g. Industrial's reminder to enter travel miles.
function CopyBanner({copyOf,note}){
  return(
    <div style={{background:t.successSoft,border:`1px solid ${t.success}`,borderRadius:10,padding:"10px 14px",marginBottom:16,fontSize:13,color:t.text}}>
      Copy of <strong>{copyOf}</strong>, new number on save.
      {note&&<div style={{marginTop:4,color:t.warning,fontWeight:600}}>{note}</div>}
    </div>
  );
}

// Autosave (see draftStore.js). Writes this calculator's draft a moment
// after each change, and straight away when the page is hidden or closed
// (the only reliable moment on mobile Safari/Chrome). A blank calculator
// nobody has touched writes nothing, so a draft always means real work.
// getDraft returns the draft to store, built from the latest render.
// onChange: told after each write, so the Quote Log's drafts strip stays
// current. Returns flush(), to write straight away (e.g. on ← Quote Log).
// disabled: a read-only view (Open) never writes or clears a draft.
function useCalcAutosave(tab,initialState,currentSnap,getDraft,onChange,disabled){
  const latest=useRef(getDraft);
  latest.current=getDraft;
  const mountSnap=useRef(null);
  const snapNow=useRef(currentSnap);
  snapNow.current=currentSnap;
  // What the calculator looked like once it had settled after opening.
  React.useEffect(()=>{ const id=setTimeout(()=>{ mountSnap.current=snapNow.current; },0); return ()=>clearTimeout(id); },[]);
  const worthKeeping=()=>!!initialState||(mountSnap.current!==null&&snapNow.current!==mountSnap.current);
  // Nothing worth keeping (e.g. edits undone on a blank quote): drop any old
  // draft rather than leave a stale one.
  const write=()=>{ if(disabled) return; if(worthKeeping()) saveDraft(tab,latest.current()); else if(!initialState) clearDraft(tab); if(onChange) onChange(); };
  const draftKey=JSON.stringify(getDraft());
  React.useEffect(()=>{ const id=setTimeout(write,600); return ()=>clearTimeout(id); },[draftKey]); // eslint-disable-line react-hooks/exhaustive-deps
  React.useEffect(()=>onPageHide(write),[]); // eslint-disable-line react-hooks/exhaustive-deps
  return write;
}

// Top of each calculator: what it's doing (e.g. "Automotive · Editing
// AUT-2026-030") and Start new quote. Back to the log is the header's Quote
// Log button; work is kept by autosave either way.
// The quote's ref as the calculator shows it (top bar, Live Summary, phone
// bar): the saved number, the amendment the next save creates, or no number
// yet - a new quote's number is only assigned by the Apps Script on save.
function calcRef({readOnly,savedAndUnchanged,savedRef,amendOf,amendNext}){
  if(readOnly) return {ref:readOnly.ref,note:"read only"};
  if(savedAndUnchanged) return {ref:savedRef.number,note:"saved"};
  if(amendOf) return {ref:amendNext,note:"not saved yet"};
  return {ref:"",note:"not saved yet"};
}
const refVersion=n=>{ const m=String(n||"").match(/([a-z])$/); return m?m[1].charCodeAt(0)-95:1; };
function RefChip({info,test,large}){
  const pill={...type.pill,fontSize:"0.66rem",padding:"2px 8px"};
  return(
    <span style={{display:"inline-flex",alignItems:"center",gap:8,flexWrap:"wrap",minWidth:0}}>
      <span style={info.ref?{...type.statValue,fontSize:large?19:14,color:t.text}:{fontFamily:fonts.display,fontSize:large?22:15,fontWeight:700,color:t.text}}>{info.ref||"New quote"}</span>
      {info.ref&&<span style={{...pill,background:t.accentDim,color:t.accent}}>v{refVersion(info.ref)}</span>}
      {test&&<span style={{...pill,background:t.warningSoft,color:t.warning}}>Test</span>}
      <span style={{fontSize:large?13:12,color:info.note==="saved"?t.success:info.note==="read only"?t.accent:t.textMuted}}>· {info.note}</span>
    </span>
  );
}
// Top of each calculator: the ref (sticky on desktop, so it stays in view
// while the form scrolls), the client, what the calculator is doing (e.g.
// "Automotive · Editing AUT-2026-030") and Start new quote.
function CalcNav({name,status,onNew,isMobile,refInfo,test,client,children}){
  return(
    <div style={isMobile
      ?{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10,flexWrap:"wrap",marginBottom:14}
      :{display:"flex",alignItems:"center",justifyContent:"space-between",gap:12,flexWrap:"wrap",position:"sticky",top:0,zIndex:20,background:t.bg,margin:"-32px -40px 22px",padding:"14px 40px 12px",borderBottom:`1px solid ${t.border}`}}>
      <div style={{minWidth:0}}>
        <div style={{display:"flex",alignItems:"baseline",gap:10,flexWrap:"wrap",minWidth:0}}>
          {refInfo&&<RefChip info={refInfo} test={test} large/>}
          {client&&<span style={{fontSize:15,fontWeight:600,color:t.text,overflowWrap:"anywhere"}}>{client}</span>}
        </div>
        <div style={{fontSize:13,color:t.textMuted,marginTop:3}}><span style={{fontFamily:fonts.display,fontWeight:700,fontSize:15,color:t.text,letterSpacing:"0.02em"}}>{name}</span> · {status}</div>
      </div>
      {onNew&&<button type="button" onClick={onNew} style={{...secondaryBtn,minHeight:isMobile?40:32,padding:"0 14px",fontSize:12.5}}>Start new quote</button>}
      {children}
    </div>
  );
}
// Buttons: one primary (Save, accent with dark ink for contrast), the rest
// secondary (outline).
// Calculator page root: dark tokens, calculator fonts.
const calcRoot={background:t.bg,color:t.text,fontFamily:fonts.body,minHeight:"calc(100vh - 60px)"};
const primaryBtn={borderRadius:10,border:`1px solid ${t.accent}`,cursor:"pointer",fontWeight:700,background:t.accent,color:t.onAccent,fontFamily:fonts.body,boxShadow:`0 0 0 1px ${t.accent}`};
const secondaryBtn={borderRadius:10,border:`1px solid ${t.border}`,cursor:"pointer",fontWeight:600,background:"transparent",color:t.text,fontFamily:fonts.body};
// Right-hand panel, the same in both calculators: Live Summary scrolls
// inside it, Save / PDF / Job Sheet stay pinned at the foot, and the panel
// itself stays in view while the form scrolls.
function CalcSidePanel({summary,onSave,onPDF,onJobSheet,pdfLoading,readOnly,refLine,children}){
  const secBtn={...secondaryBtn,width:"100%",padding:10,fontSize:13};
  return(
    <div style={{borderLeft:`1px solid ${t.border}`,background:t.bg,position:"sticky",top:73,height:"calc(100vh - 73px)",display:"flex",flexDirection:"column"}}>
      {refLine&&<div style={{padding:"14px 24px 12px",borderBottom:`1px solid ${t.border}`}}>{refLine}</div>}
      <div style={{flex:1,overflowY:"auto",padding:24,paddingBottom:12}}>
        <Eyebrow style={{marginBottom:14}}>Live summary</Eyebrow>
        {summary}
      </div>
      {!readOnly&&<div style={{padding:"14px 24px 18px",borderTop:`1px solid ${t.border}`,background:t.bg,display:"flex",flexDirection:"column",gap:8}}>
        <button type="button" onClick={onSave} style={{...primaryBtn,width:"100%",padding:13,fontSize:15}}>Save to Log</button>
        <div className="sp-grid" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
          <button type="button" onClick={onPDF} disabled={pdfLoading} style={secBtn}>{pdfLoading?"Opening…":"PDF"}</button>
          <button type="button" onClick={onJobSheet} title="Internal job sheet: hours, crew, dry ice, notes. Not for the client." style={secBtn}>Job Sheet</button>
        </div>
      </div>}
      {children}
    </div>
  );
}
// Phones, both calculators: a slim fixed bar with the total and Save. Tap
// the total for the full breakdown plus PDF, Job Sheet and the preview.
function CalcMobileBar({finalPrice,grossMargin,marginColor,summary,onSave,onPDF,onJobSheet,onPreview,pdfLoading,readOnly,refLine}){
  const[open,setOpen]=useState(false);
  const secBtn={...secondaryBtn,flex:1,minHeight:40,fontSize:13};
  return(
    <div style={{position:"fixed",bottom:0,left:0,right:0,background:t.bg,borderTop:`1px solid ${t.border}`,zIndex:200,maxHeight:"80vh",display:"flex",flexDirection:"column",paddingBottom:"env(safe-area-inset-bottom)",fontFamily:fonts.body}}>
      {open&&(
        <div style={{overflowY:"auto",padding:"14px 16px 10px",borderBottom:`1px solid ${t.border}`}}>
          {summary}
          <div style={{display:"flex",gap:8,marginTop:10}}>
            {!readOnly&&<button type="button" onClick={onPDF} disabled={pdfLoading} style={secBtn}>{pdfLoading?"Opening…":"PDF"}</button>}
            {!readOnly&&<button type="button" onClick={onJobSheet} style={secBtn}>Job Sheet</button>}
            <button type="button" onClick={()=>{ setOpen(false); onPreview(); }} style={secBtn}>Preview ↓</button>
          </div>
        </div>
      )}
      {refLine&&<div style={{padding:"8px 16px 0",minWidth:0}}>{refLine}</div>}
      <div style={{display:"flex",alignItems:"center",gap:12,padding:"8px 16px"}}>
        <button type="button" onClick={()=>setOpen(o=>!o)} aria-expanded={open} style={{flex:1,display:"flex",alignItems:"baseline",gap:8,border:"none",background:"none",padding:"4px 0",cursor:"pointer",textAlign:"left",minWidth:0}}>
          <span style={{fontSize:11,color:t.textMuted}}>{open?"▾":"▴"}</span>
          {finalPrice==null
            ? <span style={{fontSize:13,fontWeight:700,color:t.warning}}>Pick contamination</span>
            : <span style={{fontFamily:fonts.display,fontSize:26,fontWeight:700,lineHeight:1,color:t.text,fontVariantNumeric:"tabular-nums"}}>{fmt(finalPrice)}</span>}
          <span style={{fontSize:11,color:t.textMuted}}>ex VAT</span>
          <span style={{...type.statValue,fontSize:12,color:marginColor,whiteSpace:"nowrap"}}>{fmtN(grossMargin)}%</span>
        </button>
        {!readOnly&&<button type="button" onClick={onSave} style={{...primaryBtn,minHeight:44,padding:"0 22px",fontSize:15}}>Save</button>}
      </div>
    </div>
  );
}

// "New quote" / "Copy of X" / "Editing X" (+ unsaved changes) for CalcNav.
function calcStatusLabel({amendOf,savedAndUnchanged,copyOf}){
  if(amendOf) return savedAndUnchanged?`Editing ${amendOf}`:`Editing ${amendOf}, unsaved changes`;
  if(copyOf) return `Copy of ${copyOf}, new quote`;
  return "New quote";
}

// Shown once after a reload brings back unsaved work.
function DraftRestoredBanner({at}){
  const[open,setOpen]=useState(true);
  if(!open) return null;
  const when=at?new Date(at).toLocaleString("en-GB",{day:"numeric",month:"short",hour:"2-digit",minute:"2-digit"}):"";
  return(
    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:10,background:t.surface,border:`1px solid ${t.border}`,borderRadius:8,padding:"10px 14px",marginBottom:16,fontSize:13,color:t.textMuted}}>
      <span>Restored your work from {when}. + New Quote clears it.</span>
      <button type="button" onClick={()=>setOpen(false)} title="Hide" style={{background:"none",border:"none",color:t.textMuted,cursor:"pointer",fontSize:14}}>✕</button>
    </div>
  );
}

// ─── Quote Log home: + New Quote and the unsaved drafts strip ──────────────
const CALC_NAMES={automotive:"Automotive",industrial:"Industrial"};
// The draft for a calculator if it holds unsaved work (a saved quote sitting
// unchanged in a calculator isn't a draft). Older drafts have no flag: count
// them as unsaved.
function unsavedDraft(tab){ const d=loadDraft(tab); return d&&(d.dirty??true)?d:null; }
const fmtDraftTime=at=>at?new Date(at).toLocaleString("en-GB",{day:"numeric",month:"short",hour:"2-digit",minute:"2-digit"}):"";

// Light-theme dialog from the header's + New Quote: pick the calculator,
// then (only if it has unsaved work) continue it or start fresh.
function NewQuoteDialog({onPick,onClose,isMobile}){
  const[step,setStep]=useState(null); // the calculator whose draft we're asking about
  const draft=step?unsavedDraft(step):null;
  const btn=(primary)=>({minHeight:isMobile?48:42,padding:"0 18px",borderRadius:9,border:primary?"none":"1px solid #d0d8e8",background:primary?"#1D3461":"#fff",color:primary?"#fff":"#1a1a2e",fontSize:14,fontWeight:700,cursor:"pointer",flex:1});
  const choose=tab=>{ if(unsavedDraft(tab)) setStep(tab); else onPick(tab,"fresh"); };
  return(
    <div onClick={e=>e.target===e.currentTarget&&onClose()} style={{position:"fixed",inset:0,background:"rgba(10,20,50,0.45)",zIndex:1200,display:"flex",alignItems:isMobile?"flex-end":"center",justifyContent:"center",padding:isMobile?0:20}}>
      <div style={{background:"#fff",borderRadius:isMobile?"16px 16px 0 0":14,padding:24,width:"100%",maxWidth:440,boxSizing:"border-box",fontFamily:"'DM Sans','Helvetica Neue',sans-serif",color:"#1a1a2e"}}>
        {!step?(
          <>
            <div style={{fontSize:17,fontWeight:700,marginBottom:16}}>New quote: Automotive or Industrial?</div>
            <div style={{display:"flex",gap:10}}>
              <button type="button" style={btn(true)} onClick={()=>choose("automotive")}>Automotive</button>
              <button type="button" style={btn(true)} onClick={()=>choose("industrial")}>Industrial</button>
            </div>
          </>
        ):(
          <>
            <div style={{fontSize:17,fontWeight:700,marginBottom:6}}>Continue your unsaved draft, or start fresh?</div>
            <div style={{fontSize:13,color:"#4a5568",marginBottom:16}}>{CALC_NAMES[step]}: {draft?.summary||"unsaved work"}{draft?.at?` · edited ${fmtDraftTime(draft.at)}`:""}</div>
            <div style={{display:"flex",gap:10,flexDirection:isMobile?"column":"row"}}>
              <button type="button" style={btn(true)} onClick={()=>onPick(step,"continue")}>Continue draft</button>
              <button type="button" style={btn(false)} onClick={()=>{ if(window.confirm("Start fresh? The unsaved draft will be discarded.")) onPick(step,"fresh"); }}>Start fresh</button>
            </div>
          </>
        )}
        <button type="button" onClick={onClose} style={{marginTop:14,width:"100%",minHeight:40,border:"none",background:"none",color:"#6B7280",fontSize:13,cursor:"pointer"}}>Cancel</button>
      </div>
    </div>
  );
}

// Download PDF on a quote that isn't saved (new, amended, or changed since
// its last save): saving comes first, so ask. Its own dialog rather than
// window.confirm, so "Save and download" is a fresh click and the PDF tab
// isn't blocked as a pop-up however long the answer takes.
// amendOf/next: the quote being amended and the number saving creates.
function SavePdfDialog({amendOf,next,prefix,onConfirm,onClose,isMobile}){
  const btn=(primary)=>({minHeight:isMobile?48:42,padding:"0 18px",borderRadius:9,border:primary?"none":"1px solid #d0d8e8",background:primary?"#1D3461":"#fff",color:primary?"#fff":"#1a1a2e",fontSize:14,fontWeight:700,cursor:"pointer",flex:1});
  return(
    <div onClick={e=>e.target===e.currentTarget&&onClose()} style={{position:"fixed",inset:0,background:"rgba(10,20,50,0.45)",zIndex:1200,display:"flex",alignItems:isMobile?"flex-end":"center",justifyContent:"center",padding:isMobile?0:20}}>
      <div role="dialog" aria-modal="true" style={{background:"#fff",borderRadius:isMobile?"16px 16px 0 0":14,padding:24,width:"100%",maxWidth:460,boxSizing:"border-box",fontFamily:"'DM Sans','Helvetica Neue',sans-serif",color:"#1a1a2e"}}>
        <div style={{fontSize:17,fontWeight:700,marginBottom:8}}>{amendOf?`Save as ${next} and download?`:"Save this quote and download?"}</div>
        <div style={{fontSize:13,color:"#4a5568",lineHeight:1.5,marginBottom:18}}>
          {amendOf
            ?<>Downloading the PDF saves this new version to the Quote Log as a Draft. <strong>{amendOf}</strong> keeps its status until you send the new one.</>
            :<>Downloading the PDF saves it to the Quote Log as a Draft, with the next {prefix} number.</>}
        </div>
        <div style={{display:"flex",gap:10,flexDirection:isMobile?"column":"row"}}>
          <button type="button" style={btn(true)} onClick={onConfirm} autoFocus>Save and download</button>
          <button type="button" style={btn(false)} onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// Above the Quote Log: any calculator holding unsaved work.
function DraftsStrip({drafts,onContinue,onDiscard,isMobile}){
  if(!drafts.length) return null;
  return(
    <div style={{background:"#FFF8E6",border:"1px solid #F0D98A",borderRadius:12,padding:isMobile?"10px 12px":"10px 16px",marginBottom:18,fontFamily:"'IBM Plex Sans', sans-serif"}}>
      <div style={{fontSize:10,fontWeight:700,letterSpacing:"0.12em",textTransform:"uppercase",color:"#93631A",marginBottom:6}}>Unsaved drafts</div>
      {drafts.map(({tab,draft})=>(
        <div key={tab} style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10,flexWrap:"wrap",padding:"6px 0",borderTop:"1px solid #F3E4B5"}}>
          <div style={{fontSize:13,color:"#1a1a2e",minWidth:0}}>
            <strong>{CALC_NAMES[tab]}:</strong> {draft.summary||"unsaved work"}<span style={{color:"#8a7a50"}}> · edited {fmtDraftTime(draft.at)}</span>
          </div>
          <div style={{display:"flex",gap:8}}>
            <button type="button" onClick={()=>onContinue(tab)} aria-label={`Resume ${CALC_NAMES[tab]} draft`} style={{minHeight:isMobile?40:32,padding:"0 14px",borderRadius:8,border:"none",background:"#1D3461",color:"#fff",fontSize:12,fontWeight:700,cursor:"pointer"}}>Resume draft</button>
            <button type="button" onClick={()=>onDiscard(tab)} style={{minHeight:isMobile?40:32,padding:"0 14px",borderRadius:8,border:"1px solid #d0d8e8",background:"#fff",color:"#4a5568",fontSize:12,fontWeight:700,cursor:"pointer"}}>Discard</button>
          </div>
        </div>
      ))}
    </div>
  );
}

// Fills an empty job.estimator with the signed-in user's name.
function withDefaultEstimator(job,user){
  if((job.estimator||"").trim()) return job;
  const name=user?.fullName||[user?.firstName,user?.lastName].filter(Boolean).join(" ")||"";
  return name?{...job,estimator:name}:job;
}

// ─── CLIENT SCOPE LOCK ───────────────────────────────────────────────────────
// Client scope (see scopeTemplates.js "Scope of Works builder"): the
// template text is read-only until "Edit template text" unlocks it
// (scopeCustom, text in scopeCustomText); "Additional scope notes"
// (scopeExtra) print as their own paragraph at the end, before the
// protection line. notes.clientScope is rebuilt from these on every save.
const DEFAULT_NOTES={estimator:"",internal:"",clientScope:"",exclusions:"",scopeV:2,scopeCustom:false,scopeCustomText:"",scopeExtra:"",scopeLegacy:false};
function initialNotes(initialState){
  const n=initialState?.notes;
  return n?upgradeScopeNotes(n):DEFAULT_NOTES;
}
// The scope box: template (read-only) or edited text, additional notes, and
// the protection line that will follow (shown, not editable).
function ScopeEditor({notes,setNotes,templateBody,autoSections,t}){
  const custom=!!notes.scopeCustom;
  const unlock=()=>setNotes(n=>({...n,scopeCustom:true,scopeCustomText:templateBody}));
  const reset=()=>{ if(!notes.scopeLegacy||window.confirm("Replace this quote's saved scope with the template text?")) setNotes(n=>({...n,scopeCustom:false,scopeCustomText:"",scopeLegacy:false})); };
  const badge=custom?(notes.scopeLegacy?"Saved scope":"Edited"):"Template";
  return(
    <>
      <Field label={
        <span style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:8}}>
          <span style={{display:"inline-flex",alignItems:"center",gap:8}}>
            Client-Facing Scope
            <span style={{fontSize:9,padding:"2px 7px",borderRadius:999,background:t.accent+"22",color:t.accent,fontWeight:700,letterSpacing:"0.08em",textTransform:"uppercase"}}>{badge}</span>
          </span>
          {custom
            ?<span onClick={reset} style={{fontSize:10,color:t.accent,cursor:"pointer",fontWeight:600,letterSpacing:"0.05em"}}>↺ Reset to template</span>
            :templateBody&&<span onClick={unlock} style={{fontSize:10,color:t.accent,cursor:"pointer",fontWeight:600,letterSpacing:"0.05em"}}>✎ Edit template text</span>}
        </span>
      }>
        <textarea
          style={{...txta,minHeight:120,...(custom?{}:{opacity:0.75,cursor:"default"})}}
          value={custom?notes.scopeCustomText:templateBody}
          readOnly={!custom}
          onChange={e=>{ const v=e.target.value; setNotes(n=>({...n,scopeCustomText:v})); }}
          placeholder={custom?"What's included in scope...":"Choose the work above and the scope fills in here."}
        />
      </Field>
      <Field label="Additional scope notes">
        <textarea style={{...txta,minHeight:60}} value={notes.scopeExtra||""} onChange={e=>{ const v=e.target.value; setNotes(n=>({...n,scopeExtra:v})); }} placeholder="Anything specific to this job. Printed as its own paragraph at the end of the scope."/>
      </Field>
      {autoSections?.length>0&&<div style={{fontSize:11,color:t.textMuted,marginTop:-6,marginBottom:12}}>The quote prints this as 1. Clean, then {autoSections.join(", ")}. Notes that name the protection system go under Protection; other notes print last.</div>}
    </>
  );
}

// ─── JOB LOCATION ────────────────────────────────────────────────────────────
// In-unit jobs run the compressor off mains power at our unit, so there's no
// generator diesel - an electricity line (£/hr per compressor) replaces fuel.
// On-site jobs keep the diesel/fuel-burn costing. Quotes saved before this
// existed have no jobLocation and are treated as on-site, so their pricing
// is unchanged when reopened or amended.
const JOB_LOCATIONS=[{value:"on-site",label:"On-site (generator)"},{value:"in-unit",label:"In-unit (mains power)"}];
const DEFAULT_ELECTRICITY_PER_HR=3;

const SUB_SERVICES={industrial:["Machinery Cleaning","Production Line Cleaning","Electrical Panel Cleaning","Grease & Oil Removal","Fire / Damage Restoration"],marine:["Engine Bay Cleaning","Salt Removal","Restoration Prep"],aviation:["Engine Component Cleaning","Airframe Surface Cleaning","Interior Cleaning"],agriculture:["Equipment Cleaning","Storage Cleaning","Pest Residue Removal"],data_centres:["Live Equipment Cleaning","Server Rack Cleaning","Raised Floor Cleaning","Cable Infrastructure Cleaning"],facilities:["Plant Room Cleaning","Pool Plant Cleaning","Gym Equipment Cleaning"],catering:["Kitchen Equipment Cleaning","Extraction & Duct Cleaning","Cold Store Cleaning"],healthcare:["Theatre Equipment Cleaning","Medical Equipment Cleaning","Sterile Area Prep"]};
const CONTAMINATION_TYPES=["Surface Dust / Light Contamination","Environmental Soiling (General Dirt Build-up)","Oil & Grease Contamination","Carbon / Combustion Deposits","Corrosion & Oxidation","Biological Contamination","Fire & Smoke Residue","Mixed / Unknown Contamination"];
// Contamination usually met in each Industrial category. It only ORDERS the
// dropdown: these first under "Common for this industry", every other type
// under "Other". Nothing is hidden - there isn't enough real job data yet to
// rule types out. Tighten this one constant once there is.
const [C_DUST,C_SOIL,C_OIL,C_CARBON,C_CORROSION,C_BIO,C_FIRE,C_MIXED]=CONTAMINATION_TYPES;
const CONTAMINATION_COMMON_BY_CATEGORY={
  industrial:  [C_DUST,C_SOIL,C_OIL,C_CARBON,C_CORROSION,C_FIRE,C_MIXED],
  marine:      [C_SOIL,C_OIL,C_CARBON,C_CORROSION,C_BIO,C_MIXED],
  aviation:    [C_DUST,C_OIL,C_CARBON,C_CORROSION,C_MIXED],
  agriculture: [C_SOIL,C_OIL,C_BIO,C_CORROSION,C_MIXED],
  data_centres:[C_DUST,C_SOIL,C_MIXED],
  facilities:  [C_DUST,C_SOIL,C_OIL,C_CORROSION,C_BIO,C_MIXED],
  catering:    [C_OIL,C_CARBON,C_BIO,C_FIRE,C_MIXED],
  healthcare:  [C_DUST,C_BIO,C_MIXED],
};
function contaminationGroups(category){
  const common=CONTAMINATION_COMMON_BY_CATEGORY[category]||[];
  return {common, other:CONTAMINATION_TYPES.filter(c=>!common.includes(c))};
}
const CONTAMINATION_SEVERITIES=["Light","Moderate","Heavy","Severe"];
const SURFACE_TYPES=["Painted Steel","Stainless Steel","Aluminium","Plastic","Composite","Electrical Components","Mixed Surfaces"];
const SURFACE_SENSITIVITIES=["Low","Medium","High"];
const QUANTITY_UNITS=["m²","linear metres","item count","machine count","room count","custom"];
const PRODUCTION_RATE_PRESETS={"Surface Dust / Light Contamination":{Light:12,Moderate:10,Heavy:7,Severe:5},"Environmental Soiling (General Dirt Build-up)":{Light:10,Moderate:8,Heavy:6,Severe:4},"Oil & Grease Contamination":{Light:8,Moderate:6,Heavy:4,Severe:3},"Carbon / Combustion Deposits":{Light:6,Moderate:5,Heavy:3,Severe:2},"Corrosion & Oxidation":{Light:7,Moderate:5,Heavy:3,Severe:2},"Biological Contamination":{Light:8,Moderate:6,Heavy:4,Severe:3},"Fire & Smoke Residue":{Light:6,Moderate:4,Heavy:3,Severe:2},"Mixed / Unknown Contamination":{Light:8,Moderate:6,Heavy:4,Severe:3}};
const DRY_ICE_PRESETS={
  "Surface Dust / Light Contamination":            {Light:4,  Moderate:5,  Heavy:7,  Severe:8},
  "Environmental Soiling (General Dirt Build-up)": {Light:6,  Moderate:8,  Heavy:10, Severe:13},
  "Oil & Grease Contamination":                    {Light:10, Moderate:12, Heavy:16, Severe:19},
  "Carbon / Combustion Deposits":                  {Light:13, Moderate:16, Heavy:21, Severe:26},
  "Corrosion & Oxidation":                         {Light:14, Moderate:18, Heavy:23, Severe:29},
  "Biological Contamination":                      {Light:8,  Moderate:10, Heavy:13, Severe:16},
  "Fire & Smoke Residue":                          {Light:11, Moderate:14, Heavy:18, Severe:22},
  "Mixed / Unknown Contamination":                 {Light:10, Moderate:12, Heavy:16, Severe:19},
};
// Sensitivity multipliers — applied on top of contamination base rate
const SENSITIVITY_MULT={"Low":1.2,"Medium":1.0,"High":0.75};
// Machine dry ice multipliers relative to Champ Vario baseline
const MACHINE_DI_MULT={"Champ Vario":1.0,"Champ Ultra":1.6,"Karcher IB 15/120":0.85,"Cold Jet Aero 40":1.4,"Custom":1.0};
// Machine min/max clamps (kg/hr)
const MACHINE_DI_CLAMP={"Champ Vario":{min:4,max:30},"Champ Ultra":{min:3,max:70},"Karcher IB 15/120":{min:3,max:25},"Cold Jet Aero 40":{min:3,max:60},"Custom":{min:1,max:100}};
const ACCESS_IMPACT={"Easy (vehicle adjacent)":1.0,"Moderate (10–30m from vehicle)":0.9,"Difficult (30–100m)":0.75,"Severe (100m+, stairs, obstacles)":0.6};
const VERTICAL_IMPACT={"Ground Level":1.0,"1–2 Floors":0.85,"Multi-Level / Complex":0.7};
const ENVIRON_IMPACT={"Open / External":1.0,"Indoor Industrial":0.9,"Confined Space":0.7,"Live Operational Environment":0.75};
const SETUP_OVERHEAD={"Minimal":0.5,"Standard":1.0,"Complex":1.5,"Highly Complex":2.5};
const HOURS_RESTRICT={"None":0,"Limited Hours":1,"Strict Access Window / Shutdown Window":2};
const WEATHER_OH={"None":0,"Moderate":0.5,"Severe":1.5};
const RISK_UPLIFT={access:{"Easy (vehicle adjacent)":0,"Moderate (10–30m from vehicle)":1,"Difficult (30–100m)":3,"Severe (100m+, stairs, obstacles)":5},environment:{"Open / External":0,"Indoor Industrial":1,"Confined Space":6,"Live Operational Environment":5},severity:{"Light":0,"Moderate":1,"Heavy":3,"Severe":6}};
const FUEL_INTENSITY={Delicate:0.8,Standard:1.0,Heavy:1.15,Aggressive:1.25};

const fmt=(n)=>`£${Number(n||0).toLocaleString("en-GB",{minimumFractionDigits:2,maximumFractionDigits:2})}`;
const fmtN=(n,dp=1)=>Number(n||0).toFixed(dp);

// ─── Discount (optional, applied before VAT) ─────────────────────────────────
// Returns the post-discount price plus the breakdown needed to show it on the
// quote/PDF. When no discount is set, discountAmount is 0 and finalPrice ===
// subtotal, so nothing changes for quotes that don't use it.
// Restores a saved quote's commercial settings (margin, contingency, rounding
// and the discount: type, value/target and reason) exactly, over the
// calculator's defaults. _discountSnapshot is dropped: it's re-derived from
// the live discount on every save, and carrying the old one would keep
// showing a discount on Send to Client / the public quote after it's removed.
function restoreCommercial(saved, defaults){
  if(!saved) return defaults;
  const {_discountSnapshot, ...rest}=saved;
  // A saved quote's margin counts as chosen, so changing its job location
  // doesn't move it (an autosaved draft carries its own marginSet).
  return {...defaults, marginSet:true, ...rest};
}

// Starting margin by job location: in-unit work carries less overhead.
// Only the slider's starting point - switching location moves it until the
// estimator moves the slider themselves (commercial.marginSet).
const DEFAULT_MARGIN_BY_LOCATION={"in-unit":43,"on-site":60};
const defaultMarginFor=loc=>DEFAULT_MARGIN_BY_LOCATION[loc]??DEFAULT_MARGIN_BY_LOCATION["on-site"];
// Margin colour bands (slider and gross margin badges): green at or above
// `green`, amber at or above `amber`, red below.
const MARGIN_BANDS_BY_LOCATION={"in-unit":{green:43,amber:35},"on-site":{green:60,amber:50}};
const marginColorFor=(value,loc)=>{
  const b=MARGIN_BANDS_BY_LOCATION[loc]??MARGIN_BANDS_BY_LOCATION["on-site"];
  return value>=b.green?t.success:value>=b.amber?t.warning:t.danger;
};

// Shown on a reopened (amended) quote that carries a discount, so it can't
// be dropped or changed without noticing. Edit it in the Commercial section.
function DiscountBanner({amount,label,reason,t}){
  if(!(amount>0)) return null;
  const desc=[reason,label].filter(Boolean).join(", ");
  return(
    <div style={{background:t.accent+"18",border:`1px solid ${t.accent}55`,borderRadius:10,padding:"10px 14px",marginBottom:14,fontSize:13,color:t.text}}>
      <strong>Discount applied:</strong> {desc?`${desc} `:""}(−{fmt(amount)})
      <div style={{fontSize:11,color:t.textMuted,marginTop:3}}>Carried over from the saved quote. Change or remove it in the Commercial section.</div>
    </div>
  );
}

// Reopened-quote check: if the calculator doesn't reproduce the total the
// quote was saved at, say so loudly and list the cost lines that moved
// (an input that wasn't saved, a changed supplier price, etc.) so a
// different price can't be re-saved or resent without noticing.
const COST_LINE_LABELS={labourCost:"Labour",travelTimeCost:"Travel time (labour)",fuelCost:"Fuel",electricityCost:"Electricity",dryIceCost:"Dry ice",materialsCost:"Materials",conciergeCost:"Concierge",detailingCost:"Detailing",travelCost:"Travel (fuel/vehicle)",accomCost:"Accommodation",subCost:"Subsistence",equipmentRecoveryCost:"Equipment"};
function RecalcCheckBanner({savedTotal,currentTotal,savedCb,currentCb,t}){
  if(!(savedTotal>0)||Math.abs(currentTotal-savedTotal)<0.005) return null;
  const moved=Object.keys(COST_LINE_LABELS)
    .filter(k=>savedCb&&savedCb[k]!=null&&Math.abs((Number(currentCb?.[k])||0)-(Number(savedCb[k])||0))>=0.005)
    .map(k=>`${COST_LINE_LABELS[k]} ${fmt(Number(savedCb[k])||0)} → ${fmt(Number(currentCb?.[k])||0)}`);
  return(
    <div style={{background:t.danger+"18",border:`1px solid ${t.danger}`,borderRadius:10,padding:"10px 14px",marginBottom:14,fontSize:13,color:t.text}}>
      <strong style={{color:t.danger}}>⚠ Price doesn't match the saved quote.</strong> Saved at {fmt(savedTotal)} ex VAT, recalculates to {fmt(currentTotal)}.
      {moved.length>0&&<div style={{marginTop:4}}>Changed: {moved.join(" · ")}</div>}
      <div style={{fontSize:11,color:t.textMuted,marginTop:3}}>Check those inputs before saving or sending. Quotes saved before every input was stored can't always be reproduced exactly.</div>
    </div>
  );
}

function applyDiscount(subtotal, commercial){
  const type=commercial?.discountType;
  const reason=(commercial?.discountReason||"").trim();
  if(!type||type==="none"){
    return {finalPrice:subtotal,discountAmount:0,discountLabel:"",discountReason:""};
  }
  if(type==="target"){
    const rawTarget=commercial?.discountTargetPrice;
    const target=rawTarget==null?NaN:Number(rawTarget); // distinguish "not set" from an explicit 0
    if(!(target>=0)||target>=subtotal){
      return {finalPrice:subtotal,discountAmount:0,discountLabel:"",discountReason:""};
    }
    const discountAmount=subtotal-target; // subtotal already confirmed > target above
    return {
      finalPrice:target,
      discountAmount,
      discountLabel:`target ${fmt(target)}`,
      discountReason:reason,
    };
  }
  const rawValue=Number(commercial?.discountValue)||0;
  if(rawValue<=0){
    return {finalPrice:subtotal,discountAmount:0,discountLabel:"",discountReason:""};
  }
  let discountAmount=type==="percent"?subtotal*(rawValue/100):rawValue;
  discountAmount=Math.max(0,Math.min(discountAmount,subtotal)); // never discount below £0
  return {
    finalPrice:subtotal-discountAmount,
    discountAmount,
    discountLabel:type==="percent"?`${rawValue}%`:fmt(rawValue),
    discountReason:reason,
  };
}

// Writes a generated quote document into a tab opened in advance (see below)
// and triggers the browser's own print dialog - "Save as PDF" is then one
// click there, rather than downloading a raw .html file and opening it
// manually. onload is the primary trigger; the timeout is a fallback since
// onload doesn't fire reliably in every browser after document.write().
// The PDF button's save is taking a while and being checked (see
// writeWithSaveCheck): pinned to the top so it can't be missed.
// After the PDF button saved the quote: the calculator stays put and says
// so. Shown until the quote is changed again or closed.
function SavedBanner({refNo,onClose}){
  return(
    <div role="status" style={{display:"flex",alignItems:"center",gap:10,marginBottom:16,padding:"10px 14px",background:t.successSoft,border:`1px solid ${t.success}`,borderRadius:10,color:t.success,fontFamily:fonts.body}}>
      <span style={{fontSize:16,fontWeight:800,lineHeight:1}} aria-hidden="true">✓</span>
      <span style={{flex:1,minWidth:0,fontSize:14,fontWeight:700}}>Saved as {refNo}</span>
      <button type="button" onClick={onClose} aria-label="Close" style={{background:"none",border:"none",color:t.success,cursor:"pointer",fontSize:15,padding:"2px 4px"}}>✕</button>
    </div>
  );
}
function SaveCheckingBanner(){
  return(
    <div role="status" style={{position:"fixed",top:"calc(12px + env(safe-area-inset-top, 0px))",left:"50%",transform:"translateX(-50%)",zIndex:600,width:"min(560px, calc(100% - 32px))",background:"#FFF3D6",border:"2px solid #F5A623",borderRadius:10,padding:"14px 18px",display:"flex",gap:12,alignItems:"flex-start",boxShadow:"0 6px 24px rgba(0,0,0,0.35)"}}>
      <div style={{fontSize:22,lineHeight:1}}>⏳</div>
      <div>
        <div style={{fontWeight:800,color:"#7B5800",fontSize:15}}>Still saving… checking whether it went through</div>
        <div style={{color:"#7B5800",fontSize:12.5,marginTop:4}}>The Quote Log is slow to reply. Don't press Save or PDF again; this can take up to half a minute.</div>
      </div>
    </div>
  );
}

// A client email that's one of ours (INTERNAL_EMAILS) means it's a test:
// tick Test quote automatically. Once per address, so unticking it by hand
// sticks until the email changes again. Starts from the email the quote
// opened with, so reopening a saved quote never changes its flag by itself.
function useAutoTestQuote(email,testQuote,setTestQuote){
  const lastAuto=useRef(String(email||"").trim().toLowerCase());
  React.useEffect(()=>{
    const e=String(email||"").trim().toLowerCase();
    if(!e||e===lastAuto.current||!isInternalOnlySend(e)) return;
    lastAuto.current=e;
    if(!testQuote) setTestQuote(true);
  },[email]); // eslint-disable-line react-hooks/exhaustive-deps
}

// Internal job sheet (jobSheet.js) - shown on screen over the calculator
// (JobSheetOverlay in App), so unsaved inputs stay put; it has its own Print.
function openJobSheet(spec){
  window.dispatchEvent(new CustomEvent("sublime:jobsheet",{detail:buildJobSheetHTML(spec)}));
}
const hrs=n=>`${fmtN(n)} hrs`;
// "2026-11-12" -> "12 November 2026" for the job sheet.
const sheetDate=iso=>iso?quoteDateStr(String(iso).split("-").reverse().join("/")):"";

function printQuoteDocument(printWin, html){
  if(!printWin)return;
  printWin.document.open();
  printWin.document.write(html);
  printWin.document.close();
  let printed=false;
  const doPrint=()=>{ if(printed)return; printed=true; printWin.focus(); printWin.print(); };
  printWin.onload=doPrint;
  setTimeout(doPrint,500);
}

const inp={width:"100%",background:t.bg,border:`1px solid ${t.border}`,borderRadius:8,padding:"10px 13px",color:t.text,fontSize:15,outline:"none",boxSizing:"border-box",fontFamily:fonts.body,WebkitAppearance:"none",WebkitBoxShadow:`0 0 0px 1000px ${t.bg} inset`,WebkitTextFillColor:t.text};
const sel={...inp,cursor:"pointer",paddingRight:34,backgroundImage:`url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1.5l5 5 5-5' fill='none' stroke='%238FA3B3' stroke-width='1.6'/%3E%3C/svg%3E")`,backgroundRepeat:"no-repeat",backgroundPosition:"right 13px center"};
const txta={...inp,resize:"vertical",minHeight:72};

function Toggle({value,onChange,label}){
  return(
    <div onClick={()=>onChange(!value)} style={{display:"flex",alignItems:"center",gap:10,cursor:"pointer",userSelect:"none",padding:"4px 0"}}>
      <div style={{width:40,height:22,borderRadius:11,background:value?t.accent:t.border,position:"relative",transition:"background 0.2s",flexShrink:0}}>
        <div style={{width:16,height:16,borderRadius:"50%",background:value?t.onAccent:t.text,position:"absolute",top:3,left:value?21:3,transition:"left 0.2s"}}/>
      </div>
      <span style={{fontSize:14,color:value?t.text:t.textMuted}}>{label}</span>
    </div>
  );
}

// Number box for the calculators. Entering the box (click, tap or Tab)
// selects what's in it, so typing replaces the old value instead of adding to
// it ("35" then typing 40 gives 40, not 3540). Leaving the box tidies what was
// typed ("035" → "35", "4." → "4"), and the unit shows inside it: pre "£"
// before money, unit (kg, h, %, /h…) after. With neither it's a plain number input.
const tidyNumber=raw=>{const s=String(raw??"").trim();if(s==="")return s;const n=Number(s);return Number.isFinite(n)?String(n):s;};
function NumInput({pre,unit,style,onBlur,onChange,onFocus,onMouseUp,className,...props}){
  // The click that focuses the box would otherwise clear the selection on
  // mouse-up (Safari), so that one mouse-up is ignored.
  const justFocused=useRef(false);
  const focus=e=>{
    const el=e.target;
    justFocused.current=true;
    try{ el.select(); }catch(err){ /* not selectable */ }
    // iOS Safari applies the selection only after focus has settled.
    setTimeout(()=>{ try{ if(document.activeElement===el) el.select(); }catch(err){ /* not selectable */ } },0);
    onFocus&&onFocus(e);
  };
  const mouseUp=e=>{
    if(justFocused.current){ justFocused.current=false; e.preventDefault(); }
    onMouseUp&&onMouseUp(e);
  };
  const blur=e=>{
    justFocused.current=false;
    const tidy=tidyNumber(e.target.value);
    if(tidy!==e.target.value){ e.target.value=tidy; onChange&&onChange(e); }
    onBlur&&onBlur(e);
  };
  const input=<input {...props} type="number" className={[className,unit?"sp-num-unit":""].filter(Boolean).join(" ")||undefined} onChange={onChange} onFocus={focus} onMouseUp={mouseUp} onBlur={blur}
    style={{...style,...(pre?{paddingLeft:28}:{}),...(unit?{paddingRight:14+unit.length*8}:{})}}/>;
  if(!pre&&!unit) return input;
  const affix={position:"absolute",top:"50%",transform:"translateY(-50%)",fontSize:13,color:t.textMuted,pointerEvents:"none",fontFamily:fonts.body};
  return(
    <div style={{position:"relative"}}>
      {pre&&<span style={{...affix,left:13}}>{pre}</span>}
      {input}
      {unit&&<span style={{...affix,right:12}}>{unit}</span>}
    </div>
  );
}

// Tickbox row for the dark calculators, drawn like the service and add-on
// rows' boxes (the browser's own checkbox doesn't show up on the dark
// background): blue filled with a white tick when on, clear outline when off.
// The whole row is the label, so clicking the text toggles it too; a real
// checkbox underneath keeps keyboard (Space) and screen readers working.
function CheckRow({checked,onChange,children,style}){
  return(
    <label style={{position:"relative",display:"flex",alignItems:"center",gap:10,fontSize:13,color:t.text,cursor:"pointer",userSelect:"none",padding:"4px 0",minHeight:32,...style}}>
      <input type="checkbox" checked={!!checked} onChange={e=>onChange(e.target.checked)} className="sp-check-input"
        style={{position:"absolute",opacity:0,width:1,height:1,margin:0,pointerEvents:"none"}}/>
      <span aria-hidden="true" className="sp-check-box" style={{width:18,height:18,borderRadius:4,border:`2px solid ${checked?t.accent:t.textDim}`,background:checked?t.accent:"transparent",display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,fontWeight:700,lineHeight:1,color:"#fff",flexShrink:0,boxSizing:"border-box"}}>{checked?"✓":""}</span>
      <span>{children}</span>
    </label>
  );
}

// Under the calculators' Scheduled Date: booked jobs (Accepted or later) on
// the same day, so a date can't quietly clash with one. Reads the Quote Log
// at most once a minute, only while a date is set.
let _clashQuotes=null,_clashAt=0;
function JobDateClash({date,quoteNumber,test}){
  const[quotes,setQuotes]=useState(_clashQuotes);
  React.useEffect(()=>{
    if(!date) return;
    if(_clashQuotes&&Date.now()-_clashAt<60000){ setQuotes(_clashQuotes); return; }
    let live=true;
    fetchAllQuotes().then(q=>{ _clashQuotes=q; _clashAt=Date.now(); if(live) setQuotes(q); }).catch(()=>{});
    return()=>{ live=false; };
  },[date]);
  if(!date||!quotes) return null;
  const clashes=overlappingJobs({quoteNumber,start:date,end:"",test},quotes);
  if(!clashes.length) return null;
  const day=iso=>new Date(iso+"T00:00:00").toLocaleDateString("en-GB",{day:"numeric",month:"short"});
  return(
    <div role="status" style={{fontSize:12,color:t.warning,marginTop:6,lineHeight:1.45}}>
      ⚠ Another booked job on this day: {clashes.map(q=>{ const d=getJobDates(q); return `${q["Quote Number"]} (${q["Client Name"]||"no client"}, ${day(d.start)}${d.end!==d.start?`–${day(d.end)}`:""}, ${q["Status"]})`; }).join("; ")}
    </div>
  );
}

function Field({label,children,style}){
  return(
    <div style={{marginBottom:14,minWidth:0,...style}}>
      <label style={{display:"block",...type.statLabel,color:t.textMuted,marginBottom:5}}>{label}</label>
      {children}
    </div>
  );
}

// Inside a Section (which is already a card), a Card is a plain group.
function Card({children,style}){
  return <div style={{...style}}>{children}</div>;
}

function Divider({label}){
  return(
    <div style={{display:"flex",alignItems:"center",gap:10,margin:"18px 0 14px"}}>
      {label&&<Eyebrow style={{color:t.textMuted,whiteSpace:"nowrap"}}>{label}</Eyebrow>}
      <div style={{flex:1,height:1,background:t.border}}/>
    </div>
  );
}

// Small supporting stat: label (muted) over value (mono). "auto" marks a
// figure the calculator worked out.
function InfoChip({label,value,auto}){
  return(
    <div style={{background:t.bg,borderRadius:8,padding:"10px 12px",border:`1px solid ${t.border}`,minWidth:0}}>
      <div style={{...type.statLabel,color:t.textMuted,marginBottom:4,display:"flex",justifyContent:"space-between",alignItems:"center",gap:6}}>
        {label}
        {auto&&<span style={{...type.pill,fontSize:"0.62rem",padding:"1px 7px",background:t.accentDim,color:t.accent}}>Auto</span>}
      </div>
      <div style={{...type.statValue,fontSize:13.5,color:t.text,overflowWrap:"anywhere"}}>{value}</div>
    </div>
  );
}

// Sources live dry-ice pricing/delivery/bin data from the Suppliers tab
// (Admin → Suppliers) instead of the calculator having its own copy of the
// numbers - shared by both calculators so a Suppliers-tab price update is
// immediately live in both places, not just one. `selectedId` is this
// quote's own pick (equip.dryIceSupplierId / rates.dryIceSupplierId, not
// remembered between quotes), or null; falls back to the supplier ticked
// Primary in Admin (else the cheapest by £/kg) when nothing's picked or the
// picked one's no longer active.
function useDryIceSupplier(selectedId){
  const[suppliers,setSuppliers]=useState([]);
  React.useEffect(()=>{ fetchSuppliers().then(setSuppliers).catch(()=>{}); },[]);
  const dryIceSuppliers=findDryIceSuppliers(suppliers);
  const primary=primaryDryIceSupplier(dryIceSuppliers);
  const selectedSupplier=dryIceSuppliers.find(s=>s.id===selectedId)||primary;
  return{dryIceSuppliers,selectedSupplier,primary};
}
// Supplier dropdown wording: delivered-tier suppliers have no £/kg.
const dryIceOptionLabel=(s,primary)=>`${s.name} — ${hasDeliveredTiers(s)?"delivered tiers":`£${(Number(s.currentCostPricePerKg)||0).toFixed(3)}/kg`}${s.id===primary?.id?" (primary)":""}`;

// Amending a saved quote keeps the dry ice cost it was saved with until the
// kg (inc. wastage), the supplier pick or the bin size changes - no silent
// repricing of a quote the client has already seen (Edward, 30 Oct 2026).
// Returns {cost, kg, pick} from the saved quote, or null for a new quote, a
// copy (no _savedTotalExVat) or a quote saved without the figures.
// rates: the saved equip (Industrial) or rates (Automotive).
function savedDryIce(initialState,rates){
  const cb=initialState?.costBreakdown;
  if(initialState?._savedTotalExVat==null||cb?.dryIceCost==null||cb?.dryIceKgAdjusted==null) return null;
  return {cost:Number(cb.dryIceCost),kg:Number(cb.dryIceKgAdjusted),pick:`${rates?.dryIceSupplierId||""}|${rates?.binCapacityLabel||""}`};
}
// The dry ice cost to charge: the saved one while nothing that affects it has
// changed (see savedDryIce), else live from the supplier (dryIceCostFor).
function dryIceCharge(supplier,adjKg,binCapacityKg,saved,pick){
  const live=dryIceCostFor(supplier,adjKg,binCapacityKg);
  const keep=!!saved&&saved.pick===pick&&Math.abs(saved.kg-adjKg)<0.05;
  return {...live,cost:keep?saved.cost:live.cost,liveCost:live.cost,pinned:keep};
}

// The dry ice block for a delivered-tier supplier (Apsley): the tiers, the
// one(s) this job's kg lands on, and the charge. Replaces the £/kg, bin and
// delivery fields, which don't apply.
function DeliveredTiersPanel({supplier,dryIceKg,adjKg,charge,wastageField}){
  const tiers=deliveredTiersOf(supplier).sort((a,b)=>a.kg-b.kg);
  const onPlan=t=>(charge.tierPlan?.tiers||[]).some(p=>p.kg===t.kg);
  return(
    <>
      <div className="sp-grid" style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12}}>
        <InfoChip label="Pricing" value="Delivered tiers" auto/>
        <InfoChip label="Delivery & collection" value="Included" auto/>
        {wastageField}
      </div>
      {deliveredTiersExpired(supplier)&&(
        <div style={{background:t.warningSoft,border:`1px solid ${t.warning}`,borderRadius:8,padding:"10px 14px",marginTop:8,fontSize:12,color:t.warning}}>
          ⚠ {supplier.name}'s tier prices were valid to {supplier.tiersValidTo}. Still used here: confirm the current prices in Admin → Suppliers.
        </div>
      )}
      <Divider label="Delivered Tiers"/>
      <div className="sp-grid" style={{display:"grid",gridTemplateColumns:`repeat(${Math.min(tiers.length,4)},1fr)`,gap:8,marginBottom:14}}>
        {tiers.map(tr=>(
          <div key={tr.kg} style={{padding:"10px 12px",borderRadius:8,border:`1px solid ${onPlan(tr)?t.accent:t.border}`,background:onPlan(tr)?t.accentDim:"transparent"}}>
            <div style={{fontSize:13,fontWeight:700,color:onPlan(tr)?t.accent:t.text}}>{tr.kg}kg</div>
            <div style={{fontSize:11,color:t.textMuted}}>£{tr.price.toFixed(2)}</div>
          </div>
        ))}
      </div>
      <div className="sp-grid" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:14}}>
        <InfoChip label="Raw dry ice" value={`${fmtN(dryIceKg)} kg`}/>
        <InfoChip label="Adjusted (inc. wastage)" value={`${fmtN(adjKg)} kg → ${charge.tierPlan?tierPlanLabel(charge.tierPlan):"—"} → ${fmt(charge.cost)}`}/>
      </div>
      {charge.pinned&&charge.liveCost!==charge.cost&&<DryIcePinnedNote charge={charge}/>}
    </>
  );
}
function DryIcePinnedNote({charge}){
  return(
    <div style={{fontSize:12,color:t.textMuted,marginTop:-6,marginBottom:14}}>
      Dry ice kept as saved ({fmt(charge.cost)}; today's price would be {fmt(charge.liveCost)}). It reprices if the kg, supplier or bin size changes.
    </div>
  );
}

// ─── READ-ONLY VIEW ("Open" in the Quote Log) ─────────────────────────────────
// A saved quote shown in its calculator with every input locked: no Save,
// no PDF / Job Sheet, no autosave, nothing written. Sections all open.
// readOnly prop: { ref, onAmend (null when Amend isn't offered), amendLabel,
// blockedReason, onBack }.
const ReadOnlyCtx=React.createContext(false);
function ReadOnlyBanner({ro}){
  return(
    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:12,flexWrap:"wrap",background:t.accentDim,border:`1px solid ${t.accent}`,borderRadius:10,padding:"12px 16px",marginBottom:16}}>
      <div style={{minWidth:0}}>
        <div style={{fontSize:15,fontWeight:700,color:t.text}}>Viewing {ro.ref} · read only</div>
        <div style={{fontSize:12.5,color:t.textMuted,marginTop:2}}>Price and breakdown as saved. Nothing here can be changed or saved.</div>
      </div>
      <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
        <button type="button" onClick={ro.onBack} style={{...secondaryBtn,padding:"8px 14px",fontSize:13}}>← Quote Log</button>
        {ro.onAmend
          ? <button type="button" onClick={ro.onAmend} style={{...primaryBtn,padding:"8px 14px",fontSize:13}}>{ro.amendLabel||"Amend to make changes"}</button>
          : ro.blockedReason&&<span style={{display:"inline-flex",alignItems:"center",gap:6,fontSize:12.5,color:t.textMuted,maxWidth:280}}><span aria-hidden="true" style={{fontSize:13,lineHeight:1}}>ⓘ</span>{ro.blockedReason}</span>}
      </div>
    </div>
  );
}
// Locks every control inside (inputs, selects, buttons) natively.
function ReadOnlyForm({ro,children}){
  if(!ro) return children;
  return(
    <ReadOnlyCtx.Provider value={true}>
      <ReadOnlyBanner ro={ro}/>
      <fieldset disabled style={{border:0,padding:0,margin:0,minWidth:0}}>{children}</fieldset>
    </ReadOnlyCtx.Provider>
  );
}
// The figures a read-only view shows: what was charged at save time where
// the quote stored it (State JSON costBreakdown, the sheet's total, the
// discount snapshot); anything not stored stays as worked out from the
// saved inputs, for display only. keyMap: costBreakdown key -> calc key.
function asSaved(calc,initialState,keyMap){
  const cb=initialState?.costBreakdown||{};
  const out={...calc};
  Object.entries(keyMap).forEach(([k,ck])=>{ if(cb[k]!=null) out[ck]=cb[k]; });
  if(typeof initialState?.totalChargeableHrs==="number") out.totalChargeableHrs=initialState.totalChargeableHrs;
  const savedTotal=Number(initialState?._savedTotalExVat);
  if(initialState?._savedTotalExVat!=null&&Number.isFinite(savedTotal)){ out.finalPrice=savedTotal; out.pricePending=false; }
  const snap=initialState?.commercial?._discountSnapshot;
  if(snap){ out.discountAmount=snap.amount; out.subtotalExVat=snap.subtotalExVat; out.discountLabel=snap.label; out.discountReason=snap.reason; }
  out.grossProfit=out.finalPrice-out.totalCost;
  out.grossMargin=out.finalPrice>0?out.grossProfit/out.finalPrice*100:0;
  return out;
}
const IND_SAVED_KEYS={labourCost:"onSiteLabourCost",travelTimeCost:"travelTimeCost",fuelCost:"fuelCost",electricityCost:"electricityCost",dryIceCost:"diCost",travelCost:"travelCost",accomCost:"accomCost",subCost:"subCost",equipmentRecoveryCost:"recoveryCost",totalCost:"totalCost",basePrice:"basePrice",riskPct:"riskPct",afterRisk:"afterRisk",afterCont:"afterCont",dryIceKgRequired:"totalDI",dryIceKgAdjusted:"adjDI"};
const AUTO_SAVED_KEYS={customLabour:"customLabourLines",labourCost:"onSiteLabourCost",travelTimeCost:"travelTimeCost",fuelCost:"fuelCost",electricityCost:"electricityCost",dryIceCost:"dryIceCost",materialsCost:"materialsCost",conciergeCost:"conciergeFlat",detailingCost:"detailingCost",travelCost:"travelCost",equipmentRecoveryCost:"machineRecovery",totalCost:"totalCost",basePrice:"basePrice",afterCont:"afterCont",dryIceKgRequired:"dryIceKg",dryIceKgAdjusted:"adjDryIce"};

// Each section is a card: surface, 1px line, 10px radius, an eyebrow label
// with its number. Click the header to fold it.
function Section({num,title,children,defaultOpen=true}){
  const[openState,setOpen]=useState(defaultOpen);
  const readOnly=React.useContext(ReadOnlyCtx);
  const open=openState||readOnly;
  return(
    <div style={{marginBottom:18,background:t.surface,border:`1px solid ${t.border}`,borderRadius:10,padding:"18px 20px 20px"}}>
      <button type="button" onClick={()=>setOpen(o=>!o)} onKeyDown={e=>{if(e.key===" ")e.preventDefault();}} aria-expanded={open} style={{width:"100%",background:"none",border:"none",cursor:"pointer",padding:0,display:"flex",alignItems:"baseline",gap:10,marginBottom:open?16:0,fontFamily:fonts.body}}>
        <span style={{...type.statValue,fontSize:12,color:t.textMuted}}>{String(num).padStart(2,"0")}</span>
        <Eyebrow style={{flex:1,textAlign:"left"}}>{title}</Eyebrow>
        <span style={{fontSize:12,color:t.textMuted}}>{open?"▲":"▼"}</span>
      </button>
      {open&&<div>{children}</div>}
    </div>
  );
}

function MarginSlider({value,onChange,basePrice,totalCost,jobLocation}){
  const color=marginColorFor(value,jobLocation);
  return(
    <div style={{background:t.bg,border:`1px solid ${t.border}`,borderRadius:10,padding:16,marginBottom:14}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:10}}>
        <span style={{...type.statLabel,color:t.textMuted}}>Target gross profit margin</span>
        <span style={{fontFamily:fonts.display,fontSize:34,fontWeight:700,lineHeight:1,color,fontVariantNumeric:"tabular-nums"}}>{value}%</span>
      </div>
      <input type="range" min={10} max={90} step={1} value={value} onChange={e=>onChange(+e.target.value)} style={{width:"100%",accentColor:color,height:6,cursor:"pointer",marginBottom:10}}/>
      <div style={{display:"flex",gap:6,marginBottom:14}}>
        {[30,40,50,60,70,80].map(v=>(
          <button type="button" key={v} onClick={()=>onChange(v)} style={{flex:1,background:value===v?color+"26":"transparent",border:`1px solid ${value===v?color:t.border}`,borderRadius:999,padding:"4px 0",...type.statValue,fontSize:11.5,color:value===v?color:t.textMuted,cursor:"pointer"}}>{v}%</button>
        ))}
      </div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:8,padding:"10px 12px",background:t.surface,borderRadius:8,border:`1px solid ${t.border}`}}>
        <span style={{fontSize:12.5,color:t.textMuted}}>Cost <span style={type.statValue}>{fmt(totalCost)}</span> → prices at</span>
        <span style={{...type.statValue,fontSize:15,color:t.text}}>{fmt(basePrice)}</span>
      </div>
    </div>
  );
}


// ─── SUMMARY PANEL (top-level component, receives props) ──────────────────────
// Price panel, both calculators: the final price ex VAT is the key figure
// (accent-ringed card), margin and profit under it. VAT is added once, on
// the client quote.
function PriceHero({pricePending,finalPrice,grossMargin,grossProfit,marginColor,pendingText}){
  return(
    <div style={{background:t.surface,border:`1px solid ${t.accent}`,boxShadow:`0 0 0 1px ${t.accent}`,borderRadius:10,padding:"18px 20px",marginBottom:12}}>
      <Eyebrow style={{marginBottom:8}}>Final price · ex VAT</Eyebrow>
      {pricePending
        ? <div style={{fontSize:15,fontWeight:700,color:t.warning,padding:"6px 0"}}>{pendingText}</div>
        : <div style={{...type.keyFigure,fontSize:"2.9rem",color:t.text,fontVariantNumeric:"tabular-nums"}}>{fmt(finalPrice)}</div>}
      <div style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap",marginTop:12}}>
        <span style={{...type.pill,background:marginColor+"22",color:marginColor}}>{fmtN(grossMargin)}% margin</span>
        <span style={{fontSize:13,color:t.textMuted}}>Profit <span style={{...type.statValue,color:t.text}}>{fmt(grossProfit)}</span></span>
      </div>
      <div style={{fontSize:12,color:t.textMuted,marginTop:10}}>VAT is added once, on the client quote.</div>
    </div>
  );
}
// Supporting breakdown: small muted labels, mono values. Row: [label, value,
// highlight, "warn" (a total line in amber)].
function SummaryBlocks({blocks}){
  return blocks.map(block=>(
    <div key={block.title} style={{background:t.surface,border:`1px solid ${t.border}`,borderRadius:10,padding:"14px 16px",marginBottom:10}}>
      <Eyebrow style={{color:t.textMuted,fontSize:"0.7rem",marginBottom:8}}>{block.title}</Eyebrow>
      {block.rows.map(([l,v,hl,warn])=>(
        <div key={l} style={{display:"flex",justifyContent:"space-between",gap:10,padding:"3px 0",borderTop:warn==="warn"||hl?`1px solid ${t.border}`:"none",marginTop:warn==="warn"||hl?6:0,paddingTop:warn==="warn"||hl?8:3}}>
          <span style={{fontSize:13,color:hl?t.text:t.textMuted,fontWeight:hl?600:400,minWidth:0}}>{l}</span>
          <span style={{...type.statValue,fontSize:13,color:hl?(warn==="warn"?t.warning:t.text):t.text,whiteSpace:"nowrap"}}>{v}</span>
        </div>
      ))}
    </div>
  ));
}

function SummaryPanel({calc}){
  const{pricePending,finalPrice,grossMargin,grossProfit,workHrs,setupHrs,packHrs,ohHrs,totalHrs,travelTimeHrs,totalChargeableHrs,onSiteLabourCost,travelTimeCost,totalDI,adjDI,totalFuel,labourCost,fuelCost,electricityCost,inUnit,diCost,travelCost,accomCost,subCost,recoveryCost,totalCost,basePrice,afterRisk,afterCont,riskPct,commercial,discountAmount,discountLabel,discountReason}=calc;
  const mc=marginColorFor(grossMargin,inUnit?"in-unit":"on-site");
  return(
    <div>
      <PriceHero pricePending={pricePending} finalPrice={finalPrice} grossMargin={grossMargin} grossProfit={grossProfit} marginColor={mc} pendingText="Pick a contamination type to price this quote"/>
      <SummaryBlocks blocks={[
        {title:"Time Summary",rows:[["Production",`${fmtN(workHrs)} hrs`],["Setup + Pack-down",`${fmtN(setupHrs+packHrs)} hrs`],["Overhead",`${fmtN(ohHrs)} hrs`],...(travelTimeHrs>0?[["Travel Time",`${fmtN(travelTimeHrs)} hrs`]]:[]),["Total Chargeable",`${fmtN(totalChargeableHrs)} hrs`,true]]},
        {title:"Resources",rows:[["Dry Ice (raw)",`${fmtN(totalDI)} kg`],["Dry Ice (adj.)",`${fmtN(adjDI)} kg`],...(inUnit?[]:[["Fuel Required",`${fmtN(totalFuel)} L`]])]},
        {title:"Cost Breakdown",rows:[["Labour",fmt(onSiteLabourCost)],...(travelTimeCost>0?[["Travel Time (labour)",fmt(travelTimeCost)]]:[]),inUnit?["Electricity (mains)",fmt(electricityCost)]:["Fuel",fmt(fuelCost)],["Dry Ice",fmt(diCost)],["Travel (fuel/vehicle)",fmt(travelCost)],...(accomCost>0?[["Accommodation",fmt(accomCost)]]:[]),...(subCost>0?[["Subsistence",fmt(subCost)]]:[]),["Equipment Recovery",fmt(recoveryCost)],["Total Direct Cost",fmt(totalCost),true,"warn"]]},
        {title:"Price Build-Up",rows:[["Base Price",fmt(basePrice)],[`Risk (+${fmtN(riskPct,0)}%)`,fmt(afterRisk)],[`Contingency (+${commercial.contingency}%)`,fmt(afterCont)],...(discountAmount>0?[[`Discount${discountReason?` (${discountReason})`:discountLabel?` (${discountLabel})`:""}`,`-${fmt(discountAmount)}`]]:[]),["Final Price",fmt(finalPrice),true]]},
      ]}/>
    </div>
  );
}


// ─── POSTCODE LOOKUP (mileage calculator, both calculators) ──────────────────
// Spaces and case don't matter ("bs16aa" = "BS1 6AA"). A postcode that
// isn't found says which one and why, e.g. one that has been terminated.
const UK_POSTCODE=/^[A-Z]{1,2}\d[A-Z\d]?\d[A-Z]{2}$/;
const compactPostcode=pc=>String(pc||"").toUpperCase().replace(/[^A-Z0-9]/g,"");
const formatPostcode=pc=>{const c=compactPostcode(pc);return UK_POSTCODE.test(c)?`${c.slice(0,-3)} ${c.slice(-3)}`:String(pc||"").trim();};
async function lookupPostcode(pc,which){
  const c=compactPostcode(pc);
  if(!UK_POSTCODE.test(c)) return{error:`${which} postcode "${String(pc).trim()}" isn't a UK postcode format.`};
  const r=await fetch(`https://api.postcodes.io/postcodes/${c}`).then(x=>x.json());
  if(r.status===200) return{result:r.result};
  let closed=null;
  try{ const tr=await fetch(`https://api.postcodes.io/terminated_postcodes/${c}`).then(x=>x.json()); if(tr.status===200) closed=tr.result; }catch(e){ /* only for the message */ }
  return{error:closed?`${which} postcode ${formatPostcode(c)} was closed in ${closed.year_terminated}, so it can't be looked up. Use a current postcode nearby.`:`${which} postcode ${formatPostcode(c)} wasn't found. Check it and try again.`};
}

// Manual miles (one way), the mileage calculator's override: optional, and
// when set it replaces the postcode lookup. The return journey toggle still
// doubles it. Saved as manualMiles next to travelMiles (the total that
// prices travel), so pricing itself is unchanged.
const milesTotal=(oneWay,returnTrip)=>oneWay?(returnTrip?oneWay*2:oneWay):0;
const manualMilesSetOf=v=>v!=null&&v!==""&&Number(v)>=0;
// Saved quote with manual miles: was the return journey on? (total = one way: no)
const savedReturnTrip=blob=>!(manualMilesSetOf(blob?.manualMiles)&&Number(blob.manualMiles)>0&&Number(blob.travelMiles)===Number(blob.manualMiles));
function ManualMilesField({manual,onChange,lookupMiles}){
  return(
    <div style={{marginTop:12,paddingTop:12,borderTop:`1px dashed ${t.border}`}}>
      <Field label="Manual Miles Override, one way (optional)">
        <NumInput style={inp} step="1" min="0" placeholder={lookupMiles?`Calculated: ${lookupMiles} miles`:"e.g. 42"} value={manual??""} onChange={e=>onChange(e.target.value!==""&&+e.target.value>=0?+e.target.value:null)} unit="miles"/>
      </Field>
      <div style={{fontSize:13,lineHeight:1.45,color:t.textMuted,marginTop:6}}>Use when the distance is already known. Replaces the postcode lookup; Include return journey still doubles it. Clear it to use the lookup again.</div>
    </div>
  );
}

// ─── QUANTITY HERO (Industrial section 3) ────────────────────────────────────
// m² is the main price driver, so it leads the section: a big number with
// ±5 steppers, an optional Measure helper (areas as length × width, total
// feeds the quantity, saved as profile.areas) and the chain from quantity to
// price. Display only: every figure comes from calcIndustrial.
const QTY_STEP=5;
const round1=n=>Math.round(n*10)/10;
const areaOf=a=>(parseFloat(a?.length)||0)*(parseFloat(a?.width)||0);
const areasTotal=areas=>round1((areas||[]).reduce((n,a)=>n+areaOf(a),0));
function QuantityHero({quantity,unit,onQuantity,areas,onAreas,chain,hint}){
  const qty=parseFloat(quantity)||0;
  const step=d=>onQuantity(String(Math.max(0,round1(qty+d))));
  const total=areasTotal(areas);
  const hasAreas=(areas||[]).length>0;
  const mismatch=hasAreas&&total>0&&Math.abs(total-qty)>=0.05;
  const stepBtn={...secondaryBtn,width:44,height:44,flexShrink:0,fontSize:22,lineHeight:1,padding:0,display:"flex",alignItems:"center",justifyContent:"center"};
  const small={...inp,padding:"8px 10px",fontSize:14};
  const setArea=(i,patch)=>onAreas(areas.map((a,j)=>j===i?{...a,...patch}:a));
  // "✓ Add" confirms one area row on the spot (folds it to a summary line and
  // closes the phone keyboard), so nobody reaches for the quote's Save to do
  // it. On screen only: rows already measured start folded.
  const[added,setAdded]=useState(()=>new Set((areas||[]).map((a,i)=>areaOf(a)>0?i:-1).filter(i=>i>=0)));
  const addArea=i=>{if(document.activeElement)document.activeElement.blur();setAdded(s=>new Set([...s,i]));};
  const editArea=i=>setAdded(s=>{const n=new Set(s);n.delete(i);return n;});
  const removeArea=i=>{setAdded(s=>new Set([...s].filter(j=>j!==i).map(j=>j>i?j-1:j)));onAreas(areas.filter((_,j)=>j!==i));};
  return(
    <div style={{marginBottom:18}}>
      <div style={{background:t.bg,border:`1px solid ${t.accent}`,boxShadow:`0 0 0 1px ${t.accent}`,borderRadius:10,padding:"16px 18px"}}>
        <Eyebrow style={{marginBottom:10}}>Quantity to clean</Eyebrow>
        {/* One line at any width: on phones (see .sp-qty-row) the buttons and
            number shrink a little and the unit sits over its badge. */}
        <div className="sp-qty-row" style={{display:"flex",alignItems:"center",gap:10,flexWrap:"nowrap",minWidth:0}}>
          <button type="button" className="sp-qty-step" aria-label={`Take ${QTY_STEP} ${unit} off`} onClick={()=>step(-QTY_STEP)} style={stepBtn}>−</button>
          <NumInput inputMode="decimal" min="0" aria-label="Quantity to clean" value={quantity} onChange={e=>onQuantity(e.target.value)} placeholder="0" className="sp-qty-input"
            style={{...type.keyFigure,width:"6.2ch",minWidth:0,flexShrink:1,background:"transparent",border:"none",borderBottom:`2px solid ${qty>0?t.border:t.warning}`,borderRadius:0,outline:"none",color:t.text,WebkitTextFillColor:t.text,padding:"2px 0",fontVariantNumeric:"tabular-nums",boxShadow:"none"}}/>
          <span className="sp-qty-unit" style={{display:"inline-flex",alignItems:"center",gap:10,flexShrink:0}}>
            <span className="sp-qty-unit-text" style={{fontFamily:fonts.display,fontSize:"1.6rem",fontWeight:600,color:t.textMuted,lineHeight:1}}>{unit}</span>
            <span style={{...type.pill,fontSize:"0.62rem",padding:"2px 8px",background:t.accentDim,color:t.accent,whiteSpace:"nowrap"}}>Locked unit</span>
          </span>
          <button type="button" className="sp-qty-step" aria-label={`Add ${QTY_STEP} ${unit}`} onClick={()=>step(QTY_STEP)} style={stepBtn}>+</button>
        </div>
        <div style={{marginTop:12,fontSize:14,color:t.text,lineHeight:1.5}}>
          {chain.manual
            ? <span style={{color:t.warning}}>Production hours are set manually ({fmtN(chain.hrs)} hrs), so the quantity doesn't change the price. Clear it under Equipment &amp; Consumables → Advanced Overrides.</span>
            : qty>0
              ? <span style={{...type.statValue,fontSize:14}}>{fmtN(qty)} {unit} <span style={{color:t.textMuted}}>→</span> {fmtN(chain.hrs)} hrs <span style={{color:t.textMuted}}>→</span> {fmtN(chain.kg)} kg dry ice <span style={{color:t.textMuted}}>→</span> {chain.pending?<span style={{color:t.warning,fontFamily:fonts.body}}>pick a contamination type to price</span>:<strong style={{color:t.accent,fontWeight:500}}>{fmt(chain.price)} ex VAT</strong>}</span>
              : <span style={{color:t.textMuted}}>Enter the area to clean, or measure it below, to see hours, dry ice and price.</span>}
        </div>
        {hint&&<div style={{marginTop:4,fontSize:13,color:t.textMuted}}>+10 {unit} ≈ <span style={{...type.statValue,color:t.text}}>+{fmt(hint.delta)}</span> ex VAT{hint.delta<0.005&&" (price held by rounding, minimum price or a target price)"}</div>}
      </div>
      <div style={{marginTop:10}}>
        {!hasAreas
          ? <button type="button" onClick={()=>onAreas([{label:"",length:"",width:""}])} style={{...secondaryBtn,padding:"8px 14px",fontSize:13}}>Measure: add areas (length × width)</button>
          : (
            <div style={{border:`1px solid ${t.border}`,borderRadius:10,padding:"12px 14px"}}>
              <Eyebrow style={{color:t.textMuted,fontSize:"0.7rem",marginBottom:10}}>Measure (metres)</Eyebrow>
              {areas.map((a,i)=>added.has(i)&&areaOf(a)>0?(
                <div key={i} style={{display:"flex",alignItems:"center",gap:8,marginBottom:8,padding:"8px 10px",background:t.bg,border:`1px solid ${t.border}`,borderRadius:8,fontSize:14,color:t.text}}>
                  <span style={{color:t.success,fontWeight:700}} aria-hidden="true">✓</span>
                  <span style={{flex:1,minWidth:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{a.label||`Area ${i+1}`} <span style={{color:t.textMuted}}>· {fmtN(parseFloat(a.length)||0)} × {fmtN(parseFloat(a.width)||0)} m</span></span>
                  <span style={{...type.statValue,fontSize:13,whiteSpace:"nowrap"}}>= {fmtN(areaOf(a))} {unit}</span>
                  <button type="button" onClick={()=>editArea(i)} aria-label={`Edit area ${i+1}`} style={{background:"none",border:"none",color:t.accent,cursor:"pointer",fontSize:13,fontWeight:600,padding:"4px 6px",fontFamily:fonts.body}}>Edit</button>
                  <button type="button" onClick={()=>removeArea(i)} aria-label={`Remove area ${i+1}`} title="Remove" style={{background:"none",border:"none",color:t.textMuted,cursor:"pointer",fontSize:15,padding:"4px 6px"}}>✕</button>
                </div>
              ):(
                <div key={i} className="sp-area-row" style={{display:"grid",gridTemplateColumns:"minmax(0,1.6fr) minmax(0,0.8fr) auto minmax(0,0.8fr) auto auto auto",gap:8,alignItems:"center",marginBottom:8}}>
                  <input className="sp-area-label" style={small} value={a.label||""} onChange={e=>setArea(i,{label:e.target.value})} placeholder={`Area ${i+1}, e.g. Engine room`} aria-label={`Area ${i+1} name`}/>
                  <NumInput style={small} inputMode="decimal" min="0" value={a.length??""} onChange={e=>setArea(i,{length:e.target.value})} placeholder="Length" aria-label={`Area ${i+1} length (m)`} unit="m"/>
                  <span style={{color:t.textMuted}}>×</span>
                  <NumInput style={small} inputMode="decimal" min="0" value={a.width??""} onChange={e=>setArea(i,{width:e.target.value})} placeholder="Width" aria-label={`Area ${i+1} width (m)`} unit="m"/>
                  <span style={{...type.statValue,fontSize:13,color:t.text,whiteSpace:"nowrap",minWidth:"6.5ch",textAlign:"right"}}>= {fmtN(areaOf(a))} {unit}</span>
                  <button type="button" className="sp-area-add" onClick={()=>addArea(i)} disabled={!(areaOf(a)>0)} aria-label={`Add area ${i+1}`} style={{...secondaryBtn,padding:"7px 12px",fontSize:13,whiteSpace:"nowrap",opacity:areaOf(a)>0?1:0.45}}>✓ Add</button>
                  <button type="button" onClick={()=>removeArea(i)} aria-label={`Remove area ${i+1}`} title="Remove" style={{background:"none",border:"none",color:t.textMuted,cursor:"pointer",fontSize:15,padding:"4px 6px"}}>✕</button>
                </div>
              ))}
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:10,flexWrap:"wrap",marginTop:4}}>
                <button type="button" onClick={()=>onAreas([...areas,{label:"",length:"",width:""}])} style={{...secondaryBtn,padding:"6px 12px",fontSize:13}}>+ Add area</button>
                <span style={{fontSize:13,color:t.textMuted}}>Total <span style={{...type.statValue,color:t.text}}>{fmtN(total)} {unit}</span></span>
              </div>
              {mismatch&&(
                <div style={{marginTop:10,fontSize:13,color:t.warning}}>
                  Areas total {fmtN(total)} {unit} · quantity {fmtN(qty)} {unit}.{" "}
                  <button type="button" onClick={()=>onQuantity(String(total))} style={{background:"none",border:"none",padding:0,color:t.accent,fontWeight:600,cursor:"pointer",textDecoration:"underline",fontSize:13,fontFamily:fonts.body}}>Use areas total</button>
                </div>
              )}
            </div>
          )}
      </div>
    </div>
  );
}

// Scope templates live in scopeTemplates.js (shared with the quote renderers).

// Industrial pricing: every figure the calculator shows and saves, from its
// inputs. Moved here unchanged from IndustrialCalculator so the quantity
// hint ("+10 m²") can price the same quote again with a different quantity.
function calcIndustrial({profile,access,equip,deploy,recovery,commercial,overrides,job,diSupplier,diSaved,calcMiles,returnTrip,mileRate,avgSpeed}){
  const basePR=overrides.productionRate||(PRODUCTION_RATE_PRESETS[profile.contaminationType]?.[profile.severity]??6);
  const prodMult=(ACCESS_IMPACT[access.access]??1)*(VERTICAL_IMPACT[access.vertical]??1)*(ENVIRON_IMPACT[access.environment]??1);
  const adjPR=basePR*prodMult;
  const manualHoursSet=overrides.manualHours!=null&&overrides.manualHours>=0;
  const calculatedWorkHrs=profile.quantity&&adjPR>0?parseFloat(profile.quantity)/adjPR:0;
  const workHrs=manualHoursSet?overrides.manualHours:calculatedWorkHrs;
  const hasJob=parseFloat(profile.quantity||0)>0||(manualHoursSet&&overrides.manualHours>0);
  const setupHrs=hasJob?(SETUP_OVERHEAD[access.setupComplexity]??1):0;
  const ohHrs=hasJob?((HOURS_RESTRICT[access.hoursRestriction]??0)+(WEATHER_OH[access.weather]??0)):0;
  const packHrs=hasJob?0.5:0;
  const totalHrs=workHrs+setupHrs+packHrs+ohHrs;
  const diBase=DRY_ICE_PRESETS[profile.contaminationType]?.[profile.severity]??12;
  const diSensMult=SENSITIVITY_MULT[profile.sensitivity]??1.0;
  const diMachineMult=MACHINE_DI_MULT[equip.machineType]??1.0;
  const diClamp=MACHINE_DI_CLAMP[equip.machineType]??{min:1,max:100};
  const diRawHr=diBase*diSensMult*diMachineMult;
  const dryIceHr=overrides.dryIcePerHr??Math.min(diClamp.max,Math.max(diClamp.min,diRawHr));
  // Dry ice is only consumed while the gun is actually firing — active
  // production time (workHrs), not setup/pack-down/overhead time.
  const totalDI=workHrs*dryIceHr;
  const adjDI=totalDI*(1+equip.wastage/100);
  const diPricePerKg=Number(diSupplier?.currentCostPricePerKg)||0;
  const diDeliveryPerBin=Number(diSupplier?.deliverySurchargePerBin)||0;
  const diBinRentalPerBin=Number(diSupplier?.binRentalPerBin)||0;
  const diBinOptions=diSupplier?.binCapacities||[];
  const diSelectedBin=diBinOptions.find(b=>b.label===equip.binCapacityLabel)||diBinOptions[0]||null;
  const binSpec={capacity:diSelectedBin?.capacity_kg||0,hire:diBinRentalPerBin};
  // £/kg + delivery and hire per bin, or a delivered-tier supplier's tiers.
  const diCharge=dryIceCharge(diSupplier,adjDI,binSpec.capacity,diSaved,`${equip.dryIceSupplierId||""}|${equip.binCapacityLabel||""}`);
  const binsNeeded=diCharge.binsNeeded;
  const binHire=diCharge.binHire;
  const diCost=diCharge.cost;
  const inUnit=job.jobLocation==="in-unit";
  const fuelHr=overrides.fuelBurn||(equip.baseFuelBurn*(FUEL_INTENSITY[equip.intensity]??1));
  const totalFuel=inUnit?0:totalHrs*fuelHr;
  const fuelCost=totalFuel*equip.dieselPrice;
  const totalCrew=(deploy.vehicles||[{crew:2}]).reduce((s,v)=>s+(v.crew||1),0);
  const totalMiles=calcMiles?(returnTrip?calcMiles*2:calcMiles):parseFloat(deploy.travelMiles)||0;
  // Travel time is billed as labour (crew get paid for the drive), separate
  // from the £/mile travelCost below which only covers fuel/vehicle running
  // costs. It's derived from the same totalMiles used for mileage, at an
  // assumed average road speed — it does NOT feed dry ice/fuel/equipment
  // hours, since none of that runs while the vehicle is in transit.
  const travelTimeHrs=totalMiles>0&&avgSpeed>0?totalMiles/avgSpeed:0;
  const totalChargeableHrs=totalHrs+travelTimeHrs;
  const onSiteLabourCost=totalHrs*equip.labourRatePerHour*totalCrew;
  const travelTimeCost=travelTimeHrs*equip.labourRatePerHour*totalCrew;
  const labourCost=onSiteLabourCost+travelTimeCost;
  const hasCompressor=(deploy.vehicles||[]).some(v=>v.compressor);
  const compressorTowUplift=hasCompressor?1.20:1.0;
  const travelCost=totalMiles*mileRate*((deploy.vehicles||[]).length||1)*compressorTowUplift;
  const accomCost=deploy.overnight?deploy.accommodationPerNight*totalCrew*deploy.nights:0;
  const subCost=deploy.overnight?deploy.subsistencePerPersonPerDay*totalCrew*deploy.nights:0;
  const numVehicles=(deploy.vehicles||[]).length||1;
  const numCompressors=(deploy.vehicles||[]).filter(v=>v.compressor).length;
  const recoveryCost=(recovery.machinePerHr+recovery.compressorPerHr*numCompressors+recovery.vehiclePerHr*numVehicles)*totalHrs;
  // Per compressor, like equipment recovery - but at least one, since an
  // in-unit job still runs a compressor even with none set to be towed.
  const electricityPerHr=equip.electricityPerHr??DEFAULT_ELECTRICITY_PER_HR;
  const electricityCost=inUnit?totalHrs*electricityPerHr*Math.max(1,numCompressors):0;
  const totalCost=labourCost+fuelCost+electricityCost+diCost+travelCost+accomCost+subCost+recoveryCost;
  const riskPct=(RISK_UPLIFT.access[access.access]??0)+(RISK_UPLIFT.environment[access.environment]??0)+(RISK_UPLIFT.severity[profile.severity]??0);
  const m=commercial.targetMargin/100;
  const basePrice=m<1?totalCost/(1-m):totalCost;
  const afterRisk=basePrice*(1+riskPct/100);
  const afterCont=afterRisk*(1+commercial.contingency/100);
  let finalPrice=afterCont;
  if(commercial.roundPrice==="nearest £50")finalPrice=Math.ceil(finalPrice/50)*50;
  if(commercial.roundPrice==="nearest £100")finalPrice=Math.ceil(finalPrice/100)*100;
  if(commercial.minJobPrice>0)finalPrice=Math.max(finalPrice,commercial.minJobPrice);
  const subtotalExVat=finalPrice;
  const{finalPrice:discountedPrice,discountAmount,discountLabel,discountReason}=applyDiscount(subtotalExVat,commercial);
  finalPrice=discountedPrice;
  const grossProfit=finalPrice-totalCost;
  const grossMargin=finalPrice>0?(grossProfit/finalPrice*100):0;
  return{basePR,prodMult,adjPR,manualHoursSet,calculatedWorkHrs,workHrs,hasJob,setupHrs,ohHrs,packHrs,totalHrs,diBase,diSensMult,diMachineMult,diClamp,diRawHr,dryIceHr,totalDI,adjDI,diPricePerKg,diDeliveryPerBin,diBinRentalPerBin,diBinOptions,diSelectedBin,binSpec,binsNeeded,binHire,diCost,diCharge,inUnit,fuelHr,totalFuel,fuelCost,totalCrew,totalMiles,travelTimeHrs,totalChargeableHrs,onSiteLabourCost,travelTimeCost,labourCost,hasCompressor,compressorTowUplift,travelCost,accomCost,subCost,numVehicles,numCompressors,recoveryCost,electricityPerHr,electricityCost,totalCost,riskPct,m,basePrice,afterRisk,afterCont,finalPrice,subtotalExVat,grossProfit,grossMargin,discountAmount,discountLabel,discountReason};
}

// ─── INDUSTRIAL CALCULATOR ────────────────────────────────────────────────────
function IndustrialCalculator({ onSaveToLog, onQuoteSaved, initialState, amendmentMeta, onAmendmentConsumed, onNew, onDraftChange, readOnly }){
  const[isMobile]=useState(()=>window.innerWidth<768);
  // Lazy initializer: getNextQuoteRef() increments and persists a counter as
  // a side effect, so it must only ever run once, on mount - a plain
  // (non-lazy) useState argument gets re-evaluated on every re-render (every
  // keystroke), silently burning counter values that are never actually used
  // as the real quoteRef. That's what caused SBL numbers to skip.
  // Estimator defaults to whoever is signed in (new quotes, and reopened
  // quotes saved without one), so it's never left blank on the quote.
  const{user:signedInUser}=useUser();
  const[job,setJob]=useState(()=>readOnly&&initialState?.job?initialState.job:withDefaultEstimator(initialState?.job || {quoteRef:getNextQuoteRef('SBL'),clientName:"",clientEmail:"",clientPhone:"",clientPOC:"",clientAddress:"",siteName:"",postcode:"",jobDate:"",estimator:"",workType:"One-Off Project",jobLocation:"on-site"},signedInUser));
  const[service,setService]=useState(initialState?.service || {category:"industrial",subService:""});
  const[profile,setProfile]=useState(initialState?.profile || {contaminationType:"",severity:"Moderate",surfaceType:"Mixed Surfaces",sensitivity:"Medium",quantity:"",quantityUnit:"m²"});
  const[access,setAccess]=useState(initialState?.access || {access:"Easy (vehicle adjacent)",vertical:"Ground Level",hoursRestriction:"None",environment:"Open / External",setupComplexity:"Standard",weather:"None"});
  const[deploy,setDeploy]=useState(initialState?.deploy || {vehicles:[{crew:2,compressor:true}],travelMiles:"",overnight:false,nights:1,accommodationPerNight:90,subsistencePerPersonPerDay:35});
  const[equip,setEquip]=useState(initialState?.equip || {...loadPersistedDefaults('sublime_equip_defaults', {machineType:"Champ Ultra",intensity:"Standard",baseFuelBurn:10.0,dieselPrice:1.90,electricityPerHr:DEFAULT_ELECTRICITY_PER_HR,dryIceSupplierId:null,wastage:10,binCapacityLabel:null,labourRatePerHour:35}),dryIceSupplierId:null});
  // The supplier pick is this quote's only: new quotes start on the Primary.
  React.useEffect(()=>{ if(readOnly) return; persistDefaults('sublime_equip_defaults', {...equip,dryIceSupplierId:null}); },[equip]);
  const[recovery,setRec]=useState(initialState?.recovery || loadPersistedDefaults('sublime_recovery_defaults', {machinePerHr:1.70,compressorPerHr:2.80,vehiclePerHr:3.70}));
  React.useEffect(()=>{ if(readOnly) return; persistDefaults('sublime_recovery_defaults', recovery); },[recovery]);
  const[commercial,setCom]=useState(()=>restoreCommercial(initialState?.commercial,{targetMargin:defaultMarginFor("on-site"),marginSet:false,contingency:5,minJobPrice:0,roundPrice:"nearest £50",discountType:"none",discountValue:0,discountTargetPrice:null,discountReason:""}));
  const[notes,setNotes]=useState(()=>initialNotes(initialState));
  const[overrides,setOv]=useState(initialState?.overrides || {productionRate:null,dryIcePerHr:null,fuelBurn:null,manualHours:null});
  const[advMode,setAdvMode]=useState(false);
  // Mobile: the sticky bar's Preview button scrolls down to the live quote.
  const previewRef=useRef(null);
  const goToPreview=()=>previewRef.current?.scrollIntoView({behavior:"smooth",block:"start"});
  const[fromPC,setFromPC]=useState("TR27 4QZ");
  const[toPC,setToPC]=useState("");
  React.useEffect(()=>{ if(job.postcode){ setToPC(job.postcode); } else { setToPC(""); } },[job.postcode]);
  const[calcMiles,setCalcMiles]=useState(null);
  const[returnTrip,setReturnTrip]=useState(()=>savedReturnTrip(initialState?.deploy));
  const[mileLoading,setMileLoading]=useState(false);
  const[mileError,setMileError]=useState("");
  // Rate per mile and average travel speed price the travel, so they're saved
  // with the quote (mileRate / avgSpeed) and a reopened or amended quote uses
  // its own. New quotes, and quotes saved before 20 Oct 2026, start from this
  // browser's remembered values (as before).
  const remembered=(key,fallback)=>{ try{ const v=localStorage.getItem(key); return v!=null&&v!==""&&!isNaN(parseFloat(v))?parseFloat(v):fallback; }catch(e){ return fallback; } };
  const[mileRate,setMileRate]=useState(()=>Number.isFinite(initialState?.mileRate)?initialState.mileRate:remembered('sublime_mile_rate',0.45));
  const[avgSpeed,setAvgSpeed]=useState(()=>Number.isFinite(initialState?.avgSpeed)?initialState.avgSpeed:remembered('sublime_avg_speed',40));
  // Remembered only when changed here, so opening a quote doesn't overwrite
  // this browser's values with that quote's.
  const openedTravel=useRef({mileRate,avgSpeed});
  React.useEffect(()=>{ if(readOnly||mileRate===openedTravel.current.mileRate) return; try{ localStorage.setItem('sublime_mile_rate', String(mileRate)); }catch(e){} },[mileRate]); // eslint-disable-line react-hooks/exhaustive-deps
  React.useEffect(()=>{ if(readOnly||avgSpeed===openedTravel.current.avgSpeed) return; try{ localStorage.setItem('sublime_avg_speed', String(avgSpeed)); }catch(e){} },[avgSpeed]); // eslint-disable-line react-hooks/exhaustive-deps
  const[pdfLoading,setPdfLoading]=useState(false);
  // True while Print/Save PDF is saving; Save to Log waits for it.
  const saveBusy=useRef(false);
  // Save ID for the PDF button's save, kept until it succeeds so a retry
  // can't add a second row (see writeWithSaveCheck in quoteLogger).
  const pdfSaveId=useRef(null);
  const[pdfSaveChecking,setPdfSaveChecking]=useState(false); // "Still saving…" banner for the PDF button's save
  const[savedNotice,setSavedNotice]=useState(null); // ref the PDF button just saved (SavedBanner)
  const[pdfAsk,setPdfAsk]=useState(false); // SavePdfDialog open (PDF on an unsaved quote)
  // Set when Download PDF saves this quote: its real number + what it looked
  // like, so the field can show the number and a repeat click on an
  // unchanged quote reprints it instead of saving another row.
  const[savedRef,setSavedRef]=useState(initialState?._savedRef||null);
  // Once this calculator has saved a quote, it keeps editing that quote:
  // any later save is an amendment of it (…a, …b), never a new number.
  const[savedAsBase,setSavedAsBase]=useState(initialState?._savedAsBase||null);
  // Set when this calculator was opened with "Copy as new quote". Part of the
  // calculator's state (from initialState._copyOf), so anything that restores
  // that state restores the copy too. Never saved to the sheet.
  const[copyOf]=useState(initialState?._copyOf||"");
  // Test quote flag (column Y): from an amended quote or a draft, and set by
  // the save dialog's tickbox.
  const[testQuote,setTestQuote]=useState(initialState?._testQuote??IS_PREVIEW_DEPLOYMENT);
  useAutoTestQuote(job.clientEmail,testQuote,setTestQuote);
  const amendOf=savedAsBase||amendmentMeta?.originalNumber||"";
  // Provisional number the next save will get (server confirms it).
  const amendNext=savedAsBase?nextAmendmentNumber(savedAsBase):job.quoteRef;
  const[showSaveModal,setShowSaveModal]=useState(false);

  // Scope follows the Application template unless it's locked as custom (see
  // CLIENT SCOPE LOCK). Runs on a new quote's first render and on each
  // Application change - not on reopening a saved quote, which keeps its scope.
  const scopeKey=`${service?.category} > ${service?.subService}`;
  const scopeTemplate=SCOPE_TEMPLATES[scopeKey]||"";
  const composedScope=composeScope({body:notes.scopeCustom?notes.scopeCustomText:scopeTemplate,extra:notes.scopeExtra});

  const calculateMileage=async()=>{
    if(!fromPC||!toPC)return;
    setMileLoading(true);setMileError("");
    try{
      setFromPC(formatPostcode(fromPC));setToPC(formatPostcode(toPC));
      const[a,b]=await Promise.all([lookupPostcode(fromPC,"From"),lookupPostcode(toPC,"To")]);
      if(a.result&&b.result){
        const{latitude:lat1,longitude:lon1}=a.result;const{latitude:lat2,longitude:lon2}=b.result;
        const R=3958.8,dLat=(lat2-lat1)*Math.PI/180,dLon=(lon2-lon1)*Math.PI/180;
        const x=Math.sin(dLat/2)**2+Math.cos(lat1*Math.PI/180)*Math.cos(lat2*Math.PI/180)*Math.sin(dLon/2)**2;
        const miles=Math.round(R*2*Math.atan2(Math.sqrt(x),Math.sqrt(1-x))*1.3);
        setCalcMiles(miles);setDeploy(d=>({...d,travelMiles:returnTrip?miles*2:miles}));
      }else{setMileError([a.error,b.error].filter(Boolean).join(" "));}
    }catch{setMileError("Could not calculate distance");}
    setMileLoading(false);
  };

  const{dryIceSuppliers,selectedSupplier:diSupplier,primary:diPrimary}=useDryIceSupplier(equip.dryIceSupplierId);
  const diSaved=React.useMemo(()=>savedDryIce(initialState,initialState?.equip),[initialState]);

  // Manual miles set: price from deploy.travelMiles (written from it), not the lookup.
  const manualMiles=manualMilesSetOf(deploy.manualMiles)?Number(deploy.manualMiles):null;
  const pricingInputs={profile,access,equip,deploy,recovery,commercial,overrides,job,diSupplier,diSaved,calcMiles:manualMiles!=null?null:calcMiles,returnTrip,mileRate,avgSpeed};
  // calculations (see calcIndustrial)
  const{basePR,prodMult,adjPR,manualHoursSet,calculatedWorkHrs,workHrs,setupHrs,ohHrs,packHrs,totalHrs,diBase,diSensMult,diMachineMult,dryIceHr,totalDI,adjDI,diPricePerKg,diDeliveryPerBin,diBinRentalPerBin,diBinOptions,diSelectedBin,binSpec,binsNeeded,binHire,diCost,diCharge,inUnit,fuelHr,totalFuel,fuelCost,totalCrew,totalMiles,travelTimeHrs,totalChargeableHrs,onSiteLabourCost,travelTimeCost,labourCost,travelCost,accomCost,subCost,numVehicles,numCompressors,recoveryCost,electricityPerHr,electricityCost,totalCost,riskPct,basePrice,afterRisk,afterCont,finalPrice,subtotalExVat,grossProfit,grossMargin,discountAmount,discountLabel,discountReason}=calcIndustrial(pricingInputs);
  // Section 3's "+10 m²" hint: the same quote priced again with 10 more
  // (only when the quantity drives the price).
  const qtyNum=parseFloat(profile.quantity)||0;
  const qtyHint=qtyNum>0&&!manualHoursSet&&profile.contaminationType
    ?{delta:calcIndustrial({...pricingInputs,profile:{...profile,quantity:String(qtyNum+10)}}).finalPrice-finalPrice}
    :null;
  // Measure helper: areas live on the profile (saved in State JSON) only
  // once used, and their total becomes the quantity.
  const setAreas=next=>setProfile(p=>{
    const{areas,...rest}=p;
    if(!next.length) return rest;
    const total=areasTotal(next);
    return{...rest,areas:next,...(total>0?{quantity:String(total)}:{})};
  });
  const marginColor=marginColorFor(grossMargin,job.jobLocation||"on-site");
  const catLabel=SERVICE_CATEGORIES.find(s=>s.value===service.category)?.label||"—";

  // A quote isn't priced until a contamination type is picked (new quotes
  // start blank). A saved quote keeps whatever it was saved with.
  const pricePending=!profile.contaminationType;
  const calc={pricePending,finalPrice,grossMargin,grossProfit,workHrs,setupHrs,packHrs,ohHrs,totalHrs,travelTimeHrs,totalChargeableHrs,onSiteLabourCost,travelTimeCost,totalDI,adjDI,totalFuel,labourCost,fuelCost,electricityCost,inUnit,diCost,travelCost,accomCost,subCost,recoveryCost,totalCost,basePrice,afterRisk,afterCont,riskPct,commercial,subtotalExVat,discountAmount,discountLabel,discountReason};
  // Read-only view: figures as saved (see asSaved).
  const view=readOnly?asSaved(calc,initialState,IND_SAVED_KEYS):calc;
  const viewMarginColor=readOnly?marginColorFor(view.grossMargin,job.jobLocation||"on-site"):marginColor;
  // Snapshot of the internal cost build-up, saved into stateJson alongside
  // the raw inputs so QuoteLog's detail panel can show it later without
  // re-running this component's pricing logic against possibly-changed
  // formulas — it reflects exactly what was charged at save time.
  const costBreakdown={labourCost:onSiteLabourCost,travelTimeCost,fuelCost,electricityCost,dryIceCost:diCost,travelCost,accomCost,subCost,equipmentRecoveryCost:recoveryCost,totalCost,basePrice,riskPct,afterRisk,contingencyPct:commercial.contingency,afterCont,dryIceKgRequired:totalDI,dryIceKgAdjusted:adjDI};

  // For "Target final price" discounts, the original subtotal can't be
  // reconstructed later from the saved final price alone (they'd be the same
  // number) — so when saving, snapshot the computed discount breakdown
  // alongside the input config, for the log/PDF-regeneration path to read
  // directly instead of re-deriving it.
  const commercialForSave=discountAmount>0?{...commercial,_discountSnapshot:{amount:discountAmount,subtotalExVat,label:discountLabel,reason:discountReason}}:commercial;
  // Everything a saved quote needs to be reopened and priced exactly the same
  // - the ONE definition every save path uses (PDF, both Save dialogs), so a
  // field can't be saved by one button and silently dropped by another.
  const stateForSave={job,service,profile,access,deploy,equip,recovery,commercial:commercialForSave,notes:{...notes,clientScope:composedScope},overrides,mileRate,avgSpeed,totalChargeableHrs,costBreakdown,...(amendmentMeta?.basedOn?{amendedFrom:amendmentMeta.basedOn}:{})};
  // Live client quote under the form. Shows the saved number only while the
  // calculator still matches what was saved.
  const currentSnap=quoteSnapshot(stateForSave);
  const savedAndUnchanged=matchesSaved(savedRef,currentSnap,finalPrice);
  // Opened via Amend: the quote as loaded counts as already saved, so saving
  // it without a change creates nothing. Taken once the mount effects have
  // settled; the total is the one on the sheet.
  const snapRef=useRef("");
  snapRef.current=currentSnap;
  React.useEffect(()=>{
    // Not when starting from an earlier version: restoring it IS a change.
    if(!amendmentMeta?.originalNumber||amendmentMeta?.basedOn||!initialState||initialState._fromDraft) return;
    const id=setTimeout(()=>setSavedRef({number:amendmentMeta.originalNumber,snap:snapRef.current,total:initialState._savedTotalExVat??NaN}),0);
    return ()=>clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[]);
  const flushDraft=useCalcAutosave("industrial",initialState,currentSnap,()=>({
    state:{...stateForSave,_savedTotalExVat:initialState?._savedTotalExVat,_testQuote:testQuote},
    amendOf:amendmentMeta?.originalNumber||"",basedOn:amendmentMeta?.basedOn||"",startsAsDraft:!!amendmentMeta?.startsAsDraft,savedRef,savedAsBase,copyOf,
    // For the Quote Log's drafts strip.
    dirty:!savedAndUnchanged,
    summary:[job.clientName||"No client yet",service.subService||(catLabel!=="—"?catLabel:"")].filter(Boolean).join(", ")+(amendOf?` (editing ${amendOf})`:copyOf?` (copy of ${copyOf})`:""),
  }),onDraftChange,!!readOnly);
  // Start new quote: write the draft first so the "unsaved work?" check is
  // current, then let the app ask and clear.
  const startNew=()=>{ flushDraft(); if(onNew) onNew(); };
  const quotePreview=<QuotePreview category={catLabel} state={stateForSave} totalExVat={view.finalPrice} quoteNumber={readOnly?readOnly.ref:(savedAndUnchanged?savedRef.number:"")} t={t}/>;
  // Save to Log / Save & Email: the calculator keeps what was entered, now
  // marked as saved, and the app opens the Quote Log on the new row.
  const markSaved=saved=>{
    setShowSaveModal(false);
    if(onAmendmentConsumed) onAmendmentConsumed();
    if(onSaveToLog) onSaveToLog();
    if(saved?.quoteNumber){ setSavedRef({number:saved.quoteNumber,snap:currentSnap,total:finalPrice}); setSavedAsBase(saved.quoteNumber); setJob(j=>({...j,quoteRef:saved.quoteNumber})); setTestQuote(!!saved.testQuote); }
    if(onQuoteSaved) onQuoteSaved(saved?.quoteNumber,saved?.sendNote,!!saved?.unchanged);
  };



  // Only the pricing input actually needed to produce a sane quote is
  // required - deliberately not Client Name, so a draft can be roughed out
  // on pricing before a client is attached.
  const requiredFieldsMissing=()=>{
    if(!profile.contaminationType){ alert("Pick a Contamination Type (Cleaning Profile) before generating a quote."); return true; }
    if(!profile.quantity){ alert("Add a Quantity to Clean before generating a quote."); return true; }
    return false;
  };

  // Shared by every "Save to Log" entry point (desktop output card, mobile
  // output card, mobile sticky bar) so the validation logic only exists in
  // one place.
  const handleOpenSaveModal=()=>{
    if(saveBusy.current)return;
    if(requiredFieldsMissing())return;
    setShowSaveModal(true);
  };

  const printJobSheet=()=>openJobSheet({
    heading:`${catLabel||"Industrial"} job${service.subService?`, ${service.subService}`:""}`,
    refNo:jobSheetRef(savedAndUnchanged?savedRef:null,amendOf),
    dateStr:quoteDateStr(null),
    sections:[
      {title:"Job",rows:[["Estimator",job.estimator],["Work type",job.workType],["Scheduled date",sheetDate(job.jobDate)],["Job location",inUnit?"In-unit (mains power)":"On-site (generator)"]]},
      {title:"Client & site",rows:[["Client",job.clientName],["Contact",job.clientPOC],["Phone",job.clientPhone],["Email",job.clientEmail],["Site",[job.siteName,job.postcode].filter(Boolean).join(", ")],["Address",job.clientAddress]]},
      {title:"Time",rows:[["Production",hrs(workHrs)],["Setup + pack-down",hrs(setupHrs+packHrs)],["Overhead",hrs(ohHrs)],["Total on site",hrs(totalHrs)]]},
      {title:"Crew & kit",rows:[["Operatives",totalCrew],["Vehicles",numVehicles],["Compressors",numCompressors],["Machine",equip.machineType],["Travel",totalMiles>0?`${Math.round(totalMiles)} miles`:""],["Overnight",deploy.overnight?`${deploy.nights} night${deploy.nights>1?"s":""}`:""]]},
      {title:"Materials",rows:[["Dry ice (raw)",`${fmtN(totalDI)} kg`],["Dry ice (inc. wastage)",`${fmtN(adjDI)} kg`],[inUnit?"Electricity":"Fuel",inUnit?fmt(electricityCost):`${fmtN(totalFuel)} L`]]},
      {title:"Surface",rows:[["Contamination",[profile.contaminationType,profile.severity].filter(Boolean).join(", ")],["Surface",profile.surfaceType],["Quantity",profile.quantity?`${profile.quantity} ${profile.quantityUnit||""}`:""]]},
    ],
    blocks:[{title:"Client scope",text:composedScope,html:scopeTextToHTML(composedScope)},{title:"Estimator notes",text:notes.estimator},{title:"Internal notes",text:notes.internal},{title:"Exclusions",text:notes.exclusions}],
  });

  // confirmed: true from the SavePdfDialog's "Save and download". Anything
  // else (the PDF button's click event) asks first when this would save.
  const downloadPDF=async(confirmed)=>{
    // One PDF save at a time: a second click while the first is saving does
    // nothing (the ref catches clicks faster than the button greys out).
    if(saveBusy.current)return;
    if(requiredFieldsMissing())return;
    if(confirmed!==true&&!matchesSaved(savedRef,currentSnap,finalPrice)){ setPdfAsk(true); return; }
    setPdfAsk(false);
    saveBusy.current=true;
    // Opened synchronously, before any await, so it's still tied to this
    // click for popup-blocker purposes - the HTML gets written into it once
    // the async work below finishes.
    const printWin=window.open('','_blank');
    if(!printWin){ saveBusy.current=false; alert("Please allow pop-ups for this site to print/save the quote as a PDF."); return; }
    setPdfLoading(true);
    try{
      // Save to the quote log first, same as the "Save to Log" button, so the
      // quote can still be found and edited afterward.
      let finalJob=job;
      const snap=currentSnap;
      const alreadySaved=matchesSaved(savedRef,snap,finalPrice);
    let justSaved=false;
      if(alreadySaved) finalJob={...job,quoteRef:savedRef.number};
      if(!alreadySaved) try{
        await ensureHeaders();
        const amendmentOf=amendOf;
        const logData={
          quoteNumber:job.quoteRef,
          dateCreated:new Date().toLocaleDateString("en-GB"),
          clientName:job.clientName||"",
          clientEmail:job.clientEmail||"",
          clientPhone:job.clientPhone||"",
          category:catLabel,
          application:service.subService,
          siteAddress:job.clientAddress||"",
          status:"Draft", // new quotes and amendments alike: Draft until actually sent (the amended quote keeps its own status)
          totalExVat:finalPrice,
          totalIncVat:finalPrice*1.2,
          margin:commercial.targetMargin,
          mileage:totalMiles,
          mileageCost:travelCost,
          notes:"",
          amendmentOf,
          testQuote,
          pdfGenerated:true,
          emailSent:false,
          scopeSummary:composedScope,
          stateJson:stateForSave,
          saveId:pdfSaveId.current||(pdfSaveId.current=newSaveId()),
        };
        if(amendmentOf){
          // job.quoteRef here is only the provisional number shown while
          // editing - the Apps Script assigns the real one, locked, from the
          // sheet's live state (see appendAmendment doc comment). Use
          // whatever it actually assigned for the PDF filename/content and
          // the form going forward, in the rare case a real race resolved to
          // a different letter than what was on screen.
          const result=await appendAmendment(logData,{onSlow:()=>setPdfSaveChecking(true)}); pdfSaveId.current=null;
          if(result.quoteNumber&&result.quoteNumber!==job.quoteRef){
            finalJob={...job,quoteRef:result.quoteNumber};
            setJob(finalJob);
          }
          setSavedRef({number:finalJob.quoteRef,snap,total:finalPrice}); setSavedAsBase(finalJob.quoteRef); justSaved=true;
          if(onAmendmentConsumed)onAmendmentConsumed();
        }else{
          // New quotes are numbered by the Apps Script too (see appendQuote).
          const result=await appendQuote(logData,{onSlow:()=>setPdfSaveChecking(true)}); pdfSaveId.current=null;
          if(result.quoteNumber&&result.quoteNumber!==job.quoteRef){
            finalJob={...job,quoteRef:result.quoteNumber};
            setJob(finalJob);
          }
          setSavedRef({number:finalJob.quoteRef,snap,total:finalPrice}); setSavedAsBase(finalJob.quoteRef); justSaved=true;
        }
      }catch(logErr){
        console.error("Failed to save quote to log:",logErr);
        // Never print a provisional number as if it were real.
        finalJob={...job,quoteRef:"Draft (not saved)"};
        // The printable copy still opens either way (see below), which
        // otherwise makes a failed save invisible - nothing else here
        // would tell you the sheet was never actually updated.
        alert("Warning: this quote could not be saved to the log ("+logErr.message+"). The printable copy will still open, but Save to Log / Quote Printed status won't reflect this attempt until you save it manually.");
      }

      let logoSrc="/logo-dark.png";
      try{
        const resp=await fetch("/logo-dark.png");
        const blob=await resp.blob();
        logoSrc=await new Promise(res=>{
          const r=new FileReader();
          r.onload=()=>res(r.result);
          r.readAsDataURL(blob);
        });
      }catch(e){}
      const html=await generateQuoteHTML({job:finalJob,service,profile,deploy,notes,finalPrice,logoSrc,subtotalExVat,discount:discountAmount>0?{amount:discountAmount,label:discountLabel,reason:discountReason}:null});
      printQuoteDocument(printWin,html);
      // Saved just now (not a reprint): say so here; the calculator stays
      // open (the Quote Log only on ← Quote Log).
      if(justSaved) setSavedNotice(finalJob.quoteRef);
    }catch(e){console.error(e);alert("Something went wrong generating the quote: "+e.message);try{printWin.close();}catch(_){}}
    setPdfLoading(false); setPdfSaveChecking(false);
    saveBusy.current=false;
  };

  // The ref shown in the top bar, Live Summary and phone bar.
  const refInfo=calcRef({readOnly,savedAndUnchanged,savedRef,amendOf,amendNext});
  const refProps={refInfo,test:testQuote,client:(job.clientName||"").trim()};
  const refLine=<RefChip info={refInfo} test={testQuote}/>;
  // Desktop actions, above the form (same place as Automotive's).
  const actionsBar=(
    <><CalcNav name="Industrial" status={readOnly?"Read only":calcStatusLabel({amendOf,savedAndUnchanged,copyOf})} onNew={readOnly?null:startNew} {...refProps}/>{pdfSaveChecking&&<SaveCheckingBanner/>}{savedNotice&&savedAndUnchanged&&savedRef?.number===savedNotice&&<SavedBanner refNo={savedNotice} onClose={()=>setSavedNotice(null)}/>}</>
  );

  const formView=(
    <ReadOnlyForm ro={readOnly}>
      {initialState?._fromDraft&&<DraftRestoredBanner at={initialState._draftAt}/>}
      {amendOf&&(savedAndUnchanged?<NoChangesBanner number={savedRef.number}/>:<AmendBanner amendOf={amendOf} next={amendNext}/>)}
      {copyOf&&!amendOf&&<CopyBanner copyOf={copyOf} note={!(Number(deploy.travelMiles)>0)?"Enter the travel miles for the new site (Deployment section).":""}/>}
      {initialState&&<RecalcCheckBanner savedTotal={initialState._savedTotalExVat} currentTotal={finalPrice} savedCb={initialState.costBreakdown} currentCb={costBreakdown} t={t}/>}
      {initialState&&<DiscountBanner amount={discountAmount} label={discountLabel} reason={discountReason} t={t}/>}
      <Section num="1" title="Service Selection">
        <Card>
          <div className="sp-grid" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
            <Field label={<span style={{display:"inline-flex",alignItems:"center",gap:8}}>Service Category<SectorPill category={service.category} dark size="sm" style={{textTransform:"none",letterSpacing:0}}/></span>}><select style={sel} value={service.category} onChange={e=>setService({category:e.target.value,subService:""})}>{SERVICE_CATEGORIES.filter(c=>c.value!=="automotive"||service.category==="automotive").map(c=><option key={c.value} value={c.value}>{c.label}</option>)}</select></Field>
            <Field label="Application Type"><select style={sel} value={service.subService} onChange={e=>setService(s=>({...s,subService:e.target.value}))}><option value="">— Select —</option>{(SUB_SERVICES[service.category]||[]).map(s=><option key={s}>{s}</option>)}</select></Field>
          </div>
          <Field label="Cleaning Method">
            <div style={{background:t.bg,border:`1px solid ${t.border}`,borderRadius:8,padding:"10px 13px",color:t.textMuted,fontSize:13,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              Dry Ice CO₂ Blasting
              <span style={{fontSize:9,background:t.accentDim,color:t.accent,border:`1px solid ${t.accentGlow}`,borderRadius:4,padding:"2px 6px"}}>LOCKED V1</span>
            </div>
          </Field>
          <ScopeEditor notes={notes} setNotes={setNotes} templateBody={scopeTemplate} t={t}/>
        </Card>
      </Section>
      <Section num="2" title="Job Details">
        <Card>
          <div className="sp-grid" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
            <Field label="Quote Reference"><QuoteRefField number={readOnly?readOnly.ref:(savedAndUnchanged?savedRef.number:"")} amending={amendOf}/></Field>
            <Field label="Work Type"><select style={sel} value={job.workType} onChange={e=>setJob(j=>({...j,workType:e.target.value}))}>{["One-Off Project","Maintenance Contract","Emergency Call-Out"].map(o=><option key={o}>{o}</option>)}</select></Field>
            {/* Same order as the Automotive Job Details. */}
            <Field label="Job Location"><select style={sel} value={job.jobLocation||"on-site"} onChange={e=>{const loc=e.target.value;setJob(j=>({...j,jobLocation:loc}));setCom(c=>c.marginSet?c:{...c,targetMargin:defaultMarginFor(loc)});}}>{JOB_LOCATIONS.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}</select></Field>
            <Field label="Scheduled Date (optional)"><input type="date" style={inp} value={job.jobDate} onChange={e=>setJob(j=>({...j,jobDate:e.target.value}))}/><JobDateClash date={job.jobDate} quoteNumber={amendOf||job.quoteRef} test={testQuote}/></Field>
            <Field label="Estimator Name"><input style={inp} value={job.estimator} onChange={e=>setJob(j=>({...j,estimator:e.target.value}))} placeholder="Your name"/></Field>
          </div>
          <Divider label="Client"/>
          <div className="sp-grid" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
            <Field label="Client Name"><NoAutofillInput style={inp} name="sp-job-clientname" value={job.clientName} onChange={e=>setJob(j=>({...j,clientName:e.target.value}))} placeholder="Company / contact"/></Field>
            <Field label="Email"><NoAutofillInput style={inp} name="sp-job-clientemail" value={job.clientEmail} onChange={e=>setJob(j=>({...j,clientEmail:e.target.value}))} placeholder="email@example.com"/></Field>
            <Field label="Phone"><NoAutofillInput style={inp} name="sp-job-clientphone" value={job.clientPhone} onChange={e=>setJob(j=>({...j,clientPhone:e.target.value}))} placeholder="07xxx xxxxxx"/></Field>
            <Field label="Point of Contact"><NoAutofillInput style={inp} name="sp-job-clientpoc" value={job.clientPOC} onChange={e=>setJob(j=>({...j,clientPOC:e.target.value}))} placeholder="Contact name"/></Field>
          </div>
          <Field label="Client Address"><NoAutofillInput as="textarea" style={txta} name="sp-job-clientaddress" value={job.clientAddress} onChange={e=>setJob(j=>({...j,clientAddress:e.target.value}))} placeholder="Street, Town, County, Postcode"/></Field>
          <Divider label="Site"/>
          <div className="sp-grid" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
            <Field label="Site Name"><NoAutofillInput style={inp} name="sp-job-sitename" value={job.siteName} onChange={e=>setJob(j=>({...j,siteName:e.target.value}))} placeholder="Site name"/></Field>
            <Field label="Site Postcode"><NoAutofillInput style={inp} name="sp-job-postcode" value={job.postcode} onChange={e=>setJob(j=>({...j,postcode:e.target.value.toUpperCase()}))} placeholder="EC1A 1BB"/></Field>
          </div>
        </Card>
      </Section>
      <Section num="3" title="Cleaning Profile">
        <Card>
          <QuantityHero quantity={profile.quantity} unit={profile.quantityUnit||"m²"} onQuantity={v=>setProfile(p=>({...p,quantity:v}))} areas={profile.areas||[]} onAreas={setAreas}
            chain={{manual:manualHoursSet,hrs:workHrs,kg:view.adjDI,price:view.finalPrice,pending:view.pricePending}} hint={readOnly?null:qtyHint}/>
          <div className="sp-grid" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
            <Field label="Contamination Type">{(()=>{
              // Every type is offered; the category's usual ones come first. A
              // saved quote's older value that's no longer in the list stays as
              // it was, marked "(saved)".
              const{common,other}=contaminationGroups(service.category);
              const ct=profile.contaminationType;
              const savedOdd=ct&&!CONTAMINATION_TYPES.includes(ct);
              return(
                <select style={{...sel,...(!ct?{borderColor:t.warning}:{})}} value={ct} onChange={e=>setProfile(p=>({...p,contaminationType:e.target.value}))}>
                  {!ct&&<option value="">— Pick contamination —</option>}
                  {savedOdd&&<option value={ct}>{ct} (saved)</option>}
                  {common.length>0&&<optgroup label="Common for this industry">{common.map(o=><option key={o} value={o}>{o}</option>)}</optgroup>}
                  <optgroup label={common.length?"Other":"All types"}>{other.map(o=><option key={o} value={o}>{o}</option>)}</optgroup>
                </select>
              );
            })()}{!profile.contaminationType&&<div style={{fontSize:11,color:t.warning,marginTop:4}}>Needed before the quote can be priced.</div>}</Field>
            <Field label="Severity"><select style={sel} value={profile.severity} onChange={e=>setProfile(p=>({...p,severity:e.target.value}))}>{CONTAMINATION_SEVERITIES.map(o=><option key={o}>{o}</option>)}</select></Field>
            <Field label="Surface Type"><select style={sel} value={profile.surfaceType} onChange={e=>setProfile(p=>({...p,surfaceType:e.target.value}))}>{SURFACE_TYPES.map(o=><option key={o}>{o}</option>)}</select></Field>
            <Field label="Surface Sensitivity"><select style={sel} value={profile.sensitivity} onChange={e=>setProfile(p=>({...p,sensitivity:e.target.value}))}>{SURFACE_SENSITIVITIES.map(o=><option key={o}>{o}</option>)}</select></Field>
          </div>
          <div className="sp-grid" style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr",gap:12,paddingTop:14,borderTop:`1px solid ${t.border}`}}>
            <Stat label={`Base rate${overrides.productionRate?" · override":""}`} value={`${fmtN(basePR)} ${profile.quantityUnit}/hr`}/>
            <Stat label="Adj. rate" value={`${fmtN(adjPR,2)} ${profile.quantityUnit}/hr`} note={`× ${fmtN(prodMult,2)} access`}/>
            <Stat label={`Dry ice${overrides.dryIcePerHr?" · override":""}`} value={`${fmtN(dryIceHr)} kg/hr`}/>
            <Stat label="Production hours" value={`${fmtN(workHrs)} hrs`} note={manualHoursSet?<span style={{...type.pill,display:"inline-block",marginTop:2,fontSize:"0.66rem",padding:"2px 8px",background:t.warningSoft,color:t.warning,border:`1px solid ${t.warning}`}}>Set manually</span>:`${fmtN(qtyNum)} ÷ ${fmtN(adjPR,2)}/hr`}/>
          </div>
          <div style={{marginTop:10,fontSize:12.5,color:t.textMuted}}>
            Dry ice: {overrides.dryIcePerHr?`manual override, ${fmtN(dryIceHr)} kg/hr`:`${fmtN(diBase)} base × ${fmtN(diSensMult,2)} sensitivity × ${fmtN(diMachineMult,2)} machine = ${fmtN(dryIceHr)} kg/hr`}
          </div>
        </Card>
      </Section>
      <Section num="4" title="Site Access & Constraints">
        <Card>
          {manualHoursSet&&(
            <div style={{background:t.warningSoft,border:`1px solid ${t.warning}`,borderRadius:8,padding:"10px 14px",marginBottom:14,fontSize:14,lineHeight:1.45,color:t.warning}}>
              ⚠ Manual Hours Override is active — these settings won't affect pricing until it's cleared (Equipment &amp; Consumables → Advanced Overrides).
            </div>
          )}
          <div className="sp-grid" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
            <Field label="Access"><select style={sel} value={access.access} onChange={e=>setAccess(a=>({...a,access:e.target.value}))}>{Object.keys(ACCESS_IMPACT).map(o=><option key={o}>{o}</option>)}</select></Field>
            <Field label="Vertical Access"><select style={sel} value={access.vertical} onChange={e=>setAccess(a=>({...a,vertical:e.target.value}))}>{Object.keys(VERTICAL_IMPACT).map(o=><option key={o}>{o}</option>)}</select></Field>
            <Field label="Environment"><select style={sel} value={access.environment} onChange={e=>setAccess(a=>({...a,environment:e.target.value}))}>{Object.keys(ENVIRON_IMPACT).map(o=><option key={o}>{o}</option>)}</select></Field>
            <Field label="Setup Complexity"><select style={sel} value={access.setupComplexity} onChange={e=>setAccess(a=>({...a,setupComplexity:e.target.value}))}>{Object.keys(SETUP_OVERHEAD).map(o=><option key={o}>{o}</option>)}</select></Field>
            <Field label="Hours Restriction"><select style={sel} value={access.hoursRestriction} onChange={e=>setAccess(a=>({...a,hoursRestriction:e.target.value}))}>{Object.keys(HOURS_RESTRICT).map(o=><option key={o}>{o}</option>)}</select></Field>
            <Field label="Weather Exposure"><select style={sel} value={access.weather} onChange={e=>setAccess(a=>({...a,weather:e.target.value}))}>{Object.keys(WEATHER_OH).map(o=><option key={o}>{o}</option>)}</select></Field>
          </div>
          <div className="sp-grid" style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginTop:8}}>
            <InfoChip label="Production Impact" value={`${fmtN(prodMult*100,0)}%`}/>
            <InfoChip label="Overhead Hours" value={`+${fmtN(setupHrs+ohHrs+packHrs)}h`}/>
            <InfoChip label="Risk Uplift" value={`+${fmtN(riskPct,0)}%`}/>
          </div>
        </Card>
      </Section>
      <Section num="5" title="Deployment & Travel">
        <Card>
          <div style={{marginBottom:12}}>
            {(deploy.vehicles||[{crew:2,compressor:true}]).map((v,i)=>(
              <div key={i} style={{display:"grid",gridTemplateColumns:"auto 1fr 1fr auto",gap:10,alignItems:"center",padding:"10px 12px",borderRadius:8,border:`1px solid ${t.border}`,background:t.navyDark,marginBottom:8}}>
                <span style={{fontSize:11,fontWeight:700,color:t.textMuted,minWidth:70}}>Vehicle {i+1}</span>
                <Field label="Crew" style={{margin:0}}>
                  <NumInput style={{...inp,padding:"7px 10px"}} min="1" value={v.crew} onChange={e=>setDeploy(d=>({...d,vehicles:d.vehicles.map((x,j)=>j===i?{...x,crew:+e.target.value}:x)}))}/>
                </Field>
                <Field label="Tow Compressor" style={{margin:0,paddingTop:4}}>
                  <Toggle value={v.compressor} onChange={val=>setDeploy(d=>({...d,vehicles:d.vehicles.map((x,j)=>j===i?{...x,compressor:val}:x)}))} label="Yes"/>
                </Field>
                {(deploy.vehicles||[]).length>1&&(
                  <button type="button" onClick={()=>setDeploy(d=>({...d,vehicles:d.vehicles.filter((_,j)=>j!==i)}))} style={{width:28,height:28,borderRadius:6,border:`1px solid ${t.danger}44`,background:"transparent",color:t.danger,cursor:"pointer",fontSize:14,flexShrink:0}}>×</button>
                )}
              </div>
            ))}
            <button type="button" onClick={()=>setDeploy(d=>({...d,vehicles:[...(d.vehicles||[]),{crew:1,compressor:false}]}))} style={{width:"100%",padding:"8px 0",borderRadius:8,border:`1px dashed ${t.border}`,background:"transparent",color:t.textMuted,cursor:"pointer",fontSize:11,fontWeight:700,letterSpacing:"0.1em"}}>+ Add Vehicle</button>
          </div>
          <div className="sp-grid" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:8}}>
            <div style={{background:t.navyDark,borderRadius:8,padding:"10px 12px",border:`1px solid ${t.border}`}}>
              <div style={{fontSize:9,color:t.textDim,letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:4}}>Total Crew</div>
              <div style={{fontSize:13,fontWeight:700,color:t.accent}}>{(deploy.vehicles||[]).reduce((s,v)=>s+(v.crew||1),0)} operatives</div>
            </div>
            <div style={{background:t.navyDark,borderRadius:8,padding:"10px 12px",border:`1px solid ${t.border}`}}>
              <div style={{fontSize:9,color:t.textDim,letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:4}}>Vehicles / Compressors</div>
              <div style={{fontSize:13,fontWeight:700,color:t.accent}}>{(deploy.vehicles||[]).length} vehicles · {(deploy.vehicles||[]).filter(v=>v.compressor).length} compressor{(deploy.vehicles||[]).filter(v=>v.compressor).length!==1?"s":""}</div>
            </div>
          </div>
          <Divider label="Mileage Calculator"/>
          <div className="sp-grid" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
            <Field label="From Postcode"><NoAutofillInput style={inp} name="sp-from-postcode" value={fromPC} onChange={e=>setFromPC(e.target.value.toUpperCase())} placeholder="TR27 4QZ"/></Field>
            <Field label="To Postcode"><NoAutofillInput style={inp} name="sp-to-postcode" value={toPC} onChange={e=>setToPC(e.target.value.toUpperCase())} placeholder="EC1A 1BB"/></Field>
          </div>
          <div className="sp-grid" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
            <Field label="Rate per Mile (£)"><NumInput pre="£" style={inp} step="0.01" value={mileRate} onChange={e=>setMileRate(+e.target.value)} unit="/mile"/></Field>
            <Field label="Avg Travel Speed (mph)"><NumInput style={inp} step="1" value={avgSpeed} onChange={e=>setAvgSpeed(+e.target.value)} unit="mph"/></Field>
          </div>
          <Field label="&nbsp;">
            <button type="button" onClick={calculateMileage} disabled={mileLoading||manualMiles!=null} title={manualMiles!=null?"Manual miles in use: clear it to use the lookup":undefined} style={{...secondaryBtn,width:"100%",padding:10,fontSize:13,opacity:manualMiles!=null?0.5:1}}>{mileLoading?"Calculating...":"Calculate Distance"}</button>
          </Field>
          <Toggle value={returnTrip} onChange={v=>{setReturnTrip(v);if(manualMiles!=null)setDeploy(d=>({...d,travelMiles:milesTotal(manualMiles,v)}));else if(calcMiles)setDeploy(d=>({...d,travelMiles:v?calcMiles*2:calcMiles}));}} label="Include return journey"/>
          {mileError&&<div style={{color:t.danger,fontSize:12,marginTop:8}}>{mileError}</div>}
          <ManualMilesField manual={deploy.manualMiles} lookupMiles={calcMiles} onChange={v=>setDeploy(d=>({...d,manualMiles:v,travelMiles:v!=null?milesTotal(v,returnTrip):(calcMiles?milesTotal(calcMiles,returnTrip):"")}))}/>
          {(manualMiles!=null||calcMiles)&&(
            <div style={{background:t.navyDark,borderRadius:8,padding:12,border:`1px solid ${t.border}`,marginTop:10}}>
              <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
                <span style={{fontSize:11,color:t.textMuted}}>One-way distance{manualMiles!=null?" (manual)":""}</span>
                <span style={{fontSize:12,fontWeight:600}}>{manualMiles!=null?manualMiles:calcMiles} miles</span>
              </div>
              <div style={{display:"flex",justifyContent:"space-between",borderTop:`1px solid ${t.border}`,paddingTop:8,marginTop:4}}>
                <span style={{fontSize:11,color:t.textMuted}}>Total → travel cost (fuel/vehicle)</span>
                <span style={{fontSize:12,color:t.accent,fontWeight:700}}>{totalMiles} miles → {fmt(travelCost)}</span>
              </div>
              <div style={{display:"flex",justifyContent:"space-between",borderTop:`1px solid ${t.border}`,paddingTop:8,marginTop:4}}>
                <span style={{fontSize:11,color:t.textMuted}}>Travel time (labour, all crew)</span>
                <span style={{fontSize:12,color:t.accent,fontWeight:700}}>{fmtN(travelTimeHrs)} hrs → {fmt(travelTimeHrs*equip.labourRatePerHour*totalCrew)}</span>
              </div>
            </div>
          )}
          <Divider label="Overnight"/>
          <Toggle value={deploy.overnight} onChange={v=>setDeploy(d=>({...d,overnight:v}))} label="Overnight Stay Required"/>
          {deploy.overnight&&(
            <div className="sp-grid" style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12,marginTop:14}}>
              <Field label="Nights"><NumInput style={inp} min="1" value={deploy.nights} onChange={e=>setDeploy(d=>({...d,nights:+e.target.value}))} unit="nights"/></Field>
              <Field label="Hotel / Person / Night (£)"><NumInput pre="£" style={inp} value={deploy.accommodationPerNight} onChange={e=>setDeploy(d=>({...d,accommodationPerNight:+e.target.value}))}/></Field>
              <Field label="Subsistence / Person / Day (£)"><NumInput pre="£" style={inp} value={deploy.subsistencePerPersonPerDay} onChange={e=>setDeploy(d=>({...d,subsistencePerPersonPerDay:+e.target.value}))}/></Field>
            </div>
          )}
        </Card>
      </Section>
      <Section num="6" title="Equipment & Consumables" defaultOpen={false}>
        <Card>
          <Divider label="Dry Ice Machine"/>
          <div className="sp-grid" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
            <Field label="Machine Type"><select style={sel} value={equip.machineType} onChange={e=>setEquip(q=>({...q,machineType:e.target.value}))}>{["Champ Vario","Champ Ultra"].map(o=><option key={o}>{o}</option>)}</select></Field>
            <Field label="Cleaning Intensity"><select style={sel} value={equip.intensity} onChange={e=>setEquip(q=>({...q,intensity:e.target.value}))}>{Object.keys(FUEL_INTENSITY).map(o=><option key={o}>{o}</option>)}</select></Field>
          </div>
          <Divider label="Compressor — Kaeser Mobilair M59"/>
          {inUnit?(
            <>
              <Field label="Electricity (£/hr per compressor)"><NumInput pre="£" style={inp} step="0.1" value={electricityPerHr} onChange={e=>setEquip(q=>({...q,electricityPerHr:+e.target.value}))} unit="/h"/></Field>
              <InfoChip label="In-unit, mains power" value={`${fmtN(totalHrs)} hrs × ${fmt(electricityPerHr)}/hr × ${Math.max(1,numCompressors)} compressor${Math.max(1,numCompressors)>1?"s":""} → ${fmt(electricityCost)}`} auto/>
            </>
          ):(
            <>
              <div className="sp-grid" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
                <Field label="Base Fuel Burn (L/hr)"><NumInput style={inp} step="0.1" value={equip.baseFuelBurn} onChange={e=>setEquip(q=>({...q,baseFuelBurn:+e.target.value}))} unit="L/h"/></Field>
                <Field label="Diesel Price (£/L)"><NumInput pre="£" style={inp} step="0.01" value={equip.dieselPrice} onChange={e=>setEquip(q=>({...q,dieselPrice:+e.target.value}))} unit="/L"/></Field>
              </div>
              <InfoChip label={`Fuel burn at ${equip.intensity} intensity`} value={`${fmtN(fuelHr,2)} L/hr → ${fmt(fuelCost)} total`} auto/>
            </>
          )}
          <Divider label={`Dry Ice Supply — ${diSupplier?.name||"No active supplier"}`}/>
          {!diSupplier&&(
            <div style={{background:t.warningSoft,border:`1px solid ${t.warning}`,borderRadius:8,padding:"10px 14px",marginBottom:14,fontSize:12,color:t.warning}}>
              ⚠ No active dry-ice supplier found in Admin → Suppliers. Add one there (Products Supplied must mention "dry ice") before quoting.
            </div>
          )}
          {dryIceSuppliers.length>1&&(
            <Field label="Supplier">
              <select style={sel} value={diSupplier?.id||""} onChange={e=>setEquip(q=>({...q,dryIceSupplierId:e.target.value}))}>
                {dryIceSuppliers.map(s=><option key={s.id} value={s.id}>{dryIceOptionLabel(s,diPrimary)}</option>)}
              </select>
            </Field>
          )}
          {hasDeliveredTiers(diSupplier)?(
            <DeliveredTiersPanel supplier={diSupplier} dryIceKg={totalDI} adjKg={adjDI} charge={diCharge}
              wastageField={<Field label="Wastage (%)"><NumInput style={inp} value={equip.wastage} onChange={e=>setEquip(q=>({...q,wastage:+e.target.value}))} unit="%"/></Field>}/>
          ):(<>
          <div className="sp-grid" style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12}}>
            <InfoChip label="Price per kg (£)" value={diSupplier?`£${diPricePerKg.toFixed(3)}`:"—"} auto/>
            <InfoChip label="Delivery per container (£)" value={diSupplier?`£${diDeliveryPerBin.toFixed(2)}`:"—"} auto/>
            <Field label="Wastage (%)"><NumInput style={inp} value={equip.wastage} onChange={e=>setEquip(q=>({...q,wastage:+e.target.value}))} unit="%"/></Field>
          </div>
          {(()=>{const sd=daysSince(diSupplier?.priceLastUpdated);return sd!=null&&sd>SUPPLIER_PRICE_STALE_DAYS&&(
            <div style={{background:t.warningSoft,border:`1px solid ${t.warning}`,borderRadius:8,padding:"10px 14px",marginTop:8,marginBottom:14,fontSize:12,color:t.warning}}>
              ⚠ {diSupplier.name}'s price was last checked {sd} days ago (over {SUPPLIER_PRICE_STALE_DAYS}) — worth confirming it's still current in Admin → Suppliers.
            </div>
          );})()}
          <Divider label="Container Selection"/>
          <div className="sp-grid" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:14}}>
            {diBinOptions.map(b=>(
              <div key={b.label} onClick={()=>setEquip(q=>({...q,binCapacityLabel:b.label}))} style={{padding:"12px 14px",borderRadius:8,border:`1px solid ${diSelectedBin?.label===b.label?t.accent:t.border}`,background:diSelectedBin?.label===b.label?t.accentDim:"transparent",cursor:"pointer"}}>
                <div style={{fontSize:13,fontWeight:700,color:diSelectedBin?.label===b.label?t.accent:t.text,marginBottom:2}}>{b.label}</div>
                <div style={{fontSize:11,color:t.textMuted}}>{b.capacity_kg}kg capacity &nbsp;·&nbsp; £{diBinRentalPerBin.toFixed(2)} hire/bin</div>
              </div>
            ))}
          </div>
          <div className="sp-grid" style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:14}}>
            <InfoChip label="Bins required" value={`${binsNeeded} × ${diSelectedBin?.label||"—"}`}/>
            <InfoChip label="Bin hire" value={`£${binHire.toFixed(2)}`}/>
            <InfoChip label="Max capacity" value={`${(binsNeeded*binSpec.capacity).toFixed(0)}kg`}/>
          </div>
          <div className="sp-grid" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:14}}>
            <InfoChip label="Raw Dry Ice" value={`${fmtN(totalDI)} kg`}/>
            <InfoChip label="Adjusted (inc. wastage)" value={`${fmtN(adjDI)} kg → ${fmt(diCost)}`}/>
          </div>
          {diCharge.pinned&&diCharge.liveCost!==diCharge.cost&&<DryIcePinnedNote charge={diCharge}/>}
          </>)}
          <Divider label="Labour Rate"/>
          <div style={{maxWidth:220}}>
            <Field label="Rate (£/hr per person)"><NumInput pre="£" style={inp} value={equip.labourRatePerHour} onChange={e=>setEquip(q=>({...q,labourRatePerHour:+e.target.value}))} unit="/h"/></Field>
          </div>
          <button type="button" onClick={()=>setAdvMode(m=>!m)} style={{display:"flex",alignItems:"center",gap:6,background:"transparent",border:`1px dashed ${t.border}`,borderRadius:8,color:t.textMuted,padding:"8px 14px",fontSize:11,cursor:"pointer",width:"100%",justifyContent:"center",marginTop:8}}>
            {advMode?"▲ Hide":"▼ Show"} Advanced Overrides
          </button>
          {advMode&&(
            <div style={{marginTop:12,padding:14,background:t.navyDark,borderRadius:8,border:`1px solid ${t.border}`}}>
              <div style={{fontSize:11,color:t.warning,marginBottom:10}}>⚠ These override auto-calculated values</div>
              <div className="sp-grid" style={{display:"grid",gridTemplateColumns:inUnit?"1fr 1fr":"1fr 1fr 1fr",gap:12}}>
                <Field label={`Prod. Rate (${profile.quantityUnit}/hr)`}><NumInput style={inp} placeholder={fmtN(basePR)} value={overrides.productionRate||""} onChange={e=>setOv(o=>({...o,productionRate:e.target.value?+e.target.value:null}))} unit={`${profile.quantityUnit}/h`}/></Field>
                <Field label="Dry Ice (kg/hr)"><NumInput style={inp} placeholder={fmtN(dryIceHr)} value={overrides.dryIcePerHr||""} onChange={e=>setOv(o=>({...o,dryIcePerHr:e.target.value?+e.target.value:null}))} unit="kg/h"/></Field>
                {!inUnit&&<Field label="Fuel Burn (L/hr)"><NumInput style={inp} placeholder={fmtN(fuelHr,2)} value={overrides.fuelBurn||""} onChange={e=>setOv(o=>({...o,fuelBurn:e.target.value?+e.target.value:null}))} unit="L/h"/></Field>}
              </div>
              <div style={{marginTop:12,paddingTop:12,borderTop:`1px dashed ${t.border}`}}>
                <Field label="Manual Hours Override (optional)">
                  <NumInput style={inp} step="0.1" min="0" placeholder={`Calculated: ${fmtN(calculatedWorkHrs)} hrs`} value={overrides.manualHours??""} onChange={e=>setOv(o=>({...o,manualHours:e.target.value?+e.target.value:null}))} unit="h"/>
                </Field>
                <div style={{fontSize:13,lineHeight:1.45,color:t.textMuted,marginTop:6}}>Replaces the m² ÷ rate production time above with this figure. Everything downstream — dry ice, fuel, labour cost, margin — recalculates off it.</div>
              </div>
            </div>
          )}
        </Card>
      </Section>
      <Section num="7" title="Commercial Settings">
        <Card>
          <Divider label="Equipment Recovery"/>
          <div className="sp-grid" style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12}}>
            <Field label="Machine (£/hr)"><NumInput pre="£" style={inp} value={recovery.machinePerHr} onChange={e=>setRec(r=>({...r,machinePerHr:+e.target.value}))} unit="/h"/></Field>
            <Field label="Compressor (£/hr)"><NumInput pre="£" style={inp} value={recovery.compressorPerHr} onChange={e=>setRec(r=>({...r,compressorPerHr:+e.target.value}))} unit="/h"/></Field>
            <Field label="Vehicle (£/hr)"><NumInput pre="£" style={inp} value={recovery.vehiclePerHr} onChange={e=>setRec(r=>({...r,vehiclePerHr:+e.target.value}))} unit="/h"/></Field>
          </div>
          <Divider label="Margin & Uplift"/>
          <MarginSlider value={commercial.targetMargin} onChange={v=>setCom(c=>({...c,targetMargin:v,marginSet:true}))} basePrice={basePrice} totalCost={totalCost} jobLocation={job.jobLocation||"on-site"}/>
          <div className="sp-grid" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
            <Field label="Risk Uplift (%)">
              <div style={{background:t.bg,border:`1px solid ${t.border}`,borderRadius:8,padding:"10px 13px",color:t.textMuted,fontSize:13,display:"flex",justifyContent:"space-between"}}>
                {fmtN(riskPct,0)}%
                <span style={{fontSize:9,background:t.accentDim,color:t.accent,border:`1px solid ${t.accentGlow}`,borderRadius:4,padding:"2px 6px"}}>AUTO</span>
              </div>
            </Field>
            <Field label="Contingency (%)"><NumInput style={inp} value={commercial.contingency} onChange={e=>setCom(c=>({...c,contingency:+e.target.value}))} unit="%"/></Field>
          </div>
          <div className="sp-grid" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
            <Field label="Minimum Job Price (£)"><NumInput pre="£" style={inp} value={commercial.minJobPrice} onChange={e=>setCom(c=>({...c,minJobPrice:+e.target.value}))}/></Field>
            <Field label="Round Final Price"><select style={sel} value={commercial.roundPrice} onChange={e=>setCom(c=>({...c,roundPrice:e.target.value}))}>{["none","nearest £50","nearest £100"].map(o=><option key={o}>{o}</option>)}</select></Field>
          </div>
          <Divider label="Discount (optional)"/>
          {/* Presets (discountPresets.js): set the % and reason in one go;
              changing them by hand makes it a manual discount again. */}
          <Field label="Discount preset">
            <select style={sel} value={activeDiscountPreset(commercial)} onChange={e=>setCom(c=>applyDiscountPreset(c,e.target.value))}>
              <option value="">No preset (manual)</option>
              {Object.entries(DISCOUNT_PRESETS).map(([k,p])=><option key={k} value={k}>{p.label}</option>)}
            </select>
          </Field>
          <div className="sp-grid" style={{display:"grid",gridTemplateColumns:commercial.discountType&&commercial.discountType!=="none"?"1fr 1fr":"1fr",gap:12}}>
            <Field label="Discount Type">
              <select style={sel} value={commercial.discountType||"none"} onChange={e=>setCom(c=>({...c,discountType:e.target.value}))}>
                <option value="none">None</option>
                <option value="percent">Discount amount — Percentage (%)</option>
                <option value="flat">Discount amount — Flat (£)</option>
                <option value="target">Target final price (£, ex VAT)</option>
              </select>
            </Field>
            {commercial.discountType==="percent"&&(
              <Field label="Discount (%)">
                <NumInput style={inp} min="0" max="100" value={commercial.discountValue||""} onChange={e=>setCom(c=>({...c,discountValue:e.target.value?+e.target.value:0}))} placeholder="0" unit="%"/>
              </Field>
            )}
            {commercial.discountType==="flat"&&(
              <Field label="Discount (£)">
                <NumInput pre="£" style={inp} min="0" value={commercial.discountValue||""} onChange={e=>setCom(c=>({...c,discountValue:e.target.value?+e.target.value:0}))} placeholder="0"/>
              </Field>
            )}
            {commercial.discountType==="target"&&(
              <Field label="Target Final Price (£)">
                <NumInput pre="£" style={inp} min="0" value={commercial.discountTargetPrice??""} onChange={e=>setCom(c=>({...c,discountTargetPrice:e.target.value?+e.target.value:null}))} placeholder={fmtN(subtotalExVat)}/>
              </Field>
            )}
          </div>
          {commercial.discountType&&commercial.discountType!=="none"&&(
            <>
              <Field label="Discount Reason"><input style={inp} value={commercial.discountReason||""} onChange={e=>setCom(c=>({...c,discountReason:e.target.value}))} placeholder="e.g. Introductory rate"/></Field>
              <div style={{fontSize:11,color:discountAmount>0?t.accent:t.textDim,marginTop:6}}>
                {discountAmount>0
                  ? `→ Discount: ${fmt(discountAmount)} (${fmtN(subtotalExVat>0?discountAmount/subtotalExVat*100:0,1)}%) — subtotal ${fmt(subtotalExVat)} → ${fmt(finalPrice)}`
                  : commercial.discountType==="target"?"Enter a target price below the current subtotal to calculate the discount.":"Enter a value above to calculate the discount."}
              </div>
            </>
          )}
        </Card>
      </Section>
      <Section num="8" title="Notes & Assumptions" defaultOpen={false}>
        <Card>
          <div className="sp-grid" style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"1fr 1fr",gap:12}}>
            <Field label="Estimator Notes"><textarea style={txta} value={notes.estimator} onChange={e=>setNotes(n=>({...n,estimator:e.target.value}))} placeholder="Pricing assumptions, site observations..."/></Field>
            <Field label="Internal Assumptions"><textarea style={txta} value={notes.internal} onChange={e=>setNotes(n=>({...n,internal:e.target.value}))} placeholder="Production rate basis, risks..."/></Field>
            <Field label="Exclusions"><textarea style={txta} value={notes.exclusions} onChange={e=>setNotes(n=>({...n,exclusions:e.target.value}))} placeholder="What is not included..."/></Field>
          </div>
        </Card>
      </Section>
    </ReadOnlyForm>
  );

  if(isMobile){
    return(
      <div className="sp-calc" style={calcRoot}>
        <div style={{padding:"16px 16px 96px"}}>
          <CalcNav name="Industrial" status={readOnly?"Read only":calcStatusLabel({amendOf,savedAndUnchanged,copyOf})} onNew={readOnly?null:startNew} isMobile {...refProps}/>{pdfSaveChecking&&<SaveCheckingBanner/>}{savedNotice&&savedAndUnchanged&&savedRef?.number===savedNotice&&<SavedBanner refNo={savedNotice} onClose={()=>setSavedNotice(null)}/>}
          {formView}
          <div ref={previewRef}>{quotePreview}</div>
        </div>
        <CalcMobileBar readOnly={!!readOnly} refLine={refLine} finalPrice={view.pricePending?null:view.finalPrice} grossMargin={view.grossMargin} marginColor={viewMarginColor} summary={<SummaryPanel calc={view}/>} onSave={handleOpenSaveModal} onPDF={downloadPDF} onJobSheet={printJobSheet} onPreview={goToPreview} pdfLoading={pdfLoading}/>
        {pdfAsk&&<SavePdfDialog amendOf={amendOf} next={amendNext} prefix="SBL" isMobile={isMobile} onConfirm={()=>downloadPDF(true)} onClose={()=>setPdfAsk(false)}/>}
        {showSaveModal&&(
          <SaveQuoteModal
            quoteNumber={amendOf?amendNext:job.quoteRef}
            category={catLabel}
            totalExVat={finalPrice}
            totalIncVat={finalPrice*1.2}
            margin={commercial.targetMargin}
            mileage={totalMiles}
            mileageCost={travelCost}
            scopeText={composedScope}
            application={service.subService}
            stateJson={stateForSave}
            amendmentOf={amendOf}
            amendStatus="Draft"
            savedAs={savedAndUnchanged?savedRef.number:""}
            initialTestQuote={testQuote}
            initialClientName={job.clientName}
            initialClientEmail={job.clientEmail}
            initialClientPhone={job.clientPhone}
            initialSiteAddress={job.clientAddress}
            onClose={()=>setShowSaveModal(false)}
            onSaved={markSaved}
          />
        )}
      </div>
    );
  }

  return(
    <div className="sp-calc" style={{...calcRoot,display:"grid",gridTemplateColumns:"minmax(0,1fr) 380px",height:"calc(100vh - 73px)",minHeight:0}}>
      <div style={{padding:"32px 40px",overflowY:"auto",height:"100%"}}>
        {actionsBar}
        {formView}
        {quotePreview}
      </div>
      <CalcSidePanel readOnly={!!readOnly} refLine={refLine} summary={<SummaryPanel calc={view}/>} onSave={handleOpenSaveModal} onPDF={downloadPDF} onJobSheet={printJobSheet} pdfLoading={pdfLoading}>
        {pdfAsk&&<SavePdfDialog amendOf={amendOf} next={amendNext} prefix="SBL" isMobile={isMobile} onConfirm={()=>downloadPDF(true)} onClose={()=>setPdfAsk(false)}/>}
        {showSaveModal&&(
        <SaveQuoteModal
          quoteNumber={amendOf?amendNext:job.quoteRef}
          category={catLabel}
          totalExVat={finalPrice}
          totalIncVat={finalPrice*1.2}
          margin={commercial.targetMargin}
          mileage={totalMiles}
          mileageCost={travelCost}
          scopeText={composedScope}
          application={service.subService}
          stateJson={stateForSave}
          amendmentOf={amendOf}
            amendStatus="Draft"
          savedAs={savedAndUnchanged?savedRef.number:""}
          initialTestQuote={testQuote}
          initialClientName={job.clientName}
          initialClientEmail={job.clientEmail}
          initialClientPhone={job.clientPhone}
          initialSiteAddress={job.clientAddress}
          onClose={()=>setShowSaveModal(false)}
          onSaved={markSaved}
        />
      )}
      </CalcSidePanel>
    </div>
  );
}



// ─── AUTOMOTIVE CALCULATOR ────────────────────────────────────────────────────

// Automotive services, protection, add-ons and pricing factors live in
// autoCatalogue.js (shared with the client quote template).

// ─── CUSTOM LABOUR ─────────────────────────────────────────────────────────
// Free-form labour items on an Automotive quote: {id, label, hours,
// materials, preset?, seized?}. label is the client-facing description;
// hours use the quote's labour rate and crew (no per-row rate) and are NOT
// scaled by vehicle type; materials are marked up like other materials.
const SEIZED_FIXINGS_HOURS=1;
const CUSTOM_LABOUR_PRESETS={
  underslungTanks:{
    name:"Remove and refit underslung tanks (fresh/grey water)",
    hours:2.0,
    label:"Drain-down, removal and refit of underslung water tanks to give full access to the chassis rails, including disconnection and reconnection of associated pipework and sensors.",
    seizedOption:true,
  },
};
// Hours an item actually adds, including the seized-fixings allowance.
function customLabourHours(row){
  return (Number(row?.hours)||0)+(row?.seized&&CUSTOM_LABOUR_PRESETS[row?.preset]?.seizedOption?SEIZED_FIXINGS_HOURS:0);
}
// A custom row that adds cost (hours or materials) but has no description:
// it would be priced into the total yet invisible on the client quote, so
// saving is blocked until it's described or removed.
function isBlankPricedCustomRow(row){
  return !String(row?.label||"").trim()&&(customLabourHours(row)>0||(Number(row?.materials)||0)>0);
}
// A row with no description and nothing priced does nothing - left out of
// pricing, the Job Sheet and the saved quote.
function isEmptyCustomRow(row){
  return !String(row?.label||"").trim()&&customLabourHours(row)===0&&!(Number(row?.materials)||0);
}
function newCustomLabourRow(presetKey){
  const pr=CUSTOM_LABOUR_PRESETS[presetKey];
  // internal: priced as normal but never on the client quote. New rows start
  // internal; untick to show the description under "Additional work".
  return {id:Date.now().toString(36)+Math.random().toString(36).slice(2,6),label:pr?.label||"",hours:pr?.hours??1,materials:0,internal:true,...(pr?{preset:presetKey,seized:false}:{})};
}

function calcAuto(state, rates, diData){
  const macPreset = AUTO_MACHINE_PRESETS[state.machineType] || AUTO_MACHINE_PRESETS["Champ Vario"];
  const dryIceKgHr = rates.dryIceKgHrOverride || macPreset.dryIceKgHr;
  const fuelBurnLHr = rates.fuelBurnOverride || macPreset.fuelBurnLHr;
  const contamMult = AUTO_CONTAM_MULT[state.contamination]?.mult || 1.0;
  const vehicleMult = AUTO_VEHICLE_MULT[state.vehicleType] || 1.0;

  let labourHrs = 0;
  let dryIceKg = 0;
  // Service hours that don't run the blaster (blastFrac < 1, e.g. Full
  // Underbody's setup and wheels): labour only, no dry ice or machine time.
  let nonBlastHrs = 0;

  state.services.forEach(key => {
    const svc = AUTO_SERVICE_HOURS[key];
    if(!svc) return;
    const hrs = svc.hrs * contamMult * vehicleMult;
    const blastFrac = svc.blastFrac ?? 1;
    labourHrs += hrs;
    nonBlastHrs += hrs * (1 - blastFrac);
    dryIceKg += hrs * blastFrac * dryIceKgHr * svc.diMult;
  });

  // Protection labour (scaled for larger vehicles - see AUTO_PROTECTION_SCALE)
  const prot = AUTO_PROTECTION[state.protection];
  const protScale = AUTO_PROTECTION_SCALE[state.vehicleType] || 1;
  // Labour only, like custom labour: kept out of machine hours and dry ice below.
  const protHrs = (prot?.labourHrs || 0) * protScale;
  labourHrs += protHrs;

  // Custom labour items - not scaled by vehicle type
  const customItems = (state.customLabour || []).filter(r => !isEmptyCustomRow(r)).map(r => ({ ...r, effHours: customLabourHours(r) }));
  // Custom items add labour only: they don't run the blaster, so they're kept
  // out of machine hours (fuel/electricity, equipment wear) and dry ice.
  const customHrs = customItems.reduce((n, r) => n + r.effHours, 0);
  labourHrs += customHrs;

  // Add-ons
  state.addons.forEach(key => {
    const addon = AUTO_ADDONS[key];
    if(!addon) return;
    labourHrs += addon.labourHrs;
  });

  // Concierge
  const conciergeHrs = state.concierge ? 1.5 : 0;
  labourHrs += conciergeHrs;

  const adjDryIce = dryIceKg * (1 + rates.wastage/100);
  // In-unit (mains power): no generator diesel; electricity per compressor-hour
  // instead, over the same hours fuel would have run. One compressor.
  const inUnit = state.jobLocation === "in-unit";
  // Hours the blaster and compressor actually run: the services' blasting
  // time only. Custom labour, protection, add-ons and concierge are labour
  // only (no electricity/fuel or equipment), as are services' non-blasting hours.
  const addonHrs = state.addons.reduce((n, k) => n + (AUTO_ADDONS[k]?.labourHrs || 0), 0);
  const machineHrs = labourHrs - customHrs - protHrs - addonHrs - conciergeHrs - nonBlastHrs;
  const fuelLitres = inUnit ? 0 : machineHrs * fuelBurnLHr;
  const electricityCost = inUnit ? machineHrs * (rates.electricityPerHr ?? DEFAULT_ELECTRICITY_PER_HR) : 0;

  // Travel time is billed as labour (crew get paid for the drive), separate
  // from the £/mile travelCost below which only covers fuel/vehicle running
  // costs. Derived from the same rates.travelMiles used for mileage, at an
  // assumed average road speed. It does NOT feed dry ice/fuel/equipment
  // hours (labourHrs stays on-site-only for those), since none of that runs
  // while the vehicle is in transit.
  const travelTimeHrs = (rates.travelMiles||0) > 0 ? (rates.travelMiles||0) / (rates.avgTravelSpeed||40) : 0;
  const totalChargeableHrs = labourHrs + travelTimeHrs;
  const onSiteLabourCost = labourHrs * rates.labourRate * rates.crewSize;
  const travelTimeCost = travelTimeHrs * rates.labourRate * rates.crewSize;
  const labourCost = onSiteLabourCost + travelTimeCost;
  // £/kg + delivery and hire per bin, or a delivered-tier supplier's tiers;
  // the saved figure while amending, until the kg or supplier pick changes.
  const autoBinCapacity=diData?.binCapacityKg||0;
  const diCharge=dryIceCharge(diData?.supplier,adjDryIce,autoBinCapacity,diData?.saved,diData?.pick);
  const autoBinsNeeded=diCharge.binsNeeded;
  const autoBinHire=diCharge.binHire;
  const dryIceCost = diCharge.cost;
  const fuelCost = fuelLitres * rates.dieselPrice;
  const materialsCost = (prot?.materials || 0) * protScale + state.addons.reduce((s,k)=>s+(AUTO_ADDONS[k]?.materials||0),0)
    + customItems.reduce((n, r) => n + (Number(r.materials) || 0), 0);
  // Per-item hours and labour cost for the internal price build-up.
  const customLabourLines = customItems.map(r => ({ label: r.label || "Custom labour", hours: r.effHours, labourCost: r.effHours * rates.labourRate * rates.crewSize, materials: Number(r.materials) || 0 }));
  const conciergeFlat = state.concierge ? rates.conciergeFlat : 0;
  const conciergeTravel = state.concierge ? (parseFloat(state.conciergeMiles)||0) * rates.conciergePerMile * (state.conciergeReturn?2:1) : 0;
  const machineRecovery = (rates.machinePerHr + rates.compressorPerHr) * machineHrs;

  const travelCost = (rates.travelMiles||0) * (rates.mileRate||0.45);
  const detailingCost = state.detailing ? state.detailing.reduce((s,k)=>s+(AUTO_DETAILING[k]?.clientPrice||0),0) : 0;
  const totalCost = labourCost + dryIceCost + fuelCost + electricityCost + materialsCost + conciergeFlat + conciergeTravel + machineRecovery + travelCost + detailingCost;

  return {
    customLabourLines, protScale, protHrs,
    labourHrs, travelTimeHrs, totalChargeableHrs, onSiteLabourCost, travelTimeCost, dryIceKg, adjDryIce, fuelLitres,
    labourCost, dryIceCost, fuelCost, electricityCost, inUnit, materialsCost,
    conciergeFlat, conciergeTravel, machineRecovery,
    travelCost, detailingCost, totalCost,
    autoBinsNeeded, autoBinHire, autoBinCapacity, diCharge,
  };
}

function AutomotiveTab({ onSaveToLog, onQuoteSaved, initialState, amendmentMeta, onAmendmentConsumed, onNew, onDraftChange, readOnly }){
  const[isMobile]=useState(()=>window.innerWidth<768);
  // Lazy initializer - see the matching comment in IndustrialCalculator.
  // Estimator defaults to whoever is signed in (new quotes, and reopened
  // quotes saved without one), so it's never left blank on the quote.
  const{user:signedInUser}=useUser();
  const[job,setJob]=useState(()=>readOnly&&initialState?.job?initialState.job:withDefaultEstimator(initialState?.job || {quoteRef:getNextQuoteRef('AUT'), estimator:"", workType:"One-Off Preservation", jobDate:"", jobLocation:"in-unit"},signedInUser));
  const[client,setClient]=useState(initialState?.client || {name:"", email:"", phone:"", address:"", postcode:""});
  const[vehicle,setVehicle]=useState(initialState?.vehicle || {reg:"", make:"", model:"", year:"", colour:"", vehicleType:"Modern / Daily"});

  
  const[services,setServices]=useState(initialState?.services || []);
  // Ticking a package unticks what it already includes (see
  // AUTO_SERVICE_INCLUDES), with a short note saying so.
  const[serviceNote,setServiceNote]=useState("");
  const toggleService=key=>{
    if(services.includes(key)){ setServices(s=>s.filter(k=>k!==key)); setServiceNote(""); return; }
    const inc=AUTO_SERVICE_INCLUDES[key]||[];
    const incAddons=AUTO_ADDON_INCLUDES[key]||[];
    const removed=[...services.filter(k=>inc.includes(k)).map(k=>AUTO_SERVICE_HOURS[k]?.label),...addons.filter(k=>incAddons.includes(k)).map(k=>AUTO_ADDONS[k]?.label)];
    setServices(s=>[...s.filter(k=>!inc.includes(k)),key]);
    setAddons(a=>a.filter(k=>!incAddons.includes(k)));
    setServiceNote(removed.length?`${removed.join(", ")} unticked: already included in ${AUTO_SERVICE_HOURS[key].label}.`:"");
  };
  const[contamination,setContamination]=useState(initialState?.contamination || "Moderate");
  const[protection,setProtection]=useState(initialState?.protection || "none");
  // Choosing a protection system that includes an add-on (Dinitrol: cavity
  // wax) unticks that add-on, with a short note, like the service overlaps.
  const[protectionNote,setProtectionNote]=useState("");
  const chooseProtection=key=>{
    const inc=AUTO_PROTECTION_ADDON_INCLUDES[key]||[];
    const removed=addons.filter(k=>inc.includes(k)).map(k=>AUTO_ADDONS[k]?.label);
    setProtection(key);
    if(removed.length) setAddons(a=>a.filter(k=>!inc.includes(k)));
    setProtectionNote(removed.length?`${removed.join(", ")} unticked: already included in ${AUTO_PROTECTION[key].label}.`:"");
  };
  const[addons,setAddons]=useState(initialState?.addons||[]);
  const[customLabour,setCustomLabour]=useState(initialState?.customLabour||[]);
  const updateCustomLabour=(id,patch)=>setCustomLabour(rows=>rows.map(r=>r.id===id?{...r,...patch}:r));
  const[detailing,setDetailing]=useState(initialState?.detailing||[]);

  const[concierge,setConcierge]=useState(initialState?.concierge||false);
  // Saved with the quote (they change the price) and restored on reopen.
  const[conciergeMiles,setConciergeMiles]=useState(initialState?.conciergeMiles??"");
  const[conciergeReturn,setConciergeReturn]=useState(initialState?.conciergeReturn??true);

  const[machineType,setMachineType]=useState(initialState?.machineType||"Champ Vario");

  // Only the market/company-rate fields persist as defaults across quotes -
  // travelMiles and the two dry-ice/fuel overrides are per-quote and must always
  // start fresh, so they're listed here but excluded from RATES_PERSIST_KEYS.
  // The dry-ice supplier pick is per quote too: new quotes start on the
  // Primary supplier (Admin → Suppliers).
  const RATES_PERSIST_KEYS = ['labourRate','crewSize','binCapacityLabel','wastage','dieselPrice','electricityPerHr','machinePerHr','compressorPerHr','conciergeFlat','conciergePerMile','mileRate','avgTravelSpeed'];
  const[rates,setRates]=useState(()=>{ migrateAutoLabourDefault(); return initialState?.rates || loadPersistedDefaults('sublime_auto_rates_defaults', {
    labourRate:AUTO_DEFAULT_LABOUR_RATE, crewSize:1,
    dryIceSupplierId:null, binCapacityLabel:null, wastage:10,
    dieselPrice:1.90, electricityPerHr:DEFAULT_ELECTRICITY_PER_HR,
    dryIceKgHrOverride:null, fuelBurnOverride:null,
    machinePerHr:1.70, compressorPerHr:2.80,
    conciergeFlat:75, conciergePerMile:1.50,
    travelMiles:0, mileRate:0.45, avgTravelSpeed:40,
  }, RATES_PERSIST_KEYS); });
  // Remember only the rates changed here, on top of the stored ones. Opening
  // a saved quote or draft (its own rates, e.g. an old £45/h quote) no longer
  // overwrites the defaults for the next new quote just by being opened.
  const openedRates=useRef(rates);
  React.useEffect(()=>{
    if(readOnly) return;
    const changed=RATES_PERSIST_KEYS.filter(k=>rates[k]!==openedRates.current[k]);
    if(!changed.length) return;
    const stored=loadPersistedDefaults('sublime_auto_rates_defaults', {}, RATES_PERSIST_KEYS);
    persistDefaults('sublime_auto_rates_defaults', {...stored,...Object.fromEntries(changed.map(k=>[k,rates[k]]))}, RATES_PERSIST_KEYS);
  },[rates]); // eslint-disable-line react-hooks/exhaustive-deps
  const autoManualMiles=manualMilesSetOf(rates.manualMiles)?Number(rates.manualMiles):null;

  const[commercial,setCom]=useState(()=>restoreCommercial(initialState?.commercial,{
    targetMargin:defaultMarginFor("in-unit"), marginSet:false, contingency:5, minJobPrice:0, roundPrice:"nearest £50",
    discountType:"none", discountValue:0, discountTargetPrice:null, discountReason:"",
  }));

  const[notes,setNotes]=useState(()=>initialNotes(initialState));
  const[vehicleUse,setVehicleUse]=useState(initialState?.vehicleUse||{primaryUse:"Weekend / Leisure",storage:"Garaged",knownCorrosion:"None visible",previousProtection:"Unknown",referralSource:""});
  const[advMode,setAdvMode]=useState(false);
  const[pdfLoading,setPdfLoading]=useState(false);
  // True while Print/Save PDF is saving; Save to Log waits for it.
  const saveBusy=useRef(false);
  // Save ID for the PDF button's save, kept until it succeeds so a retry
  // can't add a second row (see writeWithSaveCheck in quoteLogger).
  const pdfSaveId=useRef(null);
  const[pdfSaveChecking,setPdfSaveChecking]=useState(false); // "Still saving…" banner for the PDF button's save
  const[savedNotice,setSavedNotice]=useState(null); // ref the PDF button just saved (SavedBanner)
  const[pdfAsk,setPdfAsk]=useState(false); // SavePdfDialog open (PDF on an unsaved quote)
  // Set when Download PDF saves this quote: its real number + what it looked
  // like, so the field can show the number and a repeat click on an
  // unchanged quote reprints it instead of saving another row.
  const[savedRef,setSavedRef]=useState(initialState?._savedRef||null);
  // Once this calculator has saved a quote, it keeps editing that quote:
  // any later save is an amendment of it (…a, …b), never a new number.
  const[savedAsBase,setSavedAsBase]=useState(initialState?._savedAsBase||null);
  // Set when this calculator was opened with "Copy as new quote". Part of the
  // calculator's state (from initialState._copyOf), so anything that restores
  // that state restores the copy too. Never saved to the sheet.
  const[copyOf]=useState(initialState?._copyOf||"");
  // Test quote flag (column Y): from an amended quote or a draft, and set by
  // the save dialog's tickbox.
  const[testQuote,setTestQuote]=useState(initialState?._testQuote??IS_PREVIEW_DEPLOYMENT);
  useAutoTestQuote(client.email,testQuote,setTestQuote);
  const amendOf=savedAsBase||amendmentMeta?.originalNumber||"";
  // Provisional number the next save will get (server confirms it).
  const amendNext=savedAsBase?nextAmendmentNumber(savedAsBase):job.quoteRef;
  // Mobile: the sticky bar's Preview button scrolls down to the live quote.
  const previewRef=useRef(null);
  const goToPreview=()=>previewRef.current?.scrollIntoView({behavior:"smooth",block:"start"});

  // Scope is rebuilt from the ticked services (plus a line naming the
  // protection system, if one is selected) unless it's locked as custom (see
  // CLIENT SCOPE LOCK). Runs whenever the services or protection change - not
  // on reopening a saved quote, which keeps its scope.
  const scopeTemplate=buildAutoScopeBody(services,addons,protection);
  // An older saved scope is kept word for word, so nothing is appended to it.
  // Numbered sections (Clean, Rust treatment, Protection, Additional work,
  // Add-ons - see composeAutoScope); an older saved scope stays word for word.
  const scopeBody=notes.scopeCustom?notes.scopeCustomText:scopeTemplate;
  const scopeSectioned=!notes.scopeLegacy&&!!services.length;
  const composedScope=scopeSectioned
    ?composeAutoScope({body:scopeBody,state:{services,addons,protection,customLabour,concierge,detailing},extra:notes.scopeExtra})
    :composeScope({body:scopeBody,extra:notes.scopeExtra});
  // Sections added after the template, named for the editor's note.
  const scopeAutoSections=scopeSectioned?(composedScope.match(/^\d+\. [^\n]+:$/mg)||[]).slice(1).map(h=>h.replace(/^\d+\. |:$/g,"")):[];
  const[showSaveModal,setShowSaveModal]=useState(false);
  // Remote travel
  const[includeTravel,setIncludeTravel]=useState(()=>(Number(initialState?.rates?.travelMiles)||0)>0);
  const[fromPC,setFromPC]=useState("TR27 4QZ");
  const[toPC,setToPC]=useState("");
  // Was watching job.postcode, which Automotive never had an input for (a from-
  // base service has no site postcode) - client.postcode is the one that
  // actually matters here, for pricing a future collection/concierge service.
  React.useEffect(()=>{ if(client.postcode){ setToPC(client.postcode); } else { setToPC(""); } },[client.postcode]);
  const[calcMiles,setCalcMiles]=useState(null);
  const[returnTrip,setReturnTrip]=useState(()=>savedReturnTrip(initialState?.rates));
  const[mileLoading,setMileLoading]=useState(false);
  const[mileError,setMileError]=useState("");

  const toggleArr=(arr,setArr,val)=>{
    setArr(a=>a.includes(val)?a.filter(x=>x!==val):[...a,val]);
  };

  const calculateMileage=async()=>{
    if(!fromPC||!toPC)return;
    setMileLoading(true);setMileError("");
    try{
      setFromPC(formatPostcode(fromPC));setToPC(formatPostcode(toPC));
      const[a,b]=await Promise.all([lookupPostcode(fromPC,"From"),lookupPostcode(toPC,"To")]);
      if(a.result&&b.result){
        const{latitude:lat1,longitude:lon1}=a.result;
        const{latitude:lat2,longitude:lon2}=b.result;
        const R=3958.8,dLat=(lat2-lat1)*Math.PI/180,dLon=(lon2-lon1)*Math.PI/180;
        const x=Math.sin(dLat/2)**2+Math.cos(lat1*Math.PI/180)*Math.cos(lat2*Math.PI/180)*Math.sin(dLon/2)**2;
        const miles=Math.round(R*2*Math.atan2(Math.sqrt(x),Math.sqrt(1-x))*1.3);
        setCalcMiles(miles);
        setRates(r=>({...r,travelMiles:returnTrip?miles*2:miles}));
      }else{setMileError([a.error,b.error].filter(Boolean).join(" "));}
    }catch{setMileError("Could not calculate distance");}
    setMileLoading(false);
  };

  const state={
    services, contamination, protection, addons, detailing,
    concierge, conciergeMiles, conciergeReturn,
    vehicleType:vehicle.vehicleType, machineType,
    jobLocation:job.jobLocation,
    customLabour,
  };

  const{dryIceSuppliers,selectedSupplier:diSupplier,primary:diPrimary}=useDryIceSupplier(rates.dryIceSupplierId);
  const diSaved=React.useMemo(()=>savedDryIce(initialState,initialState?.rates),[initialState]);
  const diBinOptions=diSupplier?.binCapacities||[];
  const diSelectedBin=diBinOptions.find(b=>b.label===rates.binCapacityLabel)||diBinOptions[0]||null;
  const diPricePerKg=Number(diSupplier?.currentCostPricePerKg)||0;
  const diDeliveryPerBin=Number(diSupplier?.deliverySurchargePerBin)||0;
  const diBinRentalPerBin=Number(diSupplier?.binRentalPerBin)||0;
  const diData={supplier:diSupplier,binCapacityKg:diSelectedBin?.capacity_kg||0,saved:diSaved,pick:`${rates.dryIceSupplierId||""}|${rates.binCapacityLabel||""}`};

  const calc = calcAuto(state, rates, diData);
  const autoBinSpec={capacity:diSelectedBin?.capacity_kg||0,hire:diBinRentalPerBin};
  const autoBinsNeeded=calc.autoBinsNeeded;
  const autoBinHire=calc.autoBinHire;
  const m = commercial.targetMargin/100;
  const basePrice = m<1 ? calc.totalCost/(1-m) : calc.totalCost;
  const afterCont = basePrice * (1 + commercial.contingency/100);
  // Snapshot of the internal cost build-up, saved into stateJson alongside
  // the raw inputs so QuoteLog's detail panel can show it later without
  // re-running this component's pricing logic against possibly-changed
  // formulas — it reflects exactly what was charged at save time. Automotive
  // has no separate Risk stage (Industrial does), so there's no riskPct/
  // afterRisk here.
  const costBreakdown={customLabour:calc.customLabourLines,labourCost:calc.onSiteLabourCost,travelTimeCost:calc.travelTimeCost,fuelCost:calc.fuelCost,electricityCost:calc.electricityCost,dryIceCost:calc.dryIceCost,materialsCost:calc.materialsCost,conciergeCost:calc.conciergeFlat+calc.conciergeTravel,detailingCost:calc.detailingCost,travelCost:calc.travelCost,equipmentRecoveryCost:calc.machineRecovery,totalCost:calc.totalCost,basePrice,contingencyPct:commercial.contingency,afterCont,dryIceKgRequired:calc.dryIceKg,dryIceKgAdjusted:calc.adjDryIce};
  let finalPrice = afterCont;
  if(commercial.roundPrice==="nearest £50") finalPrice=Math.ceil(finalPrice/50)*50;
  if(commercial.roundPrice==="nearest £100") finalPrice=Math.ceil(finalPrice/100)*100;
  if(commercial.minJobPrice>0) finalPrice=Math.max(finalPrice,commercial.minJobPrice);
  const subtotalExVat=finalPrice;
  const{finalPrice:discountedPrice,discountAmount,discountLabel,discountReason}=applyDiscount(subtotalExVat,commercial);
  finalPrice=discountedPrice;
  const grossProfit = finalPrice - calc.totalCost;
  const grossMargin = finalPrice>0 ? (grossProfit/finalPrice*100) : 0;
  const marginColor = marginColorFor(grossMargin,job.jobLocation||"on-site");
  // What the price panel shows: the live figures, or in a read-only view the
  // figures as saved (see asSaved).
  const sv=(()=>{
    const live={...calc,basePrice,afterCont,finalPrice,subtotalExVat,discountAmount,discountLabel,discountReason,grossProfit,grossMargin};
    if(!readOnly) return {...live,marginColor};
    const v=asSaved(live,initialState,AUTO_SAVED_KEYS);
    if(initialState?.costBreakdown?.conciergeCost!=null) v.conciergeTravel=0; // saved as one figure
    return {...v,marginColor:marginColorFor(v.grossMargin,job.jobLocation||"on-site")};
  })();

  // See IndustrialCalculator for why "Target final price" needs a snapshot.
  const commercialForSave=discountAmount>0?{...commercial,_discountSnapshot:{amount:discountAmount,subtotalExVat,label:discountLabel,reason:discountReason}}:commercial;
  // Everything a saved quote needs to be reopened and priced exactly the same
  // - the ONE definition every save path uses (PDF, both Save dialogs), so a
  // field can't be saved by one button and silently dropped by another.
  const stateForSave={job,client,vehicle,vehicleUse,services,contamination,protection,addons,customLabour:customLabour.filter(r=>!isEmptyCustomRow(r)),detailing,concierge,conciergeMiles,conciergeReturn,machineType,rates,commercial:commercialForSave,notes:{...notes,clientScope:composedScope},totalChargeableHrs:calc.totalChargeableHrs,costBreakdown,...(amendmentMeta?.basedOn?{amendedFrom:amendmentMeta.basedOn}:{})};
  // Live client quote under the form (see IndustrialCalculator).
  const currentSnap=quoteSnapshot(stateForSave);
  const savedAndUnchanged=matchesSaved(savedRef,currentSnap,finalPrice);
  // Opened via Amend: the quote as loaded counts as already saved, so saving
  // it without a change creates nothing. Taken once the mount effects have
  // settled; the total is the one on the sheet.
  const snapRef=useRef("");
  snapRef.current=currentSnap;
  React.useEffect(()=>{
    // Not when starting from an earlier version: restoring it IS a change.
    if(!amendmentMeta?.originalNumber||amendmentMeta?.basedOn||!initialState||initialState._fromDraft) return;
    const id=setTimeout(()=>setSavedRef({number:amendmentMeta.originalNumber,snap:snapRef.current,total:initialState._savedTotalExVat??NaN}),0);
    return ()=>clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[]);
  const flushDraft=useCalcAutosave("automotive",initialState,currentSnap,()=>({
    state:{...stateForSave,_savedTotalExVat:initialState?._savedTotalExVat,_testQuote:testQuote},
    amendOf:amendmentMeta?.originalNumber||"",basedOn:amendmentMeta?.basedOn||"",startsAsDraft:!!amendmentMeta?.startsAsDraft,savedRef,savedAsBase,copyOf,
    // For the Quote Log's drafts strip.
    dirty:!savedAndUnchanged,
    summary:[client.name||"No client yet",[vehicle.make,vehicle.model].filter(Boolean).join(" ")||vehicle.vehicleType].filter(Boolean).join(", ")+(amendOf?` (editing ${amendOf})`:copyOf?` (copy of ${copyOf})`:""),
  }),onDraftChange,!!readOnly);
  // Start new quote: write the draft first so the "unsaved work?" check is
  // current, then let the app ask and clear.
  const startNew=()=>{ flushDraft(); if(onNew) onNew(); };
  const quotePreview=<QuotePreview category="Automotive" state={stateForSave} totalExVat={sv.finalPrice} quoteNumber={readOnly?readOnly.ref:(savedAndUnchanged?savedRef.number:"")} t={t}/>;
  // Save to Log / Save & Email: the calculator keeps what was entered, now
  // marked as saved, and the app opens the Quote Log on the new row.
  const markSaved=saved=>{
    setShowSaveModal(false);
    if(onAmendmentConsumed) onAmendmentConsumed();
    if(onSaveToLog) onSaveToLog();
    if(saved?.quoteNumber){ setSavedRef({number:saved.quoteNumber,snap:currentSnap,total:finalPrice}); setSavedAsBase(saved.quoteNumber); setJob(j=>({...j,quoteRef:saved.quoteNumber})); setTestQuote(!!saved.testQuote); }
    if(onQuoteSaved) onQuoteSaved(saved?.quoteNumber,saved?.sendNote,!!saved?.unchanged);
  };

  const macPreset = AUTO_MACHINE_PRESETS[machineType] || AUTO_MACHINE_PRESETS["Champ Vario"];
  const selectedServicesLabel = services.map(k=>AUTO_SERVICE_HOURS[k]?.label).filter(Boolean).join(", ")||"—";

  // Only the pricing input actually needed to produce a sane quote is
  // required - deliberately not Client Name, so a draft can be roughed out
  // on pricing before a client is attached.
  const requiredFieldsMissing=()=>{
    if(services.length===0){ alert("Select at least one service before generating a quote."); return true; }
    if(customLabour.some(isBlankPricedCustomRow)){ alert("A custom labour item has hours or materials but no description. Add a description (it's what the client sees on the quote) or remove the item."); return true; }
    return false;
  };
  const handleOpenSaveModal=()=>{ if(saveBusy.current)return; if(requiredFieldsMissing())return; setShowSaveModal(true); };

  // ── PDF ──────────────────────────────────────────────────────────────────
  const printJobSheet=()=>openJobSheet({
    heading:`Automotive job${[vehicle.make,vehicle.model].filter(Boolean).length?`, ${[vehicle.year,vehicle.make,vehicle.model].filter(Boolean).join(" ")}`:""}`,
    refNo:jobSheetRef(savedAndUnchanged?savedRef:null,amendOf),
    dateStr:quoteDateStr(null),
    sections:[
      {title:"Job",rows:[["Estimator",job.estimator],["Work type",job.workType],["Scheduled date",sheetDate(job.jobDate)],["Job location",calc.inUnit?"In-unit (mains power)":"On-site (generator)"]]},
      {title:"Client & vehicle",rows:[["Client",client.name],["Phone",client.phone],["Email",client.email],["Address",[client.address,client.postcode].filter(Boolean).join(", ")],["Vehicle",[vehicle.year,vehicle.make,vehicle.model].filter(Boolean).join(" ")],["Registration",vehicle.reg?String(vehicle.reg).toUpperCase():""],["Colour",vehicle.colour],["Type",vehicle.vehicleType],["Contamination",contamination]]},
      {title:"Work",rows:[["Services",selectedServicesLabel],["Protection",protection&&protection!=="none"?AUTO_PROTECTION[protection]?.label:""],["Add-ons",autoAddonsSummary(addons,services,protection)],["Detailing",detailing.map(k=>AUTO_DETAILING[k]?.label).filter(Boolean).join(", ")],["Concierge",concierge?`${conciergeMiles||0} miles${conciergeReturn?", return":", one way"}`:""],["Custom labour",(calc.customLabourLines||[]).map(l=>`${l.label.length>60?l.label.slice(0,59)+"…":l.label} (${fmtN(l.hours,1)}h)`).join("; ")]]},
      {title:"Time & materials",rows:[["Labour",hrs(calc.labourHrs)],["Operatives",rates.crewSize],["Machine",machineType],["Dry ice (inc. wastage)",`${fmtN(calc.adjDryIce)} kg`],[calc.inUnit?"Electricity":"Fuel",calc.inUnit?fmt(calc.electricityCost):`${fmtN(calc.fuelLitres)} L`],["Travel",(Number(rates.travelMiles)||0)>0?`${Math.round(rates.travelMiles)} miles`:""]]},
    ],
    blocks:[{title:"Client scope",text:composedScope,html:scopeTextToHTML(composedScope)},{title:"Estimator notes",text:notes.estimator},{title:"Internal notes",text:notes.internal},{title:"Exclusions",text:notes.exclusions}],
  });

  // confirmed: true from the SavePdfDialog's "Save and download". Anything
  // else (the PDF button's click event) asks first when this would save.
  const downloadPDF=async(confirmed)=>{
    // One PDF save at a time: a second click while the first is saving does
    // nothing (the ref catches clicks faster than the button greys out).
    if(saveBusy.current)return;
    if(requiredFieldsMissing())return;
    if(confirmed!==true&&!matchesSaved(savedRef,currentSnap,finalPrice)){ setPdfAsk(true); return; }
    setPdfAsk(false);
    saveBusy.current=true;
    // Opened synchronously, before any await, so it's still tied to this
    // click for popup-blocker purposes - the HTML gets written into it once
    // the async work below finishes.
    const printWin=window.open('','_blank');
    if(!printWin){ saveBusy.current=false; alert("Please allow pop-ups for this site to print/save the quote as a PDF."); return; }
    setPdfLoading(true);
    try{
    // Save to the quote log first, same as the "Save to Log" button, so the
    // quote can still be found and edited afterward.
    let finalQuoteNumber=job.quoteRef;
    const snap=currentSnap;
    const alreadySaved=matchesSaved(savedRef,snap,finalPrice);
    let justSaved=false;
    if(alreadySaved) finalQuoteNumber=savedRef.number;
    if(!alreadySaved) try{
      await ensureHeaders();
      const amendmentOf=amendOf;
      const logData={
        quoteNumber:job.quoteRef,
        dateCreated:new Date().toLocaleDateString("en-GB"),
        clientName:client.name||"",
        clientEmail:client.email||"",
        clientPhone:client.phone||"",
        category:"Automotive",
        application:selectedServicesLabel,
        siteAddress:"",
        status:"Draft", // new quotes and amendments alike: Draft until actually sent (the amended quote keeps its own status)
        totalExVat:finalPrice,
        totalIncVat:finalPrice*1.2,
        margin:commercial.targetMargin,
        notes:"",
        amendmentOf,
        testQuote,
        pdfGenerated:true,
        emailSent:false,
        scopeSummary:composedScope,
        stateJson:stateForSave,
          saveId:pdfSaveId.current||(pdfSaveId.current=newSaveId()),
      };
      if(amendmentOf){
        // job.quoteRef here is only the provisional number shown while
        // editing - the Apps Script assigns the real one, locked, from the
        // sheet's live state (see appendAmendment doc comment).
        const result=await appendAmendment(logData,{onSlow:()=>setPdfSaveChecking(true)}); pdfSaveId.current=null;
        if(result.quoteNumber){
          finalQuoteNumber=result.quoteNumber;
          if(result.quoteNumber!==job.quoteRef)setJob(j=>({...j,quoteRef:result.quoteNumber}));
        }
        setSavedRef({number:finalQuoteNumber,snap,total:finalPrice}); setSavedAsBase(finalQuoteNumber); justSaved=true;
        if(onAmendmentConsumed)onAmendmentConsumed();
      }else{
        // New quotes are numbered by the Apps Script too (see appendQuote).
        const result=await appendQuote(logData,{onSlow:()=>setPdfSaveChecking(true)}); pdfSaveId.current=null;
        if(result.quoteNumber){
          finalQuoteNumber=result.quoteNumber;
          if(result.quoteNumber!==job.quoteRef)setJob(j=>({...j,quoteRef:result.quoteNumber}));
        }
        setSavedRef({number:finalQuoteNumber,snap,total:finalPrice}); setSavedAsBase(finalQuoteNumber); justSaved=true;
      }
    }catch(logErr){
        console.error("Failed to save quote to log:",logErr);
        // Never print a provisional number as if it were real.
        finalQuoteNumber="Draft (not saved)";
        // The printable copy still opens either way (see below), which
        // otherwise makes a failed save invisible - nothing else here
        // would tell you the sheet was never actually updated.
        alert("Warning: this quote could not be saved to the log ("+logErr.message+"). The printable copy will still open, but Save to Log / Quote Printed status won't reflect this attempt until you save it manually.");
      }

    let logoSrc="/logo-dark.png";
    try{
      const resp=await fetch("/logo-dark.png");
      const blob=await resp.blob();
      logoSrc=await new Promise(res=>{
        const r=new FileReader();
        r.onload=()=>res(r.result);
        r.readAsDataURL(blob);
      });
    }catch(e){}

    // The one client-facing Automotive quote (shared with View Full Quote),
    // built from exactly what was saved. If the save failed, finalQuoteNumber
    // is "Draft (not saved)".
    const html=buildAutomotiveQuoteHTML({
      refNo:finalQuoteNumber,
      dateStr:quoteDateStr(null),
      totalExVat:finalPrice,
      discount:discountAmount>0?{subtotalExVat,amount:discountAmount,label:discountLabel,reason:discountReason}:null,
      state:stateForSave,
      logoSrc,
      company:await loadCompanySettings(),
    });

    printQuoteDocument(printWin,html);
    // Saved just now (not a reprint): say so here; the calculator stays
    // open (the Quote Log only on ← Quote Log).
    if(justSaved) setSavedNotice(finalQuoteNumber);
    }catch(e){console.error(e);alert("Something went wrong generating the quote: "+e.message);try{printWin.close();}catch(_){}}
    setPdfLoading(false); setPdfSaveChecking(false);
    saveBusy.current=false;
  };

  // The ref shown in the top bar, Live Summary and phone bar.
  const refInfo=calcRef({readOnly,savedAndUnchanged,savedRef,amendOf,amendNext});
  const refProps={refInfo,test:testQuote,client:(client.name||"").trim()};
  const refLine=<RefChip info={refInfo} test={testQuote}/>;
  // ── Summary Panel ─────────────────────────────────────────────────────────
  const summaryPanel=(
    <div>
      <PriceHero finalPrice={sv.finalPrice} grossMargin={sv.grossMargin} grossProfit={sv.grossProfit} marginColor={sv.marginColor}/>
      <SummaryBlocks blocks={[
        {title:"Time & Resources",rows:[
          ["Labour Hours",`${fmtN(sv.labourHrs)} hrs`],
          ...(sv.travelTimeHrs>0?[["Travel Time",`${fmtN(sv.travelTimeHrs)} hrs`]]:[]),
          ["Total Chargeable",`${fmtN(sv.totalChargeableHrs)} hrs`,true],
          ["Dry Ice (adj.)",`${fmtN(sv.adjDryIce)} kg`],
          ...(sv.inUnit?[]:[["Fuel",`${fmtN(sv.fuelLitres)} L`]]),
        ]},
        {title:"Cost Breakdown",rows:[
          ["Labour",fmt(sv.onSiteLabourCost)],
          // Custom labour items, included in Labour above (and materials in Materials).
          ...(sv.customLabourLines||[]).map((l,i)=>[`↳ ${i+1}. ${l.label.length>34?l.label.slice(0,33)+"…":l.label} (${fmtN(l.hours,1)}h)`,fmt(l.labourCost)]),
          ...(sv.travelTimeCost>0?[["Travel Time (labour)",fmt(sv.travelTimeCost)]]:[]),
          ["Dry Ice",fmt(sv.dryIceCost)],
          sv.inUnit?["Electricity (mains)",fmt(sv.electricityCost)]:["Fuel",fmt(sv.fuelCost)],
          ...(sv.materialsCost>0?[["Materials",fmt(sv.materialsCost)]]:[]),
          ...(sv.machineRecovery>0?[["Equipment",fmt(sv.machineRecovery)]]:[]),
          ...((sv.conciergeFlat+sv.conciergeTravel)>0?[["Concierge",fmt(sv.conciergeFlat+sv.conciergeTravel)]]:[]),
          ...(sv.travelCost>0?[["Travel (fuel/vehicle)",fmt(sv.travelCost)]]:[]),
          ...(sv.detailingCost>0?[["Detailing",fmt(sv.detailingCost)]]:[]),
          ["Total Cost",fmt(sv.totalCost),true,"warn"],
        ]},
        {title:"Price Build-Up",rows:[
          ["Base Price",fmt(sv.basePrice)],
          [`Contingency (+${commercial.contingency}%)`,fmt(sv.afterCont)],
          ...(sv.discountAmount>0?[[`Discount${sv.discountReason?` (${sv.discountReason})`:sv.discountLabel?` (${sv.discountLabel})`:""}`,`-${fmt(sv.discountAmount)}`]]:[]),
          ["Final Price",fmt(sv.finalPrice),true],
        ]},
      ]}/>
    </div>
  );
  // ── Form ──────────────────────────────────────────────────────────────────
  const formView=(
    <ReadOnlyForm ro={readOnly}>
      {initialState?._fromDraft&&<DraftRestoredBanner at={initialState._draftAt}/>}
      {amendOf&&(savedAndUnchanged?<NoChangesBanner number={savedRef.number}/>:<AmendBanner amendOf={amendOf} next={amendNext}/>)}
      {copyOf&&!amendOf&&<CopyBanner copyOf={copyOf}/>}
      {initialState&&<RecalcCheckBanner savedTotal={initialState._savedTotalExVat} currentTotal={finalPrice} savedCb={initialState.costBreakdown} currentCb={costBreakdown} t={t}/>}
      {initialState&&<DiscountBanner amount={discountAmount} label={discountLabel} reason={discountReason} t={t}/>}
      <Section num="1" title="Job Details">
        <Card>
          <div className="sp-grid" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
            <Field label="Quote Reference"><QuoteRefField number={readOnly?readOnly.ref:(savedAndUnchanged?savedRef.number:"")} amending={amendOf}/></Field>
            <Field label="Work Type">
              <select style={sel} value={job.workType} onChange={e=>setJob(j=>({...j,workType:e.target.value}))}>
                {["One-Off Preservation","Concours Preparation","Pre-Sale / Auction Presentation","Restoration Project","Race / Rally Prep","Recommissioning","Maintenance Contract"].map(o=><option key={o}>{o}</option>)}
              </select>
            </Field>
            {/* Saved quotes from before this field existed have no jobLocation - shown and priced as on-site. */}
            <Field label="Job Location"><select style={sel} value={job.jobLocation||"on-site"} onChange={e=>{const loc=e.target.value;setJob(j=>({...j,jobLocation:loc}));setCom(c=>c.marginSet?c:{...c,targetMargin:defaultMarginFor(loc)});}}>{JOB_LOCATIONS.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}</select></Field>
            <Field label="Scheduled Date (optional)"><input type="date" style={inp} value={job.jobDate} onChange={e=>setJob(j=>({...j,jobDate:e.target.value}))}/><JobDateClash date={job.jobDate} quoteNumber={amendOf||job.quoteRef} test={testQuote}/></Field>
            <Field label="Estimator Name"><input style={inp} value={job.estimator} onChange={e=>setJob(j=>({...j,estimator:e.target.value}))} placeholder="Your name"/></Field>
          </div>
        </Card>
      </Section>

      <Section num="2" title="Client & Vehicle">
        <Card>
          <Divider label="Client"/>
          <div className="sp-grid" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
            <Field label="Client Name"><NoAutofillInput style={inp} name="sp-client-name" value={client.name} onChange={e=>setClient(c=>({...c,name:e.target.value}))} placeholder="Full name"/></Field>
            <Field label="Email"><NoAutofillInput style={inp} name="sp-client-email" value={client.email} onChange={e=>setClient(c=>({...c,email:e.target.value}))} placeholder="email@example.com"/></Field>
            <Field label="Phone"><NoAutofillInput style={inp} name="sp-client-phone" value={client.phone} onChange={e=>setClient(c=>({...c,phone:e.target.value}))} placeholder="07xxx xxxxxx"/></Field>
            <Field label="Postcode"><NoAutofillInput style={inp} name="sp-client-postcode" value={client.postcode} onChange={e=>setClient(c=>({...c,postcode:e.target.value.toUpperCase()}))} placeholder="EC1A 1BB"/></Field>
          </div>
          <Field label="Client Address"><NoAutofillInput as="textarea" style={txta} name="sp-client-address" value={client.address} onChange={e=>setClient(c=>({...c,address:e.target.value}))} placeholder="Street, Town, County, Postcode"/></Field>
          <Divider label="Vehicle"/>
          <div className="sp-grid" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
            <Field label="Registration"><input style={inp} value={vehicle.reg} onChange={e=>setVehicle(v=>({...v,reg:e.target.value.toUpperCase()}))} placeholder="e.g. AB12 CDE"/></Field>
            <Field label="Vehicle Type">
              <select style={sel} value={vehicle.vehicleType} onChange={e=>setVehicle(v=>({...v,vehicleType:e.target.value}))}>
                {Object.keys(AUTO_VEHICLE_MULT).map(o=><option key={o}>{o}</option>)}
              </select>
            </Field>
            <Field label="Make"><input style={inp} value={vehicle.make} onChange={e=>setVehicle(v=>({...v,make:e.target.value}))} placeholder="e.g. Porsche"/></Field>
            <Field label="Model"><input style={inp} value={vehicle.model} onChange={e=>setVehicle(v=>({...v,model:e.target.value}))} placeholder="e.g. 911 Carrera"/></Field>
            <Field label="Year"><input style={inp} value={vehicle.year} onChange={e=>setVehicle(v=>({...v,year:e.target.value}))} placeholder="e.g. 1987"/></Field>
            <Field label="Colour"><input style={inp} value={vehicle.colour} onChange={e=>setVehicle(v=>({...v,colour:e.target.value}))} placeholder="e.g. Guards Red"/></Field>
          </div>
          <div style={{marginTop:8}}>
            <InfoChip label="Vehicle multiplier" value={`×${AUTO_VEHICLE_MULT[vehicle.vehicleType]?.toFixed(2)} on labour & dry ice`}/>
          </div>
        </Card>
      </Section>

      <Section num="3" title="Vehicle Condition & Use">
        <Card>
          <div style={{background:t.accentDim,border:`1px solid ${t.accentGlow}`,borderRadius:8,padding:"10px 14px",marginBottom:14}}>
            <div style={{fontSize:11,color:t.accent,fontWeight:600}}>📋 Capture this at first inquiry</div>
            <div style={{fontSize:11,color:t.textMuted,marginTop:3}}>These fields inform service recommendations, protection spec and accurate scoping. Ask before quoting.</div>
          </div>
          <div className="sp-grid" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
            <Field label="Primary Use">
              <select style={sel} value={vehicleUse.primaryUse} onChange={e=>setVehicleUse(v=>({...v,primaryUse:e.target.value}))}>
                {["Daily Driver","Weekend / Leisure","Shows & Concours","Track / Rally","Stored / Laid Up","Pre-Sale"].map(o=><option key={o}>{o}</option>)}
              </select>
            </Field>
            <Field label="Storage">
              <select style={sel} value={vehicleUse.storage} onChange={e=>setVehicleUse(v=>({...v,storage:e.target.value}))}>
                {["Garaged (heated)","Garaged (unheated)","Barn / Agricultural","Outdoor — covered","Outdoor — exposed","Unknown"].map(o=><option key={o}>{o}</option>)}
              </select>
            </Field>
            <Field label="Known Corrosion / Rust">
              <select style={sel} value={vehicleUse.knownCorrosion} onChange={e=>setVehicleUse(v=>({...v,knownCorrosion:e.target.value}))}>
                {["None visible","Surface rust only","Moderate — localised areas","Heavy — multiple areas","Unknown"].map(o=><option key={o}>{o}</option>)}
              </select>
            </Field>
            <Field label="Previous Underbody Protection?">
              <select style={sel} value={vehicleUse.previousProtection} onChange={e=>setVehicleUse(v=>({...v,previousProtection:e.target.value}))}>
                {["None","Lanoguard / wax — recent","Lanoguard / wax — old","Bitumen underseal applied","Dinitrol / professional system","Unknown"].map(o=><option key={o}>{o}</option>)}
              </select>
            </Field>
          </div>
          <Field label="How did they find us?" style={{marginTop:4}}>
            <input style={inp} value={vehicleUse.referralSource} onChange={e=>setVehicleUse(v=>({...v,referralSource:e.target.value}))} placeholder="Referral, Google, Instagram..."/>
          </Field>
          {(vehicleUse.knownCorrosion==="Heavy — multiple areas"||vehicleUse.previousProtection==="Bitumen underseal applied")&&(
            <div style={{background:t.danger+"14",border:`1px solid ${t.danger}44`,borderRadius:8,padding:"10px 14px",marginTop:8}}>
              <div style={{fontSize:11,color:t.danger,fontWeight:600,marginBottom:4}}>⚠ Scope note</div>
              {vehicleUse.previousProtection==="Bitumen underseal applied"&&<div style={{fontSize:11,color:t.textMuted,marginBottom:2}}>Existing bitumen underseal will need removal — add Underseal / Bitumen Removal to services.</div>}
              {vehicleUse.knownCorrosion==="Heavy — multiple areas"&&<div style={{fontSize:11,color:t.textMuted}}>Heavy corrosion present — consider adding Rust Stabilisation to add-ons and flag in quote that welding may be recommended following inspection.</div>}
            </div>
          )}
        </Card>
      </Section>

      <Section num="4" title="Services">
        <Card>
          {/* What drives the price: vehicle type (section 2) × the ticked services. */}
          <div style={{background:t.bg,border:`1px solid ${t.accent}`,boxShadow:`0 0 0 1px ${t.accent}`,borderRadius:10,padding:"14px 16px",marginBottom:16}}>
            <Eyebrow style={{marginBottom:6}}>What this prices</Eyebrow>
            <div style={{...type.statValue,fontSize:14,color:t.text,lineHeight:1.55}}>
              <span style={{fontFamily:fonts.body,fontWeight:600}}>{vehicle.vehicleType}</span> ×{fmtN(AUTO_VEHICLE_MULT[vehicle.vehicleType]||1,2)} · {services.length} service{services.length===1?"":"s"}
              {" "}<span style={{color:t.textMuted}}>→</span> {fmtN(sv.labourHrs)} hrs <span style={{color:t.textMuted}}>→</span> {fmtN(sv.adjDryIce)} kg dry ice <span style={{color:t.textMuted}}>→</span> <strong style={{color:t.accent,fontWeight:500}}>{fmt(sv.finalPrice)} ex VAT</strong>
            </div>
            <div style={{fontSize:12,color:t.textMuted,marginTop:4}}>Vehicle type is set in section 2. Hours include protection and add-ons.</div>
          </div>
          <Field label="Contamination Level">
            <select style={sel} value={contamination} onChange={e=>setContamination(e.target.value)}>
              {Object.keys(AUTO_CONTAM_MULT).map(o=><option key={o}>{o}</option>)}
            </select>
          </Field>
          <div className="sp-grid" style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:14}}>
            <InfoChip label="Contam. multiplier" value={`×${AUTO_CONTAM_MULT[contamination]?.mult.toFixed(2)}`}/>
            <InfoChip label="Services selected" value={`${services.length} of ${Object.keys(AUTO_SERVICE_HOURS).length}`}/>
            <InfoChip label="Est. labour" value={`${fmtN(calc.labourHrs)} hrs`}/>
          </div>
          <Divider label="Select Services"/>
          {serviceNote&&<div style={{fontSize:12,color:t.textMuted,background:t.surface,border:`1px solid ${t.border}`,borderRadius:8,padding:"8px 12px",marginBottom:8}}>{serviceNote}</div>}
          {Object.entries(AUTO_SERVICE_HOURS).map(([key,svc])=>{
            const active=services.includes(key);
            const hrs=(svc.hrs * (AUTO_CONTAM_MULT[contamination]?.mult||1) * (AUTO_VEHICLE_MULT[vehicle.vehicleType]||1));
            const parent=includedBy(key,services);
            // Greyed out while a ticked package already covers it. A saved
            // quote that has both ticked keeps them (and its price) until
            // someone unticks one.
            if(parent&&!active) return(
              <div key={key} title={`Included in ${AUTO_SERVICE_HOURS[parent].label}`} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 14px",borderRadius:8,border:`1px dashed ${t.border}`,background:"transparent",cursor:"not-allowed",marginBottom:6,opacity:0.55}}>
                <div style={{width:18,height:18,borderRadius:4,border:`2px solid ${t.textDim}`,flexShrink:0}}/>
                <span style={{flex:1,fontSize:13,color:t.textMuted}}>{svc.label}</span>
                <span style={{fontSize:11,color:t.textDim,fontStyle:"italic"}}>Included in {AUTO_SERVICE_HOURS[parent].label}</span>
              </div>
            );
            return(
              <div key={key} onClick={()=>toggleService(key)} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 14px",borderRadius:8,border:`1px solid ${active?t.accent:t.border}`,background:active?t.accentDim:"transparent",cursor:"pointer",marginBottom:8}}>
                <div style={{width:18,height:18,borderRadius:4,border:`2px solid ${active?t.accent:t.textDim}`,background:active?t.accent:"transparent",display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,color:t.onAccent,flexShrink:0}}>{active?"✓":""}</div>
                <span style={{flex:1,fontSize:13,color:active?t.text:t.textMuted,fontWeight:active?600:400}}>{svc.label}{active&&parent&&<span style={{display:"block",fontSize:11,fontWeight:400,color:t.warning}}>Also included in {AUTO_SERVICE_HOURS[parent].label}: untick to avoid charging twice</span>}</span>
                <span style={{fontSize:11,color:t.textDim}}>{fmtN(hrs,1)}h</span>
              </div>
            );
          })}
          <ScopeEditor notes={notes} setNotes={setNotes} templateBody={scopeTemplate} autoSections={scopeAutoSections} t={t}/>
        </Card>
      </Section>

      <Section num="5" title="Protection & Add-ons">
        <Card>
          <Divider label="Corrosion Protection System"/>
          {Object.entries(AUTO_PROTECTION).map(([key,p])=>{
            const active=protection===key;
            return(
              <div key={key} onClick={()=>chooseProtection(key)} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 14px",borderRadius:8,border:`1px solid ${active?t.accent:t.border}`,background:active?t.accentDim:"transparent",cursor:"pointer",marginBottom:8}}>
                <div style={{width:18,height:18,borderRadius:"50%",border:`2px solid ${active?t.accent:t.textDim}`,background:active?t.accent:"transparent",flexShrink:0}}/>
                <span style={{flex:1,fontSize:13,color:active?t.text:t.textMuted,fontWeight:active?600:400}}>{p.label}</span>
                {p.materials>0&&<span style={{fontSize:11,color:t.textDim}}>+{fmt(p.materials*calc.protScale)} materials · {fmtN(p.labourHrs*calc.protScale,1)}h{calc.protScale!==1?` (×${calc.protScale} ${vehicle.vehicleType})`:""}</span>}
              </div>
            );
          })}
          <Divider label="Additional Services"/>
          {protectionNote&&<div style={{fontSize:12,color:t.textMuted,background:t.surface,border:`1px solid ${t.border}`,borderRadius:8,padding:"8px 12px",marginBottom:8}}>{protectionNote}</div>}
          {Object.entries(AUTO_ADDONS).map(([key,addon])=>{
            const active=addons.includes(key);
            // A ticked service or the protection system that covers it, by name.
            const parent=addonIncludedIn(key,services,protection);
            // Covered by a ticked service: shown ticked but greyed out, 0h. A
            // saved quote that has it ticked as well keeps it (and its price)
            // until someone unticks it, same as services.
            if(parent&&!active) return(
              <div key={key} title={`Included in ${parent}`} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 14px",borderRadius:8,border:`1px dashed ${t.border}`,background:"transparent",cursor:"not-allowed",marginBottom:6,opacity:0.55}}>
                <div style={{width:18,height:18,borderRadius:4,border:`2px solid ${t.textDim}`,background:t.textDim,display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,color:"#fff",flexShrink:0}}>✓</div>
                <span style={{flex:1,fontSize:13,color:t.textMuted}}>{addon.label}</span>
                <span style={{fontSize:11,color:t.textDim,fontStyle:"italic"}}>Included in {parent} · 0h</span>
              </div>
            );
            return(
              <div key={key} onClick={()=>toggleArr(addons,setAddons,key)} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 14px",borderRadius:8,border:`1px solid ${active?t.accent:t.border}`,background:active?t.accentDim:"transparent",cursor:"pointer",marginBottom:8}}>
                <div style={{width:18,height:18,borderRadius:4,border:`2px solid ${active?t.accent:t.textDim}`,background:active?t.accent:"transparent",display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,color:t.onAccent,flexShrink:0}}>{active?"✓":""}</div>
                <span style={{flex:1,fontSize:13,color:active?t.text:t.textMuted,fontWeight:active?600:400}}>{addon.label}{active&&parent&&<span style={{display:"block",fontSize:11,fontWeight:400,color:t.warning}}>Also included in {parent}: untick to avoid charging twice</span>}</span>
                <span style={{fontSize:11,color:t.textDim}}>{addon.materials>0?`+${fmt(addon.materials)} · `:""}{fmtN(addon.labourHrs,1)}h</span>
              </div>
            );
          })}
          <Divider label="Custom Labour"/>
          {/* Free-form labour items, priced at this quote's labour rate and crew.
              The description is what the client sees; hours and costs stay internal. */}
          {customLabour.map(row=>{
            const pr=CUSTOM_LABOUR_PRESETS[row.preset];
            return(
              <div key={row.id} style={{border:`1px solid ${t.border}`,borderRadius:8,padding:"10px 12px",marginBottom:8,background:t.navyDark}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
                  <span style={{fontSize:11,color:t.textMuted,letterSpacing:"0.05em",textTransform:"uppercase"}}>{pr?pr.name:"Custom item"}</span>
                  <button type="button" onClick={()=>setCustomLabour(rows=>rows.filter(r=>r.id!==row.id))} title="Remove this item" style={{background:"none",border:"none",color:t.textMuted,cursor:"pointer",fontSize:14}}>✕</button>
                </div>
                <CheckRow checked={!!row.internal} onChange={v=>updateCustomLabour(row.id,{internal:v})} style={{marginBottom:6}}>
                  Internal only (not shown to client)
                </CheckRow>
                <Field label={row.internal?"Description (internal: Job Sheet only)":"Description (shown to the client)"} style={{marginBottom:8}}>
                  <textarea style={{...txta,minHeight:56,...(isBlankPricedCustomRow(row)?{borderColor:t.danger}:{})}} value={row.label} onChange={e=>updateCustomLabour(row.id,{label:e.target.value})} placeholder="What the work involves"/>
                  {isBlankPricedCustomRow(row)&&<div style={{fontSize:11,color:t.danger,marginTop:4}}>{row.internal?"This item is priced but has no description, so the Job Sheet wouldn't say what it is. Add a description or remove it before saving.":"This item is priced but has no description, so the client wouldn't see it. Add a description or remove it before saving."}</div>}
                </Field>
                <div className="sp-grid" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
                  <Field label="Hours" style={{marginBottom:0}}><NumInput style={inp} min="0" step="0.5" value={row.hours} onChange={e=>updateCustomLabour(row.id,{hours:e.target.value===""?"":+e.target.value})} unit="h"/></Field>
                  <Field label="Materials £ (optional)" style={{marginBottom:0}}><NumInput pre="£" style={inp} min="0" step="1" value={row.materials||""} onChange={e=>updateCustomLabour(row.id,{materials:e.target.value===""?0:+e.target.value})}/></Field>
                </div>
                {pr?.seizedOption&&(
                  <CheckRow checked={!!row.seized} onChange={v=>updateCustomLabour(row.id,{seized:v})} style={{marginTop:6}}>
                    Allow for seized fixings (+{SEIZED_FIXINGS_HOURS} hr)
                  </CheckRow>
                )}
                <div style={{fontSize:11,color:t.textDim,marginTop:6}}>{fmtN(customLabourHours(row),1)}h at {fmt(rates.labourRate)}/hr{rates.crewSize>1?` × ${rates.crewSize} crew`:""}</div>
              </div>
            );
          })}
          <div style={{display:"flex",gap:8,marginBottom:14}}>
            <select style={{...sel,flex:1}} value="" onChange={e=>{ if(e.target.value) setCustomLabour(rows=>[...rows,newCustomLabourRow(e.target.value)]); }}>
              <option value="">Add a preset…</option>
              {Object.entries(CUSTOM_LABOUR_PRESETS).map(([k,pr])=><option key={k} value={k}>{pr.name}</option>)}
            </select>
            <button type="button" onClick={()=>setCustomLabour(rows=>[...rows,newCustomLabourRow(null)])} style={{padding:"0 14px",borderRadius:8,border:`1px dashed ${t.border}`,background:"transparent",color:t.textMuted,cursor:"pointer",fontSize:11,fontWeight:700,letterSpacing:"0.08em",whiteSpace:"nowrap"}}>+ Add custom item</button>
          </div>
          <Divider label="Subcontracted Detailing Services"/>
          <div style={{background:t.accentDim,border:`1px solid ${t.accentGlow}`,borderRadius:8,padding:"10px 14px",marginBottom:12}}>
            <div style={{fontSize:11,color:t.accent,fontWeight:600}}>🔧 Partner and cost are internal</div>
            <div style={{fontSize:11,color:t.textMuted,marginTop:3}}>Subcontracted to detailing partner. The client quote lists each ticked service by name only, under Add-ons. Will auto-generate PO when PO automation is built.</div>
          </div>
          {Object.entries(AUTO_DETAILING).map(([key,d])=>{
            const active=detailing.includes(key);
            return(
              <div key={key} onClick={()=>setDetailing(a=>a.includes(key)?a.filter(x=>x!==key):[...a,key])} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 14px",borderRadius:8,border:`1px solid ${active?t.accent:t.border}`,background:active?t.accentDim:"transparent",cursor:"pointer",marginBottom:8}}>
                <div style={{width:18,height:18,borderRadius:4,border:`2px solid ${active?t.accent:t.textDim}`,background:active?t.accent:"transparent",display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,color:t.onAccent,flexShrink:0}}>{active?"✓":""}</div>
                <div style={{flex:1}}>
                  <div style={{fontSize:13,color:active?t.text:t.textMuted,fontWeight:active?600:400}}>{d.label}</div>
                  <div style={{fontSize:11,color:t.textDim,marginTop:2}}>{d.desc}</div>
                </div>
              </div>
            );
          })}
          <Divider label="Concierge Collection & Delivery"/>
          <Toggle value={concierge} onChange={setConcierge} label="Include concierge vehicle transport service"/>
          {concierge&&(
            <div style={{marginTop:12,padding:14,background:t.navyDark,borderRadius:8,border:`1px solid ${t.border}`}}>
              <div className="sp-grid" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
                <Field label="Collection Miles (one way)"><NumInput style={inp} value={conciergeMiles} onChange={e=>setConciergeMiles(e.target.value)} placeholder="e.g. 45" unit="miles"/></Field>
                <Field label="Return Journey" style={{paddingTop:16}}><Toggle value={conciergeReturn} onChange={setConciergeReturn} label="Include return"/></Field>
              </div>
              <div className="sp-grid" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginTop:4}}>
                <InfoChip label="Flat collection fee" value={fmt(rates.conciergeFlat)}/>
                <InfoChip label="Travel cost" value={fmt(calcAuto({...state},rates).conciergeTravel)}/>
              </div>
            </div>
          )}
        </Card>
      </Section>

      <Section num="6" title="Equipment & Consumables" defaultOpen={false}>
        <Card>
          <Divider label="Dry Ice Machine"/>
          <div className="sp-grid" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
            <Field label="Machine Type">
              <select style={sel} value={machineType} onChange={e=>setMachineType(e.target.value)}>
                {Object.keys(AUTO_MACHINE_PRESETS).map(o=><option key={o}>{o}</option>)}
              </select>
            </Field>
            <Field label="Crew Size"><NumInput style={inp} min="1" value={rates.crewSize} onChange={e=>setRates(r=>({...r,crewSize:+e.target.value}))}/></Field>
          </div>
          <div className="sp-grid" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:14}}>
            <InfoChip label="Default dry ice rate" value={`${macPreset.dryIceKgHr} kg/hr`} auto/>
            {!calc.inUnit&&<InfoChip label="Default fuel burn" value={`${macPreset.fuelBurnLHr} L/hr`} auto/>}
          </div>
          <Divider label={`Dry Ice Supply — ${diSupplier?.name||"No active supplier"}`}/>
          {!diSupplier&&(
            <div style={{background:t.warningSoft,border:`1px solid ${t.warning}`,borderRadius:8,padding:"10px 14px",marginBottom:14,fontSize:12,color:t.warning}}>
              ⚠ No active dry-ice supplier found in Admin → Suppliers. Add one there (Products Supplied must mention "dry ice") before quoting.
            </div>
          )}
          {dryIceSuppliers.length>1&&(
            <Field label="Supplier">
              <select style={sel} value={diSupplier?.id||""} onChange={e=>setRates(r=>({...r,dryIceSupplierId:e.target.value}))}>
                {dryIceSuppliers.map(s=><option key={s.id} value={s.id}>{dryIceOptionLabel(s,diPrimary)}</option>)}
              </select>
            </Field>
          )}
          {hasDeliveredTiers(diSupplier)?(
            <DeliveredTiersPanel supplier={diSupplier} dryIceKg={calc.dryIceKg} adjKg={calc.adjDryIce} charge={calc.diCharge}
              wastageField={<Field label="Wastage (%)"><NumInput style={inp} value={rates.wastage} onChange={e=>setRates(r=>({...r,wastage:+e.target.value}))} unit="%"/></Field>}/>
          ):(<>
          <div className="sp-grid" style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12}}>
            <InfoChip label="Price per kg (£)" value={diSupplier?`£${diPricePerKg.toFixed(3)}`:"—"} auto/>
            <InfoChip label="Delivery per container (£)" value={diSupplier?`£${diDeliveryPerBin.toFixed(2)}`:"—"} auto/>
            <Field label="Wastage (%)"><NumInput style={inp} value={rates.wastage} onChange={e=>setRates(r=>({...r,wastage:+e.target.value}))} unit="%"/></Field>
          </div>
          {(()=>{const sd=daysSince(diSupplier?.priceLastUpdated);return sd!=null&&sd>SUPPLIER_PRICE_STALE_DAYS&&(
            <div style={{background:t.warningSoft,border:`1px solid ${t.warning}`,borderRadius:8,padding:"10px 14px",marginTop:8,marginBottom:14,fontSize:12,color:t.warning}}>
              ⚠ {diSupplier.name}'s price was last checked {sd} days ago (over {SUPPLIER_PRICE_STALE_DAYS}) — worth confirming it's still current in Admin → Suppliers.
            </div>
          );})()}
          <Divider label="Container Selection"/>
          <div className="sp-grid" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:14}}>
            {diBinOptions.map(b=>(
              <div key={b.label} onClick={()=>setRates(r=>({...r,binCapacityLabel:b.label}))} style={{padding:"12px 14px",borderRadius:8,border:`1px solid ${diSelectedBin?.label===b.label?t.accent:t.border}`,background:diSelectedBin?.label===b.label?t.accentDim:"transparent",cursor:"pointer"}}>
                <div style={{fontSize:13,fontWeight:700,color:diSelectedBin?.label===b.label?t.accent:t.text,marginBottom:2}}>{b.label}</div>
                <div style={{fontSize:11,color:t.textMuted}}>{b.capacity_kg}kg capacity &nbsp;·&nbsp; £{diBinRentalPerBin.toFixed(2)} hire/bin</div>
              </div>
            ))}
          </div>
          <div className="sp-grid" style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:14}}>
            <InfoChip label="Bins required" value={`${autoBinsNeeded} × ${diSelectedBin?.label||"—"}`}/>
            <InfoChip label="Bin hire" value={`£${autoBinHire.toFixed(2)}`}/>
            <InfoChip label="Max capacity" value={`${(autoBinsNeeded*autoBinSpec.capacity).toFixed(0)}kg`}/>
          </div>
          <div className="sp-grid" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:14}}>
            <InfoChip label="Raw dry ice" value={`${fmtN(calc.dryIceKg)} kg`}/>
            <InfoChip label="Adjusted (inc. wastage)" value={`${fmtN(calc.adjDryIce)} kg → ${fmt(calc.dryIceCost)}`}/>
          </div>
          {calc.diCharge.pinned&&calc.diCharge.liveCost!==calc.diCharge.cost&&<DryIcePinnedNote charge={calc.diCharge}/>}
          </>)}
          <Divider label={calc.inUnit?"Power & Labour":"Fuel & Labour"}/>
          <div className="sp-grid" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
            {calc.inUnit
              ?<Field label="Electricity (£/hr, compressor)"><NumInput pre="£" style={inp} step="0.1" value={rates.electricityPerHr??DEFAULT_ELECTRICITY_PER_HR} onChange={e=>setRates(r=>({...r,electricityPerHr:+e.target.value}))} unit="/h"/></Field>
              :<Field label="Diesel Price (£/L)"><NumInput pre="£" style={inp} step="0.01" value={rates.dieselPrice} onChange={e=>setRates(r=>({...r,dieselPrice:+e.target.value}))} unit="/L"/></Field>}
            <Field label="Labour Rate (£/hr)"><NumInput pre="£" style={inp} value={rates.labourRate} onChange={e=>setRates(r=>({...r,labourRate:+e.target.value}))} unit="/h"/></Field>
          </div>
          <Divider label="Concierge Rates"/>
          <div className="sp-grid" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
            <Field label="Flat Collection Fee (£)"><NumInput pre="£" style={inp} value={rates.conciergeFlat} onChange={e=>setRates(r=>({...r,conciergeFlat:+e.target.value}))}/></Field>
            <Field label="Per Mile (£)"><NumInput pre="£" style={inp} step="0.01" value={rates.conciergePerMile} onChange={e=>setRates(r=>({...r,conciergePerMile:+e.target.value}))} unit="/mile"/></Field>
          </div>
          <Divider label="Remote Job — Travel"/>
          <Toggle value={includeTravel} onChange={v=>{setIncludeTravel(v);if(!v)setRates(r=>({...r,travelMiles:0,manualMiles:null}));}} label="This job requires travel (remote site)"/>
          {includeTravel&&(
            <div style={{marginTop:12}}>
              <div className="sp-grid" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
                <Field label="From Postcode"><NoAutofillInput style={inp} name="sp-from-postcode" value={fromPC} onChange={e=>setFromPC(e.target.value.toUpperCase())} placeholder="TR27 4QZ"/></Field>
                <Field label="To Postcode"><NoAutofillInput style={inp} name="sp-to-postcode" value={toPC} onChange={e=>setToPC(e.target.value.toUpperCase())} placeholder="EC1A 1BB"/></Field>
              </div>
              <div className="sp-grid" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
                <Field label="Rate per Mile (£)"><NumInput pre="£" style={inp} step="0.01" value={rates.mileRate||0.45} onChange={e=>setRates(r=>({...r,mileRate:+e.target.value}))} unit="/mile"/></Field>
                <Field label="Avg Travel Speed (mph)"><NumInput style={inp} step="1" value={rates.avgTravelSpeed||40} onChange={e=>setRates(r=>({...r,avgTravelSpeed:+e.target.value}))} unit="mph"/></Field>
              </div>
              <Field label="&nbsp;">
                <button type="button" onClick={calculateMileage} disabled={mileLoading||autoManualMiles!=null} title={autoManualMiles!=null?"Manual miles in use: clear it to use the lookup":undefined} style={{...secondaryBtn,width:"100%",padding:10,fontSize:13,opacity:autoManualMiles!=null?0.5:1}}>{mileLoading?"Calculating...":"Calculate Distance"}</button>
              </Field>
              <Toggle value={returnTrip} onChange={v=>{setReturnTrip(v);if(autoManualMiles!=null)setRates(r=>({...r,travelMiles:milesTotal(autoManualMiles,v)}));else if(calcMiles)setRates(r=>({...r,travelMiles:v?calcMiles*2:calcMiles}));}} label="Include return journey"/>
              {mileError&&<div style={{color:t.danger,fontSize:12,marginTop:8}}>{mileError}</div>}
              <ManualMilesField manual={rates.manualMiles} lookupMiles={calcMiles} onChange={v=>setRates(r=>({...r,manualMiles:v,travelMiles:v!=null?milesTotal(v,returnTrip):(calcMiles?milesTotal(calcMiles,returnTrip):0)}))}/>
              {(autoManualMiles!=null||calcMiles)&&(
                <div style={{background:t.navyDark,borderRadius:8,padding:12,border:`1px solid ${t.border}`,marginTop:10}}>
                  <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
                    <span style={{fontSize:11,color:t.textMuted}}>One-way distance{autoManualMiles!=null?" (manual)":""}</span>
                    <span style={{fontSize:12,fontWeight:600}}>{autoManualMiles!=null?autoManualMiles:calcMiles} miles</span>
                  </div>
                  <div style={{display:"flex",justifyContent:"space-between",borderTop:`1px solid ${t.border}`,paddingTop:8,marginTop:4}}>
                    <span style={{fontSize:11,color:t.textMuted}}>Total → travel cost (fuel/vehicle)</span>
                    <span style={{fontSize:12,color:t.accent,fontWeight:700}}>{rates.travelMiles} miles → {fmt(calc.travelCost)}</span>
                  </div>
                  <div style={{display:"flex",justifyContent:"space-between",borderTop:`1px solid ${t.border}`,paddingTop:8,marginTop:4}}>
                    <span style={{fontSize:11,color:t.textMuted}}>Travel time (labour, all crew)</span>
                    <span style={{fontSize:12,color:t.accent,fontWeight:700}}>{fmtN(calc.travelTimeHrs)} hrs → {fmt(calc.travelTimeHrs*rates.labourRate*rates.crewSize)}</span>
                  </div>
                </div>
              )}
            </div>
          )}
          <button type="button" onClick={()=>setAdvMode(m=>!m)} style={{display:"flex",alignItems:"center",gap:6,background:"transparent",border:`1px dashed ${t.border}`,borderRadius:8,color:t.textMuted,padding:"8px 14px",fontSize:11,cursor:"pointer",width:"100%",justifyContent:"center",marginTop:8}}>
            {advMode?"▲ Hide":"▼ Show"} Advanced Overrides
          </button>
          {advMode&&(
            <div style={{marginTop:12,padding:14,background:t.navyDark,borderRadius:8,border:`1px solid ${t.border}`}}>
              <div style={{fontSize:11,color:t.warning,marginBottom:10}}>⚠ These override machine preset values</div>
              <div className="sp-grid" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
                <Field label="Dry Ice kg/hr override"><NumInput style={inp} placeholder={macPreset.dryIceKgHr} value={rates.dryIceKgHrOverride||""} onChange={e=>setRates(r=>({...r,dryIceKgHrOverride:e.target.value?+e.target.value:null}))} unit="kg/h"/></Field>
                {!calc.inUnit&&<Field label="Fuel burn L/hr override"><NumInput style={inp} placeholder={macPreset.fuelBurnLHr} value={rates.fuelBurnOverride||""} onChange={e=>setRates(r=>({...r,fuelBurnOverride:e.target.value?+e.target.value:null}))} unit="L/h"/></Field>}
              </div>
            </div>
          )}
        </Card>
      </Section>

      <Section num="7" title="Commercial Settings">
        <Card>
          <Divider label="Equipment Recovery"/>
          <div className="sp-grid" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
            <Field label="Machine (£/hr)"><NumInput pre="£" style={inp} value={rates.machinePerHr} onChange={e=>setRates(r=>({...r,machinePerHr:+e.target.value}))} unit="/h"/></Field>
            <Field label="Compressor (£/hr)"><NumInput pre="£" style={inp} value={rates.compressorPerHr} onChange={e=>setRates(r=>({...r,compressorPerHr:+e.target.value}))} unit="/h"/></Field>
          </div>
          <Divider label="Margin & Uplift"/>
          <MarginSlider value={commercial.targetMargin} onChange={v=>setCom(c=>({...c,targetMargin:v,marginSet:true}))} basePrice={basePrice} totalCost={calc.totalCost} jobLocation={job.jobLocation||"on-site"}/>
          <div className="sp-grid" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
            <Field label="Contingency (%)"><NumInput style={inp} value={commercial.contingency} onChange={e=>setCom(c=>({...c,contingency:+e.target.value}))} unit="%"/></Field>
            <Field label="Round Final Price">
              <select style={sel} value={commercial.roundPrice} onChange={e=>setCom(c=>({...c,roundPrice:e.target.value}))}>
                {["none","nearest £50","nearest £100"].map(o=><option key={o}>{o}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Minimum Job Price (£)"><NumInput pre="£" style={inp} value={commercial.minJobPrice} onChange={e=>setCom(c=>({...c,minJobPrice:+e.target.value}))}/></Field>
          <Divider label="Discount (optional)"/>
          {/* Presets (discountPresets.js): set the % and reason in one go;
              changing them by hand makes it a manual discount again. */}
          <Field label="Discount preset">
            <select style={sel} value={activeDiscountPreset(commercial)} onChange={e=>setCom(c=>applyDiscountPreset(c,e.target.value))}>
              <option value="">No preset (manual)</option>
              {Object.entries(DISCOUNT_PRESETS).map(([k,p])=><option key={k} value={k}>{p.label}</option>)}
            </select>
          </Field>
          <div className="sp-grid" style={{display:"grid",gridTemplateColumns:commercial.discountType&&commercial.discountType!=="none"?"1fr 1fr":"1fr",gap:12}}>
            <Field label="Discount Type">
              <select style={sel} value={commercial.discountType||"none"} onChange={e=>setCom(c=>({...c,discountType:e.target.value}))}>
                <option value="none">None</option>
                <option value="percent">Discount amount — Percentage (%)</option>
                <option value="flat">Discount amount — Flat (£)</option>
                <option value="target">Target final price (£, ex VAT)</option>
              </select>
            </Field>
            {commercial.discountType==="percent"&&(
              <Field label="Discount (%)">
                <NumInput style={inp} min="0" max="100" value={commercial.discountValue||""} onChange={e=>setCom(c=>({...c,discountValue:e.target.value?+e.target.value:0}))} placeholder="0" unit="%"/>
              </Field>
            )}
            {commercial.discountType==="flat"&&(
              <Field label="Discount (£)">
                <NumInput pre="£" style={inp} min="0" value={commercial.discountValue||""} onChange={e=>setCom(c=>({...c,discountValue:e.target.value?+e.target.value:0}))} placeholder="0"/>
              </Field>
            )}
            {commercial.discountType==="target"&&(
              <Field label="Target Final Price (£)">
                <NumInput pre="£" style={inp} min="0" value={commercial.discountTargetPrice??""} onChange={e=>setCom(c=>({...c,discountTargetPrice:e.target.value?+e.target.value:null}))} placeholder={fmtN(subtotalExVat)}/>
              </Field>
            )}
          </div>
          {commercial.discountType&&commercial.discountType!=="none"&&(
            <>
              <Field label="Discount Reason"><input style={inp} value={commercial.discountReason||""} onChange={e=>setCom(c=>({...c,discountReason:e.target.value}))} placeholder="e.g. Introductory rate"/></Field>
              <div style={{fontSize:11,color:discountAmount>0?t.accent:t.textDim,marginTop:6}}>
                {discountAmount>0
                  ? `→ Discount: ${fmt(discountAmount)} (${fmtN(subtotalExVat>0?discountAmount/subtotalExVat*100:0,1)}%) — subtotal ${fmt(subtotalExVat)} → ${fmt(finalPrice)}`
                  : commercial.discountType==="target"?"Enter a target price below the current subtotal to calculate the discount.":"Enter a value above to calculate the discount."}
              </div>
            </>
          )}
        </Card>
      </Section>

      <Section num="8" title="Notes & Assumptions" defaultOpen={false}>
        <Card>
          <div className="sp-grid" style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"1fr 1fr",gap:12}}>
            <Field label="Estimator Notes"><textarea style={txta} value={notes.estimator} onChange={e=>setNotes(n=>({...n,estimator:e.target.value}))} placeholder="Condition observations, access notes..."/></Field>
            <Field label="Internal Assumptions"><textarea style={txta} value={notes.internal} onChange={e=>setNotes(n=>({...n,internal:e.target.value}))} placeholder="Rate basis, risk factors..."/></Field>
            <Field label="Exclusions"><textarea style={txta} value={notes.exclusions} onChange={e=>setNotes(n=>({...n,exclusions:e.target.value}))} placeholder="What is not included..."/></Field>
          </div>
        </Card>
      </Section>
    </ReadOnlyForm>
  );

  if(isMobile){
    return(
      <div className="sp-calc" style={calcRoot}>
        <div style={{padding:"16px 16px 96px"}}>
          <CalcNav name="Automotive" status={readOnly?"Read only":calcStatusLabel({amendOf,savedAndUnchanged,copyOf})} onNew={readOnly?null:startNew} isMobile {...refProps}/>{pdfSaveChecking&&<SaveCheckingBanner/>}{savedNotice&&savedAndUnchanged&&savedRef?.number===savedNotice&&<SavedBanner refNo={savedNotice} onClose={()=>setSavedNotice(null)}/>}
          {formView}
          <div ref={previewRef}>{quotePreview}</div>
        </div>
        <CalcMobileBar readOnly={!!readOnly} refLine={refLine} finalPrice={sv.finalPrice} grossMargin={sv.grossMargin} marginColor={sv.marginColor} summary={summaryPanel} onSave={handleOpenSaveModal} onPDF={downloadPDF} onJobSheet={printJobSheet} onPreview={goToPreview} pdfLoading={pdfLoading}/>
        {pdfAsk&&<SavePdfDialog amendOf={amendOf} next={amendNext} prefix="AUT" isMobile={isMobile} onConfirm={()=>downloadPDF(true)} onClose={()=>setPdfAsk(false)}/>}
        {showSaveModal&&(
          <SaveQuoteModal
            quoteNumber={amendOf?amendNext:job.quoteRef}
            category="Automotive"
            showSiteAddress={job.jobLocation!=="in-unit"}
            totalExVat={finalPrice}
            totalIncVat={finalPrice*1.2}
            margin={commercial.targetMargin}
            scopeText={composedScope}
            application={selectedServicesLabel}
            stateJson={stateForSave}
            amendmentOf={amendOf}
            amendStatus="Draft"
            savedAs={savedAndUnchanged?savedRef.number:""}
            initialTestQuote={testQuote}
            initialClientName={client.name}
            initialClientEmail={client.email}
            initialClientPhone={client.phone}
            onClose={()=>setShowSaveModal(false)}
            onSaved={markSaved}
          />
        )}
      </div>
    );
  }

  return(
    <div className="sp-calc" style={{...calcRoot,display:"grid",gridTemplateColumns:"minmax(0,1fr) 380px",height:"calc(100vh - 73px)",minHeight:0}}>
      <div style={{padding:"32px 40px",overflowY:"auto",height:"100%"}}>
        <CalcNav name="Automotive" status={readOnly?"Read only":calcStatusLabel({amendOf,savedAndUnchanged,copyOf})} onNew={readOnly?null:startNew} {...refProps}/>{pdfSaveChecking&&<SaveCheckingBanner/>}{savedNotice&&savedAndUnchanged&&savedRef?.number===savedNotice&&<SavedBanner refNo={savedNotice} onClose={()=>setSavedNotice(null)}/>}
        {formView}
        {quotePreview}
      </div>
      <CalcSidePanel readOnly={!!readOnly} refLine={refLine} summary={summaryPanel} onSave={handleOpenSaveModal} onPDF={downloadPDF} onJobSheet={printJobSheet} pdfLoading={pdfLoading}/>
      {pdfAsk&&<SavePdfDialog amendOf={amendOf} next={amendNext} prefix="AUT" isMobile={isMobile} onConfirm={()=>downloadPDF(true)} onClose={()=>setPdfAsk(false)}/>}
        {showSaveModal&&(
        <SaveQuoteModal
          quoteNumber={amendOf?amendNext:job.quoteRef}
          category="Automotive"
            showSiteAddress={job.jobLocation!=="in-unit"}
          totalExVat={finalPrice}
          totalIncVat={finalPrice*1.2}
          margin={commercial.targetMargin}
          scopeText={composedScope}
          application={selectedServicesLabel}
          stateJson={stateForSave}
          amendmentOf={amendOf}
            amendStatus="Draft"
          savedAs={savedAndUnchanged?savedRef.number:""}
          initialTestQuote={testQuote}
          initialClientName={client.name}
          initialClientEmail={client.email}
          initialClientPhone={client.phone}
          onClose={()=>setShowSaveModal(false)}
          onSaved={markSaved}
        />
      )}
    </div>
  );
}


export default function App(){

  // Only a reload of this same tab keeps the page you were on. Opening the
  // app fresh (new tab, bookmark, home-screen icon, cold start) or arriving
  // by Back/Forward lands on the Quote Log; an unsaved draft is offered there
  // ("Resume draft"), never opened by itself. Remembered per browser tab.
  const[activeTab,setActiveTab]=useState(()=>{
    // A quote's record page (/record/:ref) lives in the Quote Log.
    if(window.location.pathname.startsWith("/record/")) return "log";
    try{ if(new URLSearchParams(window.location.search).get("open")) return "log"; }catch(e){ /* no URL API */ }
    let navType="navigate";
    try{ navType=performance.getEntriesByType("navigation")[0]?.type||"navigate"; }catch(e){ /* no Navigation Timing */ }
    if(navType!=="reload") return "log";
    try{ const v=window.sessionStorage.getItem("sublime_active_tab"); return ["automotive","industrial","log","settings"].includes(v)?v:"log"; }catch(e){ return "log"; }
  });
  React.useEffect(()=>{ try{ window.sessionStorage.setItem("sublime_active_tab",activeTab); }catch(e){ /* storage blocked */ } },[activeTab]);
  // Leaving the Quote Log from a record page (+ New Quote, Admin) leaves its
  // address too, so coming back lands on the list.
  const location=useLocation();
  const navigate=useNavigate();
  const onRecordPage=location.pathname.startsWith("/record/");
  React.useEffect(()=>{ if(activeTab!=="log"&&onRecordPage) navigate("/",{replace:true}); },[activeTab,onRecordPage,navigate]);
  // overflow-x: hidden makes the app's root a scroll container, which stops
  // position: sticky working inside it; clip doesn't, so the record page's
  // sticky bar sticks. Record pages only, so the other tabs are unchanged.
  // A calculator's Job Sheet (see openJobSheet), shown over the calculator.
  const[jobSheetHtml,setJobSheetHtml]=useState(null);
  React.useEffect(()=>{
    const show=e=>setJobSheetHtml(e.detail);
    window.addEventListener("sublime:jobsheet",show);
    return()=>window.removeEventListener("sublime:jobsheet",show);
  },[]);
  // An amendment being edited, per calculator: { state, meta }. state is the
  // calculator's starting point; meta.originalNumber is what links the saved
  // amendment back to the quote it amends (amendmentOf) and gets marked
  // superseded once it's saved - separate from state.job.quoteRef, which is
  // only the provisional number shown while editing.
  // Starts from any autosaved drafts (see draftStore.js).
  const[amendments,setAmendments]=useState(()=>{
    const fromDraft=tab=>{ const d=loadDraft(tab); return d?{state:draftToInitialState(d),meta:d.amendOf?{originalNumber:d.amendOf,...(d.basedOn?{basedOn:d.basedOn}:{}),...(d.startsAsDraft?{startsAsDraft:true}:{})}:null}:null; };
    return {automotive:fromDraft("automotive"),industrial:fromDraft("industrial")};
  });
  const clearAmendmentMeta=tab=>setAmendments(a=>a[tab]?{...a,[tab]:{...a[tab],meta:null}}:a);
  // Both calculators stay mounted (just hidden) so switching tabs never loses
  // work. Bumping a calculator's key remounts it: blank for + New Quote, or
  // loaded with an amendment from the Quote Log.
  const[calcKeys,setCalcKeys]=useState({automotive:0,industrial:0});
  const remount=tab=>setCalcKeys(k=>({...k,[tab]:k[tab]+1}));
  const startNewQuote=tab=>{ clearDraft(tab); setAmendments(a=>({...a,[tab]:null})); remount(tab); };
  // After a save: straight to the Quote Log with the saved quote open. The
  // calculator keeps what was entered (now marked as saved).
  // "/?open=AUT-…" (Back from View Quote): open the log on that quote's
  // panel, then tidy the address bar.
  // Browser Back from View Quote / Job Sheet: the quote QuoteLog noted
  // when it left (sublime_reopen), so its panel opens again too.
  const[logFocus,setLogFocus]=useState(()=>{
    let reopen=null;
    try{ reopen=window.sessionStorage.getItem("sublime_reopen"); window.sessionStorage.removeItem("sublime_reopen"); }catch(e){ /* storage blocked */ }
    try{ const qn=new URLSearchParams(window.location.search).get("open"); if(qn){ window.history.replaceState(null,"",window.location.pathname); return {quoteNumber:qn,reopen:true,at:Date.now()}; } }catch(e){ /* no URL API */ }
    let navType="navigate";
    try{ navType=performance.getEntriesByType("navigation")[0]?.type||"navigate"; }catch(e){ /* no Navigation Timing */ }
    if(reopen&&navType==="back_forward"&&window.location.pathname==="/") return {quoteNumber:reopen,reopen:true,at:Date.now()};
    return null;
  });
  // The Quote Log row a calculator was opened from (Amend, Copy as new
  // quote, Open in calculator): ← Quote Log reopens its panel. Cleared once
  // used, on save (the saved quote opens instead) and on a new quote.
  const[returnRow,setReturnRow]=useState(null);
  const backToLog=()=>{
    if(activeTab==="view"&&viewer){ viewer.ro.onBack(); setReturnRow(null); return; }
    if(returnRow) setLogFocus({quoteNumber:returnRow,reopen:true,at:Date.now()});
    setReturnRow(null);
    setActiveTab("log");
  };
  // Logo: back to the Quote Log list from anywhere in the app (a calculator,
  // a read-only quote, the Admin page, a Job Record), always in-app - never
  // browser history. Unsaved calculator work stays in its autosaved draft.
  const goHome=()=>{
    setJobSheetHtml(null);
    setShowNewQuote(false);
    if(activeTab!=="log") backToLog();
    if(window.location.pathname!=="/") navigate("/");
  };
  const quoteSaved=(number,sendNote,unchanged)=>{ setReturnRow(null); if(number) setLogFocus({quoteNumber:number,sendNote:sendNote||"",unchanged:!!unchanged,at:Date.now()}); setActiveTab("log"); };
  // "Open" from the Quote Log: a saved quote in its calculator, read only.
  // Its own calculator instance (tab "view"), so the calculator's draft is
  // untouched and nothing is autosaved. Back returns to the quote's panel.
  const[viewer,setViewer]=useState(null);
  const openReadOnly=({quote,stateJson,onAmend,amendLabel,blockedReason})=>{
    const tab=quote["Category"]?.toLowerCase()==="automotive"?"automotive":"industrial";
    const qn=quote["Quote Number"];
    setReturnRow(qn);
    const onBack=()=>{ setViewer(null); setLogFocus({quoteNumber:qn,reopen:true,at:Date.now()}); setActiveTab("log"); };
    setViewer({tab,key:`${qn}-${Date.now()}`,
      state:{...stateJson,_savedTotalExVat:parseFloat(String(quote["Total (ex VAT)"]||"").replace(/[^0-9.]/g,""))||null,_testQuote:quote["Test Quote"]==="Yes"},
      ro:{ref:qn,onAmend,amendLabel,blockedReason,onBack}});
    setActiveTab("view");
  };
  // "Copy as new quote": the source quote's set-up in a fresh calculator,
  // saved later under a new number (never an amendment of the source).
  const copyQuote=({ originalQuote, stateJson, copyDiscount })=>{
    const tab=originalQuote["Category"]?.toLowerCase()==="automotive"?"automotive":"industrial";
    if(!okToReplace(tab)) return;
    setReturnRow(originalQuote["Quote Number"]);
    const state=buildCopyState(stateJson,{isAuto:tab==="automotive",copyDiscount,quoteRef:getNextQuoteRef(tab==="automotive"?"AUT":"SBL"),copyOf:originalQuote["Quote Number"]});
    setAmendments(a=>({...a,[tab]:{state,meta:null}}));
    remount(tab);
    setActiveTab(tab);
  };
  // + New Quote (header): pick a calculator, then continue its unsaved draft
  // or start fresh (see NewQuoteDialog).
  const[showNewQuote,setShowNewQuote]=useState(false);
  const pickNewQuote=(tab,mode)=>{ setShowNewQuote(false); setReturnRow(null); if(mode==="fresh") startNewQuote(tab); setActiveTab(tab); };
  // Bumped whenever a calculator writes its draft, so the strip re-reads.
  const[,setDraftTick]=useState(0);
  const draftChanged=React.useCallback(()=>setDraftTick(n=>n+1),[]);
  const unsavedDrafts=["automotive","industrial"].map(tab=>({tab,draft:unsavedDraft(tab)})).filter(d=>d.draft);
  // Start new quote inside a calculator: asks only if there's unsaved work.
  const newQuoteIn=tab=>{ if(!unsavedDraft(tab)||window.confirm(`Start a new ${CALC_NAMES[tab]} quote? Your unsaved work on this one will be cleared.`)){ startNewQuote(tab); setReturnRow(null); draftChanged(); } };
  const discardDraft=tab=>{ if(window.confirm(`Discard the unsaved ${CALC_NAMES[tab]} draft?`)){ startNewQuote(tab); draftChanged(); } };
  // Amend / Copy load into a calculator, replacing what's there: check first
  // if that's unsaved work.
  const okToReplace=tab=>{ const d=unsavedDraft(tab); return !d||window.confirm(`This replaces your unsaved ${CALC_NAMES[tab]} draft (${d.summary||"unsaved work"}). Continue?`); };
  const[isMobile]=useState(()=>window.innerWidth<768);
  const{user}=useUser();
  // publicMetadata is set via the Clerk dashboard (Users → select user →
  // Metadata), not from this app - there's no self-service "become admin"
  // path here on purpose.
  const isAdmin=user?.publicMetadata?.role==="admin";
  const isLightTab=activeTab==="log"||activeTab==="settings";
  const userButtonMenu=isAdmin?(
    <UserButton.MenuItems>
      <UserButton.Action label="Admin" labelIcon={<IconSettingsGear/>} onClick={()=>setActiveTab("settings")}/>
    </UserButton.MenuItems>
  ):null;
  return (
    <div className="dark-app" style={{minHeight:"100vh",background:isLightTab?shellT.bg:t.bg,color:shellT.text,fontFamily:"'DM Sans','Helvetica Neue',sans-serif",margin:0,padding:0,overflowX:onRecordPage?"clip":"hidden"}}>
      {jobSheetHtml&&<JobSheetView overlay html={jobSheetHtml} backLabel="← Back to quote" onBack={()=>setJobSheetHtml(null)} onHome={goHome}/>}
      {/* A newer deploy than this tab is running: ask for a reload. */}
      <NewVersionBanner/>
      {IS_PREVIEW_DEPLOYMENT&&<BuildStamp/>}
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap" rel="stylesheet"/>
      <link href={FONTS_HREF} rel="stylesheet"/>
      <style>{`.dark-app .sp-calc input::placeholder, .dark-app .sp-calc textarea::placeholder { color: ${t.textDim} !important; -webkit-text-fill-color: ${t.textDim} !important; opacity: 1 !important; font-style: normal; }
        .sp-calc input:focus, .sp-calc select:focus, .sp-calc textarea:focus { border-color: ${t.accent} !important; box-shadow: 0 0 0 1px ${t.accent}, 0 0 0 1000px ${t.bg} inset !important; }
        .sp-calc button:focus-visible { outline: 2px solid ${t.accent}; outline-offset: 2px; }
        .sp-calc input[type=number]::-webkit-inner-spin-button, .sp-calc input[type=number]::-webkit-outer-spin-button { opacity: 0.4; }
        /* NumInput with a unit after the number: no spinner, so it can't cover the unit. */
        .sp-num-unit::-webkit-inner-spin-button, .sp-num-unit::-webkit-outer-spin-button { -webkit-appearance: none; margin: 0; }
        .sp-num-unit { -moz-appearance: textfield; }
        .sp-check-input:focus-visible + .sp-check-box { outline: 2px solid ${t.accent}; outline-offset: 2px; }
        @media (max-width: 720px) { .sp-calc .sp-grid { grid-template-columns: 1fr !important; } .sp-calc .sp-area-row { grid-template-columns: minmax(0,1fr) auto minmax(0,1fr) auto auto !important; } .sp-calc .sp-area-row .sp-area-label { grid-column: 1 / 4; grid-row: 1; } .sp-calc .sp-area-row .sp-area-add { grid-column: 4 / -1; grid-row: 1; } }
        @media (max-width: 480px) { .sp-calc .sp-qty-row { gap: 6px !important; } .sp-calc .sp-qty-step { width: 40px !important; height: 40px !important; } .sp-calc .sp-qty-input { font-size: 2.2rem !important; width: 4.6ch !important; } .sp-calc .sp-qty-unit { flex-direction: column; align-items: flex-start !important; gap: 3px !important; } .sp-calc .sp-qty-unit-text { font-size: 1.25rem !important; } }`}</style>
      <style>{`.sp-hint::placeholder, .dark-app input.sp-hint::placeholder { color: #a7b0bf !important; font-style: italic; opacity: 1 !important; } .dark-app input::placeholder { color: #3a4d62 !important; opacity: 1 !important; } .dark-app textarea::placeholder { color: #3a4d62 !important; opacity: 1 !important; }`}</style>
      {/* Mobile: the single-row layout (logo | tabs + account button) let the
          tab group's width push UserButton off the right edge, past the
          app's overflowX:hidden - clipped invisibly rather than wrapping.
          Stacks into two full-width rows instead, so the account button
          always has its own space next to the logo and never has to
          compete with the nav tabs for room.
          Theme follows the active tab: Log and Settings are the light-themed
          redesigned pages, so the header switches to match them there (light
          bg, dark navy logo) rather than sitting as a dark bar on top of a
          light page - Automotive/Industrial keep the original dark header
          since those calculators are still dark-themed.
          On a record page the header scrolls away: the record's own
          sticky bar (with ← Back to Quote Log) takes the top. */}
      <header style={{borderBottom:`1px solid ${isLightTab?"#E9EBF3":shellT.border}`,padding:isMobile?"14px 16px":"18px 40px",display:"flex",flexDirection:isMobile?"column":"row",alignItems:isMobile?"stretch":"center",justifyContent:"space-between",gap:isMobile?12:0,background:isLightTab?"#F4F6FC":shellT.navyDark,position:onRecordPage?"relative":"sticky",top:0,zIndex:100,boxSizing:"border-box"}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
          {/* The logo is the way home from any screen: the Quote Log. */}
          <a href="/" onClick={e=>{ e.preventDefault(); goHome(); }} aria-label="Sublime Precision Cleaning: Quote Log" title="Quote Log" style={{display:"inline-flex",lineHeight:0}}>
            <img src={isLightTab?"/logo-dark.png":"/logo.png"} alt="Sublime Precision Cleaning" style={{height:isMobile?32:42,width:"auto",objectFit:"contain"}}/>
          </a>
          {isMobile && <UserButton>{userButtonMenu}</UserButton>}
        </div>
        <div style={{display:"flex",alignItems:"center",justifyContent:isMobile?"center":"flex-end",gap:isMobile?10:16}}>
          {/* Quote Log is home; the calculators are reached from + New Quote
              (or Amend / Copy / Continue on the log), not as tabs. */}
          <div style={{display:"flex",gap:8,flex:isMobile?1:"none"}}>
            {activeTab!=="log"&&<button type="button" onClick={backToLog} style={{flex:isMobile?1:"none",minHeight:isMobile?44:38,padding:"0 18px",borderRadius:8,border:`1px solid ${isLightTab?"#E9EBF3":shellT.border}`,cursor:"pointer",fontSize:13,fontWeight:700,background:activeTab==="log"?shellT.brandBlue:(isLightTab?"#fff":"transparent"),color:activeTab==="log"?"#fff":(isLightTab?"#1a1a2e":shellT.text)}}>{isLightTab?"Quote Log":"← Quote Log"}</button>}
            {activeTab==="log"&&<button type="button" onClick={()=>setShowNewQuote(true)} style={{flex:isMobile?1:"none",minHeight:isMobile?44:38,padding:"0 18px",borderRadius:8,border:"none",cursor:"pointer",fontSize:13,fontWeight:700,background:isLightTab?"#1D3461":shellT.brandBlue,color:"#fff"}}>+ New Quote</button>}
          </div>
          {!isMobile && <UserButton>{userButtonMenu}</UserButton>}
        </div>
      </header>
      {activeTab==="log"&&<QuoteLog topContent={<DraftsStrip drafts={unsavedDrafts} onContinue={tab=>{ setReturnRow(null); setActiveTab(tab); }} onDiscard={discardDraft} isMobile={isMobile}/>} onCopyQuote={copyQuote} onOpenReadOnly={openReadOnly} focus={logFocus} onFocusShown={()=>setLogFocus(null)} onCreateAmendment={({ originalQuote, amendmentNumber, stateJson, basedOn, sourceQuote, startsAsDraft }) => { const tab = originalQuote["Category"]?.toLowerCase() === "automotive" ? "automotive" : "industrial"; if(!okToReplace(tab)) return; setViewer(null); setReturnRow((sourceQuote||originalQuote)["Quote Number"]); const state = stateJson ? {...stateJson, job: {...(stateJson.job||{}), quoteRef: amendmentNumber}, _savedTotalExVat: parseFloat(String((sourceQuote||originalQuote)["Total (ex VAT)"]||"").replace(/[^0-9.]/g,""))||null, _testQuote: originalQuote["Test Quote"]==="Yes"} : null; setAmendments(a=>({...a,[tab]:{state,meta:{originalNumber: originalQuote["Quote Number"], ...(basedOn?{basedOn}:{}), ...(startsAsDraft?{startsAsDraft:true}:{})}}})); remount(tab); setActiveTab(tab); }}/>}
      {activeTab==="settings"&&<CompanySettingsPage/>}
      {showNewQuote&&<NewQuoteDialog onPick={pickNewQuote} onClose={()=>setShowNewQuote(false)} isMobile={isMobile}/>}
      <div style={{display:activeTab==="automotive"?"block":"none"}}><AutomotiveTab onNew={()=>newQuoteIn("automotive")} onDraftChange={draftChanged} key={`auto-${calcKeys.automotive}`} onQuoteSaved={quoteSaved} t={t} initialState={amendments.automotive?.state} amendmentMeta={amendments.automotive?.meta} onAmendmentConsumed={()=>clearAmendmentMeta("automotive")}/></div>
      <div style={{display:activeTab==="industrial"?"block":"none"}}><IndustrialCalculator onNew={()=>newQuoteIn("industrial")} onDraftChange={draftChanged} key={`ind-${calcKeys.industrial}`} onQuoteSaved={quoteSaved} initialState={amendments.industrial?.state} amendmentMeta={amendments.industrial?.meta} onAmendmentConsumed={()=>clearAmendmentMeta("industrial")}/></div>
      {activeTab==="view"&&viewer&&(viewer.tab==="automotive"
        ? <AutomotiveTab key={viewer.key} t={t} initialState={viewer.state} readOnly={viewer.ro}/>
        : <IndustrialCalculator key={viewer.key} initialState={viewer.state} readOnly={viewer.ro}/>)}
      {activeTab==="view"&&!viewer&&<div style={{padding:40,color:t.textMuted}}>Nothing open. Use ← Quote Log.</div>}    </div>
  );
}
